import React, { useState, useEffect } from 'react';
import { Box, Typography, Avatar, Divider, TextField, Modal, Button, Slider, FormControl, InputLabel, Select, MenuItem, Dialog, DialogTitle, DialogContent, DialogActions, Checkbox, IconButton } from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import EmailIcon from '@mui/icons-material/Email';
import PhoneIcon from '@mui/icons-material/Phone';
import LocationOnIcon from '@mui/icons-material/LocationOn';
import KeyboardArrowUpIcon from '@mui/icons-material/KeyboardArrowUp';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { styled } from '@mui/material/styles';

// Funktion zur Berechnung der optimalen Schriftgröße für den Namen mit Zeilenumbrüchen
const calculateOptimalFontSizeWithLineBreaks = (text: string, maxWidth: number, baseFontSize: number = 18): { fontSize: number, formattedText: string } => {
  if (!text) return { fontSize: baseFontSize, formattedText: text };
  
  const avgCharWidth = 0.42; // Weniger restriktiv: erlaubt mehr Zeichen pro Zeile
  // Verwende nur den längsten Teil des Namens für die Größenberechnung (z. B. Vor- oder Nachname)
  const parts = text.split(/\s+/).filter(Boolean);
  const sizingText = parts.reduce((longest, part) => part.length > longest.length ? part : longest, '');
  const sizingLength = sizingText.length || text.length;
  
  // Prüfe zuerst, ob der Text mit voller Größe passt
  const maxCharsPerLineFullSize = Math.floor(maxWidth / (avgCharWidth * baseFontSize));
  
  // Wenn der Text mit voller Größe in eine Zeile passt, verwende volle Größe
  if (sizingLength <= maxCharsPerLineFullSize) {
    return { fontSize: baseFontSize, formattedText: text };
  }
  
  // Text ist zu lang - verwende proportionale Skalierung
  // Verwende maxWidth als Referenz für proportionale Skalierung (viel einfacher und korrekter)
  const proportionalSize = Math.floor(baseFontSize * maxWidth / 280);
  const clampedSize = Math.max(16, Math.min(48, proportionalSize));
  const maxCharsPerLine = Math.floor(maxWidth / (avgCharWidth * clampedSize)) + 3; // +3 Puffer für realistischere Breite
  
  // Wenn der Text mit proportionaler Größe in eine Zeile passt
  if (sizingLength <= maxCharsPerLine) {
    return { fontSize: clampedSize, formattedText: text };
  }
  // Zeilenumbrüche anwenden, aber Schriftgröße nicht weiter reduzieren
  const wordsForWrap = text.split(' ');
  const wrappedLines: string[] = [];
  let current = '';
  for (const word of wordsForWrap) {
    if (word.length > maxCharsPerLine) {
      if (current) {
        wrappedLines.push(current.trim());
        current = '';
      }
      let remaining = word;
      while (remaining.length > maxCharsPerLine) {
        wrappedLines.push(remaining.substring(0, maxCharsPerLine));
        remaining = remaining.substring(maxCharsPerLine);
      }
      if (remaining) {
        current = remaining;
      }
    } else {
      const test = current ? `${current} ${word}` : word;
      if (test.length <= maxCharsPerLine) {
        current = test;
      } else {
        if (current) wrappedLines.push(current.trim());
        current = word;
      }
    }
  }
  if (current) wrappedLines.push(current.trim());
  return { fontSize: clampedSize, formattedText: wrappedLines.join('\n') };
};

interface CvTemplateProps {
  cvData: any;
  profileImage?: string;
  templateStyle?: 'modern' | 'professional' | 'classic';
  showBorder?: boolean;
  showFrames?: boolean; // Neuer Prop für Rahmen-Anzeige
  leftColumnColor?: string; // Farbe für die linke Spalte
  nameTextColor?: string; // Farbe für den Namen
  leftColumnTextColor?: string; // Farbe für Text in der linken Spalte
  rightColumnTextColor?: string; // Farbe für Text in der rechten Spalte
  leftColumnTitleColor?: string; // Farbe für Titel in der linken Spalte
  rightColumnTitleColor?: string; // Farbe für Titel in der rechten Spalte
  'data-testid'?: string;
  onWorkExperienceChange?: (index: number, field: string, value: string) => void;
  onWorkExperienceReorder?: (originalIndex: number, direction: 'up' | 'down', isNonRelevant: boolean) => void;
  onContactChange?: (field: string, value: string) => void;
  onCoreCompetenciesTitleChange?: (title: string) => void;
  onCertificationsTitleChange?: (title: string) => void;
  onItSkillsTitleChange?: (title: string) => void;
  onIndustriesTitleChange?: (title: string) => void;
  onLanguagesTitleChange?: (title: string) => void;
  onHobbiesTitleChange?: (title: string) => void;
  onEducationTitleChange?: (title: string) => void;
  onWorkExperienceTitleChange?: (title: string) => void;
  onProfileTitleChange?: (title: string) => void;
  isEditMode?: boolean;
  targetLanguage?: string; // Zielsprache für Übersetzungen
  translatedYears?: string; // Übersetzte "Jahre"-Bezeichnung
  // Sichtbarkeits-Props für CV-Bereiche
  showCoreCompetenciesInCv?: boolean;
  showCertificationsInCv?: boolean;
  showItSkillsInCv?: boolean;
  showIndustriesInCv?: boolean;
  showLanguagesInCv?: boolean;
  showHobbiesInCv?: boolean;
  showEducationInCv?: boolean;
  showWorkExperienceInCv?: boolean;
  showAdditionalWorkExperienceInCv?: boolean;
  showProfileInCv?: boolean;
  showAdditionalInfoInCv?: boolean;
  showProfileImageInCv?: boolean;
  showContactInCv?: boolean;
  showCompanyNamesInCv?: boolean;
  showIndustryInCv?: boolean;
  showDateInCv?: boolean;
  showProjectSearchInCv?: boolean;
  showBulletPointsInCv?: boolean;
  profileImageBorderRadius?: number;
  profileImageSize?: string;
  profileImageWidth?: number;
  profileImagePosition?: { x: number; y: number };
  profileImageAlignment?: string;
  profileImageBorder?: boolean;
  profileImageBorderColor?: string;
  profileImageBorderThickness?: number;
  contactDisplayMode?: 'text' | 'icon';
  onContactDisplayModeChange?: (mode: 'text' | 'icon') => void; // Callback für Kontakt-Display-Mode-Änderung
  nameFontSize?: number; // Schriftgröße für den Namen (wird automatisch überschrieben)
  onNameFontSizeChange?: (fontSize: number) => void; // Callback für Schriftgrößenänderung
  topBoxColor?: string; // Farbe für die obere Box auf Seite 1
  topBoxImage?: string; // Bild/Graphik für die Header-Box (Base64 oder URL)
  topBoxHeightMode?: 'nameBottom' | 'profileMiddle';
  leftColumnImage?: string; // Bild/Graphik für die linke Spalte (Base64 oder URL)
  titleFont?: string; // Schriftart für Titel (Name, Sektions-Titel)
  bodyFont?: string;  // Schriftart für Fließtext (Kontakt, Details, etc.)
  onPageCountChange?: (count: number) => void;
}

// Wrapper component to filter out showFrames prop
const FrameBox = React.forwardRef<HTMLDivElement, any>((props, ref) => {
  // Entferne showFrames aus den Props, bevor sie an Box weitergegeben werden
  const { showFrames, ...restProps } = props;
  return <Box ref={ref} {...restProps} />;
});

// Hauptcontainer mit zweispaltigem Layout (nicht mehr verwendet)
const TemplateContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  width: '210mm',
  backgroundColor: '#ffffff',
  fontFamily: 'var(--cv-title-font, "Times New Roman", Times, serif)',
  fontSize: '11pt',
  lineHeight: 1.4,
  color: '#333',
  margin: 0,
  boxShadow: '0 0 10px rgba(0,0,0,0.1)',
  position: 'relative',
  '@media print': {
    backgroundColor: '#ffffff',
    boxShadow: 'none',
    '@page': {
      margin: '25.4mm 25.4mm 25.4mm 25.4mm', // Word-Standard Seitenränder (1 Zoll)
      size: 'A4'
    },
    '&': {
      margin: '0mm',
      padding: '0mm'
    }
  }
}));

// Word-Standard Seitenabstände (2.54cm = 25.4mm)
const WORD_MARGINS = {
  top: '25.4mm',      // 1 inch
  bottom: '25.4mm',   // 1 inch
  left: '25.4mm',     // 1 inch
  right: '25.4mm'     // 1 inch
};

// Einzelne A4-Seite mit Word-Standard Layout
const A4Page = styled(Box)(({ theme }) => ({
  width: '210mm',
  height: '297mm',
  backgroundColor: '#ffffff', // Weißer Hintergrund für die CV-Seite
  position: 'relative',
  overflow: 'hidden',
  pageBreakAfter: 'always',
  pageBreakInside: 'avoid',
  breakInside: 'avoid',
  display: 'flex',
  flexDirection: 'column',
  margin: '0', // Kein Abstand zwischen den Seiten
  boxShadow: '0 0 10px rgba(0,0,0,0.1)', // Schatten für Seitenränder
  '@media print': {
    backgroundColor: '#ffffff', // Weiß für Print
    margin: '0mm !important',
    boxShadow: 'none !important',
    '@page': {
      margin: '25.4mm 25.4mm 25.4mm 25.4mm', // Word-Standard Seitenränder (1 Zoll)
      size: 'A4'
    },
    '&': {
      margin: '0mm !important',
      padding: '0mm !important',
      pageBreakAfter: 'always',
      overflow: 'hidden'
    }
  }
}));

// Header für jede Seite
const PageHeader = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'showFrames' && prop !== 'textColor',
})<{ showFrames?: boolean; textColor?: string }>(({ theme, showFrames, textColor }) => ({
  height: '0mm', // Noch kleiner: von 7.5mm auf 3mm
  backgroundColor: '#ffffff',
  borderBottom: textColor ? `1px solid ${textColor}30` : '1px solid #e0e0e0', // 70% transparent (30% opacity)
  borderBottomColor: showFrames ? (textColor ? `${textColor}30` : '#e0e0e0') : 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  padding: '0 25.4mm',
  fontFamily: 'var(--cv-title-font, "Times New Roman", Times, serif)',
  fontSize: '10pt',
  color: '#666',
  '@media print': {
    borderBottom: '1px solid transparent'
  }
}));

// Footer für jede Seite
const PageFooter = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'showFrames',
})<{ showFrames?: boolean }>(({ theme, showFrames }) => ({
  height: '20mm', // Erhöht von 15mm auf 20mm
  backgroundColor: '#ffffff',
  borderTop: '1px solid #e0e0e0',
  borderTopColor: showFrames ? '#e0e0e0' : 'transparent',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  padding: '0 25.4mm',
  fontFamily: 'var(--cv-title-font, "Times New Roman", Times, serif)',
  fontSize: '10pt',
  color: '#666',
  '@media print': {
    borderTop: '1px solid transparent'
  }
}));

// Hauptinhalt-Bereich mit Word-Standard Abständen
const PageContent = styled(Box)(({ theme }) => ({
  flex: 1,
  display: 'flex',
  padding: `0 ${WORD_MARGINS.left} 0 ${WORD_MARGINS.left}`,
  minHeight: 'calc(297mm - 23mm)', // 297mm - Header (3mm) - Footer (20mm)
  position: 'relative',
  overflow: 'hidden',
  '@media print': {
    padding: `0 ${WORD_MARGINS.left} 0 ${WORD_MARGINS.left}`
  }
}));

// Wrapper für A4-Vielfache Höhe
const A4MultipleWrapper = styled(Box)<{ pages: number }>(({ theme, pages }) => ({
  width: '210mm', // Feste Breite für A4
  backgroundColor: 'transparent', // Transparenter Hintergrund
  margin: '0 auto', // Zentriert ohne Abstand
  padding: '0', // Kein Innenabstand
  minHeight: `calc(297mm * ${pages})`, // Höhe ohne Abstände
  display: 'flex',
  flexDirection: 'column',
  gap: '0px', // Kein Abstand zwischen den Seiten
  borderRadius: '0px', // Keine abgerundeten Ecken
  '@media print': {
    backgroundColor: 'transparent', // Transparent für Print
    margin: '0mm',
    padding: '0mm',
    gap: '0mm', // Kein Abstand im PDF
    borderRadius: '0mm',
    width: '210mm',
    height: `calc(297mm * ${pages})` // Höhe ohne Abstände im PDF
  }
}));

// Beige DIV auf der linken Seite (immer sichtbar, unabhängig von showFrames)
const GreenLeftBar = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'leftColumnColor',
})<{ leftColumnColor?: string }>(({ theme, leftColumnColor = '#f8f6f2' }) => ({
  position: 'absolute',
  left: '0',
  top: '0',
  width: '40%', // Feste Breite - wird nicht von showFrames beeinflusst
  height: '297mm', // Volle A4-Höhe
  backgroundColor: leftColumnColor, // Anpassbare Farbe für linke Spalte
  zIndex: 0, // Hintergrund-Ebene
  pointerEvents: 'none', // Verhindert Interaktionen
  '@media print': {
    backgroundColor: leftColumnColor, // Anpassbare Farbe für linke Spalte
    left: '25.4mm', // Anpassung an Word-Seitenrändern
    width: '40%', // Weitere 5% kleiner: von 45% auf 40%
    height: '297mm' // Volle A4-Höhe
  }
}));

// Vollbreite-Overlay nur auf Seite 1 (A4 Breite), ohne Layout zu beeinflussen
const TopFullWidthOverlay = styled(Box, {
  shouldForwardProp: (prop) =>
    prop !== 'topBoxColor' &&
    prop !== 'topBoxImage' &&
    prop !== 'topBoxHeightMode' &&
    prop !== 'measuredNameBottomPx' &&
    prop !== 'measuredProfileMiddlePx',
})<{ 
  topBoxColor?: string;
  topBoxImage?: string;
  topBoxHeightMode?: 'nameBottom' | 'profileMiddle';
  measuredNameBottomPx?: number | null;
  measuredProfileMiddlePx?: number | null;
}>(({ 
  topBoxColor = '#eeeeee', 
  topBoxImage,
  topBoxHeightMode = 'nameBottom',
  measuredNameBottomPx,
  measuredProfileMiddlePx
}) => {
  const overlayHeight = topBoxHeightMode === 'profileMiddle'
    ? (measuredProfileMiddlePx && measuredProfileMiddlePx > 0
      ? `${Math.ceil(measuredProfileMiddlePx)}px`
      : 'calc(5mm + 40mm + 30mm)')
    : (measuredNameBottomPx && measuredNameBottomPx > 0
      ? `calc(${Math.ceil(measuredNameBottomPx)}px + 3mm)`
      : 'calc(5mm + 40mm + 15mm)');
  return {
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    height: overlayHeight,
    backgroundColor: topBoxColor,
    backgroundImage: topBoxImage ? `url(${topBoxImage})` : 'none',
    backgroundSize: 'auto', // Originalgröße beibehalten
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'top left', // Immer oben links
    pointerEvents: 'none',
    zIndex: 0,
    '@media print': {
      top: 0,
      left: 0,
      right: 0,
      backgroundColor: topBoxColor,
      backgroundImage: topBoxImage ? `url(${topBoxImage})` : 'none',
      backgroundSize: 'auto', // Originalgröße beibehalten
      backgroundRepeat: 'no-repeat',
      backgroundPosition: 'top left'
    }
  };
});

// Linke Spalte Overlay für Hintergrundbild
const LeftColumnOverlay = styled(Box)(({ theme }) => ({
  position: 'absolute',
  top: 0,
  left: 0,
  width: '40%',
  height: '297mm',
  pointerEvents: 'none',
  zIndex: 1,
  '@media print': {
    left: '25.4mm',
    width: '40%',
    height: '297mm',
  }
}));

// Linke Spalte (schmal) - Kontakt, Profil, Sprachen, Hobbies
// Entfernt: LeftColumn styled component - wird durch Box ersetzt

// Rechte Spalte (breit) - Beruflicher Werdegang
const RightColumn = styled(Box)(({ theme }) => ({
  width: '60%',
  padding: '0px 0px 0px 20px', // Linker Abstand: top right bottom left
  display: 'flex',
  flexDirection: 'column',
  gap: '5px',
  flexGrow: 1,
  '@media print': {
    backgroundColor: 'transparent' // Keine Farbe im Print
  }
}));

// Modal für Namensgröße-Einstellungen
const NameSizeModal: React.FC<{
  open: boolean;
  onClose: () => void;
  currentFontSize: number;
  onFontSizeChange: (fontSize: number) => void;
  targetLanguage?: string;
  getTranslatedTitle: (title: string) => string;
}> = ({ open, onClose, currentFontSize, onFontSizeChange, targetLanguage, getTranslatedTitle }) => {
  const [fontSize, setFontSize] = useState(currentFontSize);

  // Aktualisiere den Slider-Wert wenn sich currentFontSize ändert
  useEffect(() => {
    setFontSize(currentFontSize);
  }, [currentFontSize]);

  const handleClose = () => {
    // Änderungen beim Schließen anwenden
    onFontSizeChange(fontSize);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleClose}
      aria-labelledby="name-size-modal"
      sx={{ zIndex: 1800 }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.25)'
        }
      }}
    >
      <Box
        sx={{
          position: 'absolute',
          top: '50%',
          left: '50%',
          transform: 'translate(-50%, -50%)',
          width: 400,
          bgcolor: 'background.paper',
          border: '2px solid #000',
          boxShadow: 24,
          p: 4,
          borderRadius: 2,
        }}
      >
        <Typography id="name-size-modal" variant="h6" component="h2" sx={{ mb: 2 }}>
          {getTranslatedTitle('Namensgröße anpassen')}
        </Typography>
        
        <Box sx={{ mb: 3 }}>
          <Typography gutterBottom>
            {getTranslatedTitle('Schriftgröße:')} {fontSize}pt
          </Typography>
          <Slider
            value={fontSize}
            onChange={(_, value) => setFontSize(value as number)}
            min={20}
            max={60}
            step={1}
            marks={[
              { value: 20, label: '20pt' },
              { value: 30, label: '30pt' },
              { value: 40, label: '40pt' },
              { value: 50, label: '50pt' },
              { value: 60, label: '60pt' },
            ]}
            valueLabelDisplay="auto"
          />
        </Box>

        <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
          <Button onClick={handleClose} variant="contained">
            {getTranslatedTitle('Schließen')}
          </Button>
        </Box>
      </Box>
    </Modal>
  );
};

// Modal für Kontakt-Einstellungen
const ContactModal: React.FC<{
  open: boolean;
  onClose: () => void;
  onExited?: () => void;
  currentDisplayMode: 'text' | 'icon';
  onDisplayModeChange: (mode: 'text' | 'icon') => void;
  targetLanguage?: string;
  getTranslatedTitle: (title: string) => string;
}> = ({ open, onClose, onExited, currentDisplayMode, onDisplayModeChange, targetLanguage, getTranslatedTitle }) => {
  const [displayMode, setDisplayMode] = useState<'text' | 'icon'>(currentDisplayMode);

  const handleClose = () => {
    onDisplayModeChange(displayMode);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      TransitionProps={{
        onExited
      }}
      maxWidth="sm"
      sx={{ zIndex: 1800 }}
      BackdropProps={{
        sx: {
          backgroundColor: 'rgba(0, 0, 0, 0.25)'
        }
      }}
      PaperProps={{
        sx: {
          width: '100%',
          maxWidth: '500px',
          overflowX: 'hidden'
        }
      }}
    >
      <DialogTitle sx={{ 
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        borderBottom: 'none',
        width: '100%',
        maxWidth: '100%',
        p: 3
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', pr: 4 }}>
          <Typography variant="h5" sx={{ fontWeight: 700, color: 'white', textShadow: '0 1px 2px rgba(0,0,0,0.1)' }}>
            {getTranslatedTitle('Kontakt-Anzeige anpassen')}
          </Typography>
          <IconButton 
            onClick={handleClose}
            sx={{ 
              color: 'white',
              padding: '8px',
              '&:hover': {
                backgroundColor: 'rgba(255,255,255,0.1)'
              }
            }}
          >
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>
      
      <DialogContent sx={{ p: 4, backgroundColor: 'white', color: '#333333' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>
          <Typography variant="h6" sx={{ mb: 2, color: '#333333' }}>
            {getTranslatedTitle('Anzeigemodus wählen')}
          </Typography>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Checkbox
              checked={displayMode === 'text'}
              onChange={(e) => setDisplayMode(e.target.checked ? 'text' : 'icon')}
              sx={{
                color: '#667eea',
                '&.Mui-checked': {
                  color: '#667eea',
                },
              }}
            />
            <Typography sx={{ color: '#333333' }}>
              {getTranslatedTitle('Text anzeigen')}
            </Typography>
          </Box>
          
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
            <Checkbox
              checked={displayMode === 'icon'}
              onChange={(e) => setDisplayMode(e.target.checked ? 'icon' : 'text')}
              sx={{
                color: '#667eea',
                '&.Mui-checked': {
                  color: '#667eea',
                },
              }}
            />
            <Typography sx={{ color: '#333333' }}>
              {getTranslatedTitle('Icon anzeigen')}
            </Typography>
          </Box>
        </Box>
      </DialogContent>
      
      <DialogActions sx={{ p: 2, backgroundColor: '#f8fafc' }}>
        <Button 
          onClick={handleClose}
          variant="outlined"
          sx={{
            px: 3,
            py: 1.5,
            borderRadius: 3,
            borderColor: '#667eea',
            color: '#757575',
            fontWeight: 600,
            transition: 'all 0.2s ease',
            '&:hover': {
              borderColor: '#5a6fd8',
              backgroundColor: '#f0f2ff',
              color: '#5a6fd8',
              transform: 'translateY(-1px)',
              boxShadow: '0 2px 8px rgba(102, 126, 234, 0.2)'
            }
          }}
        >
          {getTranslatedTitle('Schließen')}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

// Name (groß und elegant, wie im Original)
const NameTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'nameTextColor' && prop !== 'nameFontSize',
})<{ nameTextColor?: string; nameFontSize?: number }>(({ theme, nameTextColor, nameFontSize }) => ({
  fontSize: nameFontSize ? `${nameFontSize}pt` : '36pt',
  fontWeight: 'normal',
  textAlign: 'left',
  marginBottom: '5px',
  color: nameTextColor || '#333333', // Dunkelgrau statt schwarz
  fontFamily: 'var(--cv-title-font, "Times New Roman", Times, serif)',
  textTransform: 'uppercase',
  letterSpacing: '1px',
  lineHeight: 1.1
}));

// Sektions-Titel
const SectionTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  fontSize: '14pt', // Größer: von 12pt auf 14pt
  fontWeight: 'normal', // Nicht mehr fett
  textTransform: 'uppercase',
  marginBottom: '3px',
  color: textColor || '#333333', // Dunkelgrau statt schwarz
  fontFamily: 'var(--cv-title-font, "Times New Roman", Times, serif)'
}));

// Kontakt-Item
const ContactItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  marginBottom: '2px',
  fontSize: '10pt',
  fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
  textAlign: 'left',
  display: 'flex',
  color: textColor || '#333333',
  alignItems: 'center',
  gap: '5px',
  justifyContent: 'flex-start' // Links ausrichten
}));

// Kontakt-Label
const ContactLabel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  fontSize: '10pt',
  fontWeight: 'bold',
  color: textColor || '#333333', // Dunkelgrau statt schwarz
  fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
  whiteSpace: 'nowrap',
  width: '60px', // Feste Breite für alle Labels
  flexShrink: 0 // Verhindert Schrumpfen
}));

// Kontakt-Wert
const ContactValue = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  fontSize: '10pt',
  color: textColor || '#333',
  fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
  display: 'inline'
}));

// Ausbildung-Item
const EducationItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  marginBottom: '5px',
  paddingBottom: '3px',
  borderBottom: textColor ? `1px solid ${textColor}30` : '1px solid #f0f0f0', // 70% transparent (30% opacity)
  color: textColor || '#333333',
  '&:last-child': {
    borderBottom: 'none'
  }
}));

// Ausbildung-Abschluss
const EducationDegree = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  fontSize: '11pt',
  fontWeight: 'bold',
  color: textColor || '#333333', // Dunkelgrau statt schwarz
  marginBottom: '2px',
  fontFamily: 'var(--cv-title-font, "Times New Roman", Times, serif)'
}));

// Ausbildung-Institution
const EducationInstitution = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  fontSize: '10pt',
  color: textColor || '#666',
  marginBottom: '1px',
  fontFamily: 'var(--cv-body-font, Arial, sans-serif)'
}));

// Ausbildung-Zeitraum
const EducationPeriod = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  fontSize: '9pt',
  color: textColor || '#888',
  fontStyle: 'italic',
  fontFamily: 'var(--cv-body-font, Arial, sans-serif)'
}));

// Zertifizierung-Item
const CertificationItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  marginBottom: '5px',
  paddingBottom: '3px',
  borderBottom: textColor ? `1px solid ${textColor}30` : '1px solid #f0f0f0', // 70% transparent (30% opacity)
  color: textColor || '#333333',
  '&:last-child': {
    borderBottom: 'none'
  }
}));

// Zertifizierung-Name
const CertificationName = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  fontSize: '11pt',
  fontWeight: 'bold',
  color: textColor || '#333333', // Dunkelgrau statt schwarz
  marginBottom: '2px',
  fontFamily: 'var(--cv-title-font, "Times New Roman", Times, serif)'
}));

// Zertifizierung-Aussteller
const CertificationIssuer = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  fontSize: '10pt',
  color: textColor || '#666',
  marginBottom: '1px',
  fontFamily: 'var(--cv-body-font, Arial, sans-serif)'
}));

// Zertifizierung-Datum
const CertificationDate = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  fontSize: '9pt',
  color: textColor || '#888',
  fontStyle: 'italic',
  fontFamily: 'var(--cv-body-font, Arial, sans-serif)'
}));

// Skill-Item
const SkillItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  marginBottom: '3px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: textColor || '#333333'
}));

// Skill-Name
const SkillName = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  fontSize: '10pt',
  color: textColor || '#333333', // Dunkelgrau statt schwarz
  fontFamily: 'var(--cv-body-font, Arial, sans-serif)'
}));

// Skill-Level
const SkillLevel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  fontSize: '9pt',
  color: textColor || '#666',
  fontFamily: 'var(--cv-body-font, Arial, sans-serif)'
}));

// Branchen-Item
const IndustryItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  marginBottom: '3px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: textColor || '#333333'
}));

// Branchen-Name
const IndustryName = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  fontSize: '10pt',
  color: textColor || '#333333', // Dunkelgrau statt schwarz
  fontFamily: 'var(--cv-body-font, Arial, sans-serif)'
}));

// Branchen-Jahre
const IndustryYears = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  fontSize: '9pt',
  color: textColor || '#666',
  fontFamily: 'var(--cv-body-font, Arial, sans-serif)'
}));

// Sprache-Item
const LanguageItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  marginBottom: '3px',
  display: 'flex',
  justifyContent: 'space-between',
  alignItems: 'center',
  color: textColor || '#333333'
}));

// Sprache-Name
const LanguageName = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  fontSize: '10pt',
  color: textColor || '#333333', // Dunkelgrau statt schwarz
  fontFamily: 'var(--cv-body-font, Arial, sans-serif)'
}));

// Sprache-Level
const LanguageLevel = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  fontSize: '9pt',
  color: textColor || '#666',
  fontFamily: 'var(--cv-body-font, Arial, sans-serif)'
}));

// Hobby-Item
const HobbyItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  marginBottom: '3px',
  color: textColor || '#333333'
}));

// Hobby-Name
const HobbyName = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  fontSize: '10pt',
  fontWeight: 'bold',
  color: textColor || '#333333', // Dunkelgrau statt schwarz
  marginBottom: '1px',
  fontFamily: 'var(--cv-title-font, "Times New Roman", Times, serif)'
}));

// Hobby-Beschreibung
const HobbyDescription = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  fontSize: '9pt',
  color: textColor || '#666',
  fontFamily: 'var(--cv-body-font, Arial, sans-serif)'
}));

// Zusätzliche Info-Item
const AdditionalInfoItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  marginBottom: '3px',
  color: textColor || '#333333'
}));

// Zusätzliche Info-Text
const AdditionalInfoText = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  fontSize: '10pt',
  color: textColor || '#333333', // Dunkelgrau statt schwarz
  fontFamily: 'var(--cv-body-font, Arial, sans-serif)'
}));

// Profilbild (rund, in der rechten Spalte)
const ProfileImage = styled(Avatar)(({ theme }) => ({
  width: '245px', // 30% kleiner: 350px * 0.7 = 245px
  height: '245px', // 30% kleiner: 350px * 0.7 = 245px
  margin: '0 auto 5px auto',
  objectFit: 'contain',
  display: 'block'
}));

// Listen-Item
const ListItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  marginBottom: '2px',
  fontSize: '10pt',
  fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
  paddingLeft: '15px',
  position: 'relative',
  color: textColor || '#333333',
  '&::before': {
    content: '"•"',
    position: 'absolute',
    left: '0px',
    color: textColor || '#666'
  }
}));

// Sektions-Container
const SectionContainer = styled(Box)(({ theme }) => ({
}));

// Berufserfahrung-Item
const WorkItem = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  marginBottom: '2px',
  paddingBottom: '1px',
  borderBottom: textColor ? `1px solid ${textColor}30` : '1px solid #f0f0f0',
  color: textColor || '#333333',
  '&:last-child': {
    borderBottom: 'none'
  }
}));

// Kritische Sektionen
const CriticalSection = styled(Box)(({ theme }) => ({
}));

// Job-Titel
const JobTitle = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  fontSize: '12pt',
  fontWeight: 'bold',
  color: textColor || '#333333', // Dunkelgrau statt schwarz
  marginBottom: '2px',
  fontFamily: 'var(--cv-title-font, "Times New Roman", Times, serif)'
}));

// Firma und Datum
const CompanyDate = styled(Typography, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  fontSize: '10pt',
  color: textColor || '#666',
  fontStyle: 'italic',
  marginBottom: '1px',
  fontFamily: 'var(--cv-body-font, Arial, sans-serif)'
}));

// Job-Beschreibung
const JobDescription = styled(Box, {
  shouldForwardProp: (prop) => prop !== 'textColor',
})<{ textColor?: string }>(({ theme, textColor }) => ({
  fontSize: '10pt',
  lineHeight: 1.5,
  fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
  color: textColor || '#333333'
}));

export const CvTemplate: React.FC<CvTemplateProps> = ({ 
  cvData, 
  profileImage, 
  templateStyle = 'modern',
  showBorder = false,
  showFrames = false, // Neuer Prop für Rahmen-Anzeige
  leftColumnColor = templateStyle === 'professional' ? '#2e2e2e' : '#f8f6f2', // Standard-Farbe für linke Spalte (hellbeige)
  topBoxColor = templateStyle === 'professional' ? '#2e2e2e' : '#f8f6f2', // Standard-Farbe für Header-Box (gleiche wie linke Spalte)
  topBoxImage, // Optional: Bild für Header-Box
  topBoxHeightMode = 'nameBottom',
  leftColumnImage, // Optional: Bild für linke Spalte
  nameTextColor = '#333333', // Standard-Farbe für den Namen
  leftColumnTextColor = templateStyle === 'professional' ? '#ffffff' : '#333333', // Standard-Farbe für Text in der linken Spalte
  rightColumnTextColor = '#333333', // Standard-Farbe für Text in der rechten Spalte
  leftColumnTitleColor = templateStyle === 'professional' ? '#ffffff' : '#333333', // Standard-Farbe für Titel in der linken Spalte
  rightColumnTitleColor = '#333333', // Standard-Farbe für Titel in der rechten Spalte
  onWorkExperienceChange,
  onWorkExperienceReorder,
  onContactChange,
  onCoreCompetenciesTitleChange,
  onCertificationsTitleChange,
  onItSkillsTitleChange,
  onIndustriesTitleChange,
  onLanguagesTitleChange,
  onHobbiesTitleChange,
  onEducationTitleChange,
  onWorkExperienceTitleChange,
  onProfileTitleChange,
  isEditMode = false,
  targetLanguage = 'deutsch',
  translatedYears = 'Jahre',
  // Sichtbarkeits-Props
  showCoreCompetenciesInCv = true,
  showCertificationsInCv = true,
  showItSkillsInCv = true,
  showIndustriesInCv = true,
  showLanguagesInCv = true,
  showHobbiesInCv = true,
  showEducationInCv = true,
  showWorkExperienceInCv = true,
  showAdditionalWorkExperienceInCv = true,
  showProfileInCv = true,
  showAdditionalInfoInCv = true,
  showProfileImageInCv = true,
  showContactInCv = true,
  showCompanyNamesInCv = true,
  showIndustryInCv = true,
  showDateInCv = true,
  showProjectSearchInCv = false,
  showBulletPointsInCv = false,
  profileImageBorderRadius = (templateStyle === 'professional' || templateStyle === 'classic') ? 0 : 50,
  profileImageSize = 'M',
  profileImageWidth = 100,
  profileImagePosition = { x: 50, y: 50 },
  profileImageAlignment = templateStyle === 'professional' ? 'left' : 'center',
  profileImageBorder = false,
  profileImageBorderColor = '#333333',
  profileImageBorderThickness = 2,
  contactDisplayMode = 'text',
  onContactDisplayModeChange,
  nameFontSize = 36,
  onNameFontSizeChange,
  titleFont = '"Times New Roman", Times, serif',
  bodyFont = 'Arial, sans-serif',
  onPageCountChange,
  ...props
}) => {
  // State für Namensgröße-Modal
  const [isNameSizeModalOpen, setIsNameSizeModalOpen] = useState(false);
  
  // Temporäre manuelle Schriftgröße (wird bei Neubrechnung überschrieben)
  const [tempManualFontSize, setTempManualFontSize] = useState<number | null>(null);
  const firstPageRef = React.useRef<HTMLDivElement | null>(null);
  const [measuredNameBottomPx, setMeasuredNameBottomPx] = useState<number | null>(null);
  const [measuredProfileMiddlePx, setMeasuredProfileMiddlePx] = useState<number | null>(null);
  
  // Liefert die aktuell gerenderte Schriftgröße für den Namen
  const getRenderedNameFontSize = (name: string): number => {
    // Verwende temporäre manuelle Größe wenn gesetzt, sonst automatische Berechnung
    if (tempManualFontSize !== null) {
      return tempManualFontSize;
    }
    
    // Automatische Berechnung
    // Professional: Name in rechter Spalte (mehr Platz) - 400px, max 40pt
    // Modern/Classic: Name in linker Spalte (weniger Platz) - 150px/200px
    const maxWidth = templateStyle === 'professional' ? 400 : (templateStyle === 'classic' ? 200 : 150);
    const baseForTemplate = templateStyle === 'professional' ? 40 : nameFontSize; // Professional Template: 40pt als Basis
    const { fontSize } = calculateOptimalFontSizeWithLineBreaks(name || '', maxWidth, baseForTemplate);
    const finalSize = templateStyle === 'professional' ? Math.min(fontSize, 40) : fontSize; // Professional Template: max 40pt
    return finalSize;
  };
  
  // State für Kontakt-Modal
  const [isContactModalOpen, setIsContactModalOpen] = useState(false);
  
  // State für Template-Refresh
  const [refreshKey, setRefreshKey] = useState(0);
  const reflowTimerRef = React.useRef<NodeJS.Timeout | null>(null);
  const lastModalCloseAtRef = React.useRef(0);
  const lastReflowSourceRef = React.useRef<'manual' | 'modal-close' | 'template-change' | 'legacy'>('legacy');
  const triggerTemplateRefresh = React.useCallback((source: 'manual' | 'modal-close' | 'template-change' | 'legacy' = 'legacy') => {
    lastReflowSourceRef.current = source;
    setRefreshKey(prev => prev + 1);
  }, []);

  // Zentraler Reflow-Trigger für alle externen Re-Render-Events (Icon, Checkboxen, Modal-Close)
  const requestTemplateReflow = React.useCallback((source: 'manual' | 'modal-close' | 'template-change' | 'legacy' = 'legacy') => {
    // Debounce verhindert doppelte Reflows bei dicht aufeinanderfolgenden Events.
    if (reflowTimerRef.current) {
      clearTimeout(reflowTimerRef.current);
    }
    const delay = source === 'modal-close' ? 120 : 0;
    reflowTimerRef.current = setTimeout(() => {
      triggerTemplateRefresh(source);
      reflowTimerRef.current = null;
    }, delay);
  }, [triggerTemplateRefresh]);
  
  // Gespeicherte Namensgröße aus Prop übernehmen
  // Auto-Berechnung entfällt - gespeicherter Wert wird immer direkt verwendet
  useEffect(() => {
    setTempManualFontSize(nameFontSize);
  }, [nameFontSize, templateStyle, cvData.personalInfo?.fullName]);

  // Handler für Namensgröße-Änderung
  const handleNameFontSizeChange = (newFontSize: number) => {
    setTempManualFontSize(newFontSize);
    // Trigger Neuberechnung durch refreshKey-Erhöhung für korrektes Re-Rendering
    setTimeout(() => {
      triggerTemplateRefresh('legacy');
    }, 100);
    // An Parent weiterleiten zum Speichern
    if (onNameFontSizeChange) {
      onNameFontSizeChange(newFontSize);
    }
  };

  // Handler für Klick auf Namen
  const handleNameClick = () => {
    if (isEditMode) {
      setIsNameSizeModalOpen(true);
    }
  };

  // Handler für Kontakt-Modal
  const handleContactClick = () => {
    if (isEditMode) {
      setIsContactModalOpen(true);
    }
  };

  const measureNameBottom = React.useCallback(() => {
    if (templateStyle === 'classic' || topBoxHeightMode !== 'nameBottom') {
      setMeasuredNameBottomPx(null);
      return;
    }

    const firstPageElement = firstPageRef.current;
    if (!firstPageElement) return;

    const nameElement = firstPageElement.querySelector('[data-cv-name-title="true"]') as HTMLElement | null;
    if (!nameElement) return;

    const pageRect = firstPageElement.getBoundingClientRect();
    const nameRect = nameElement.getBoundingClientRect();
    const bottom = Math.max(0, nameRect.bottom - pageRect.top);

    setMeasuredNameBottomPx((prev) => {
      if (prev !== null && Math.abs(prev - bottom) < 0.5) return prev;
      return bottom;
    });
  }, [templateStyle, topBoxHeightMode]);

  const measureProfileMiddle = React.useCallback(() => {
    if (templateStyle === 'classic' || topBoxHeightMode !== 'profileMiddle') {
      setMeasuredProfileMiddlePx(null);
      return;
    }

    const firstPageElement = firstPageRef.current;
    if (!firstPageElement) return;

    const profileImageElement = firstPageElement.querySelector('[data-cv-profile-image="true"]') as HTMLElement | null;
    if (!profileImageElement) return;

    const pageRect = firstPageElement.getBoundingClientRect();
    const imageRect = profileImageElement.getBoundingClientRect();
    const middle = Math.max(0, imageRect.top - pageRect.top + imageRect.height / 2);

    setMeasuredProfileMiddlePx((prev) => {
      if (prev !== null && Math.abs(prev - middle) < 0.5) return prev;
      return middle;
    });
  }, [templateStyle, topBoxHeightMode]);

  React.useEffect(() => {
    if (templateStyle === 'classic') {
      setMeasuredNameBottomPx(null);
      setMeasuredProfileMiddlePx(null);
      return;
    }

    const runMeasurement = () => {
      if (topBoxHeightMode === 'nameBottom') {
        measureNameBottom();
      } else if (topBoxHeightMode === 'profileMiddle') {
        measureProfileMiddle();
      }
    };

    const rafId = window.requestAnimationFrame(runMeasurement);
    const timeoutId = window.setTimeout(runMeasurement, 80);
    const handleResize = () => runMeasurement();
    window.addEventListener('resize', handleResize);

    let resizeObserver: ResizeObserver | null = null;
    if (typeof ResizeObserver !== 'undefined' && firstPageRef.current) {
      resizeObserver = new ResizeObserver(() => runMeasurement());
      resizeObserver.observe(firstPageRef.current);
      const nameElement = firstPageRef.current.querySelector('[data-cv-name-title="true"]') as HTMLElement | null;
      if (nameElement) {
        resizeObserver.observe(nameElement);
      }
      const profileImageElement = firstPageRef.current.querySelector('[data-cv-profile-image="true"]') as HTMLElement | null;
      if (profileImageElement) {
        resizeObserver.observe(profileImageElement);
      }
    }

    return () => {
      window.cancelAnimationFrame(rafId);
      window.clearTimeout(timeoutId);
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) resizeObserver.disconnect();
    };
  }, [
    measureNameBottom,
    measureProfileMiddle,
    refreshKey,
    templateStyle,
    topBoxHeightMode,
    cvData.personalInfo?.fullName,
    profileImage,
    showProfileImageInCv,
    profileImageSize,
    profileImageWidth,
    profileImageAlignment,
    profileImageBorder,
    profileImageBorderThickness,
    tempManualFontSize,
    nameFontSize,
    titleFont
  ]);

  const handleContactDisplayModeChange = (mode: 'text' | 'icon') => {
    if (onContactDisplayModeChange) {
      onContactDisplayModeChange(mode);
    }
  };

  // Hilfsfunktion für Kontakt-Labels/Icons
  const renderContactLabel = (type: 'email' | 'phone' | 'address') => {
    if (contactDisplayMode === 'icon') {
      const iconProps = {
        sx: { 
          fontSize: '16px', 
          color: leftColumnTextColor,
          marginRight: '8px'
        }
      };
      
      switch (type) {
        case 'email':
          return <EmailIcon {...iconProps} />;
        case 'phone':
          return <PhoneIcon {...iconProps} />;
        case 'address':
          return <LocationOnIcon {...iconProps} />;
        default:
          return null;
      }
    } else {
      const labelText = type === 'email' ? 'Email:' : 
                       type === 'phone' ? 'Mobile:' : 'Adresse:';
      return (
        <ContactLabel
          textColor={leftColumnTextColor}
          sx={{
            color: `${leftColumnTextColor} !important`,
            width: '60px'
          }}
        >
          {getTranslatedContactLabel(labelText)}
        </ContactLabel>
      );
    }
  };

  // Funktion zur Berechnung der Profilbild-Größe
  const getProfileImageSize = (size: string) => {
    switch (size) {
      case 'S': return '180px';
      case 'M': return '245px';
      case 'L': return '300px';
      default: return '245px';
    }
  };

  
  // State für gemessene Element-Höhen
  const [measuredHeights, setMeasuredHeights] = React.useState<{[key: string]: number}>({});
  // Paging nutzt Messwerte erst, wenn sie über mehrere Messzyklen stabil sind.
  const [isMeasurementStable, setIsMeasurementStable] = React.useState(false);
  // Tatsächlich verfügbare Content-Höhen der dynamischen Spaltencontainer (in px)
  const [columnContentHeightsPx, setColumnContentHeightsPx] = React.useState<{ left?: number; right?: number }>({});
  
  // State für den Kernkompetenzen-Titel
  const [coreCompetenciesTitle, setCoreCompetenciesTitle] = React.useState('Kernkompetenzen');
  
  // State für den Zertifizierungen-Titel
  const [certificationsTitle, setCertificationsTitle] = React.useState('Zertifizierungen');
  
  // State für den IT-Skills-Titel
  const [itSkillsTitle, setItSkillsTitle] = React.useState('Informatik');
  
  // State für den Branchen-Titel
  const [industriesTitle, setIndustriesTitle] = React.useState('Branchen');
  
  // State für den Sprachen-Titel
  const [languagesTitle, setLanguagesTitle] = React.useState('Sprachen');
  
  // State für den Hobbies-Titel
  const [hobbiesTitle, setHobbiesTitle] = React.useState('Hobbies');
  
  // State für den Ausbildung-Titel
  const [educationTitle, setEducationTitle] = React.useState('Ausbildung');
  
  // State für den Berufserfahrungen-Titel
  const [workExperienceTitle, setWorkExperienceTitle] = React.useState('Beruflicher Werdegang');
  
  // State für den Profil-Titel
  const [profileTitle, setProfileTitle] = React.useState('Profil');
  
  // State für den Weitere-Angaben-Titel
  const [additionalInfoTitle, setAdditionalInfoTitle] = React.useState('Weitere Angaben');
  
  // State für den Weitere-Berufserfahrungen-Titel
  const [additionalWorkExperienceTitle, setAdditionalWorkExperienceTitle] = React.useState('Weitere Berufserfahrungen');
  
  // Verwende den optimierten Profil-Titel aus cvData falls verfügbar
  const currentProfileTitle = cvData.profileTitle || profileTitle;

  const elementRefs = React.useRef<{[key: string]: HTMLDivElement | null}>({});
  const leftColumnFrameRef = React.useRef<HTMLDivElement | null>(null);
  const rightColumnFrameRef = React.useRef<HTMLDivElement | null>(null);
  
  // Event Listener für Titel-Änderungen vom Modal
  React.useEffect(() => {
    const handleTitleUpdate = (event: any) => {
      if (event.detail && event.detail.title) {
        setCoreCompetenciesTitle(event.detail.title);
      }
    };
    
    window.addEventListener('updateCoreCompetenciesTitle', handleTitleUpdate);
    
    return () => {
      window.removeEventListener('updateCoreCompetenciesTitle', handleTitleUpdate);
    };
  }, []);

  // Event Listener für Zertifizierungen-Titel-Änderungen vom Modal
  React.useEffect(() => {
    const handleTitleUpdate = (event: any) => {
      if (event.detail && event.detail.title) {
        setCertificationsTitle(event.detail.title);
      }
    };
    
    window.addEventListener('updateCertificationsTitle', handleTitleUpdate);
    
    return () => {
      window.removeEventListener('updateCertificationsTitle', handleTitleUpdate);
    };
  }, []);

  // Event Listener für IT-Skills-Titel-Änderungen vom Modal
  React.useEffect(() => {
    const handleTitleUpdate = (event: any) => {
      if (event.detail && event.detail.title) {
        setItSkillsTitle(event.detail.title);
      }
    };
    
    window.addEventListener('updateItSkillsTitle', handleTitleUpdate);
    
    return () => {
      window.removeEventListener('updateItSkillsTitle', handleTitleUpdate);
    };
  }, []);

  // Event Listener für Branchen-Titel-Änderungen vom Modal
  React.useEffect(() => {
    const handleTitleUpdate = (event: any) => {
      if (event.detail && event.detail.title) {
        setIndustriesTitle(event.detail.title);
      }
    };
    
    window.addEventListener('updateIndustriesTitle', handleTitleUpdate);
    
    return () => {
      window.removeEventListener('updateIndustriesTitle', handleTitleUpdate);
    };
  }, []);

  // Event Listener für Sprachen-Titel-Änderungen vom Modal
  React.useEffect(() => {
    const handleTitleUpdate = (event: any) => {
      if (event.detail && event.detail.title) {
        setLanguagesTitle(event.detail.title);
      }
    };
    
    window.addEventListener('updateLanguagesTitle', handleTitleUpdate);
    
    return () => {
      window.removeEventListener('updateLanguagesTitle', handleTitleUpdate);
    };
  }, []);

  // Event Listener für Hobbies-Titel-Änderungen vom Modal
  React.useEffect(() => {
    const handleTitleUpdate = (event: any) => {
      if (event.detail && event.detail.title) {
        setHobbiesTitle(event.detail.title);
      }
    };
    
    window.addEventListener('updateHobbiesTitle', handleTitleUpdate);
    
    return () => {
      window.removeEventListener('updateHobbiesTitle', handleTitleUpdate);
    };
  }, []);

  // Event Listener für Ausbildung-Titel-Änderungen vom Modal
  React.useEffect(() => {
    const handleTitleUpdate = (event: any) => {
      if (event.detail && event.detail.title) {
        setEducationTitle(event.detail.title);
      }
    };
    
    window.addEventListener('updateEducationTitle', handleTitleUpdate);
    
    return () => {
      window.removeEventListener('updateEducationTitle', handleTitleUpdate);
    };
  }, []);

  // Event Listener für Berufserfahrungen-Titel-Änderungen vom Modal
  React.useEffect(() => {
    const handleTitleUpdate = (event: any) => {
      if (event.detail && event.detail.title) {
        setWorkExperienceTitle(event.detail.title);
      }
    };
    
    window.addEventListener('updateWorkExperienceTitle', handleTitleUpdate);
    
    return () => {
      window.removeEventListener('updateWorkExperienceTitle', handleTitleUpdate);
    };
  }, []);

  // Event Listener für Profil-Titel-Änderungen vom Modal
  React.useEffect(() => {
    const handleTitleUpdate = (event: any) => {
      if (event.detail && event.detail.title) {
        setProfileTitle(event.detail.title);
      }
    };
    
    window.addEventListener('updateProfileTitle', handleTitleUpdate);
    
    return () => {
      window.removeEventListener('updateProfileTitle', handleTitleUpdate);
    };
  }, []);

  // Event Listener für Weitere-Angaben-Titel-Änderungen vom Modal
  React.useEffect(() => {
    const handleTitleUpdate = (event: any) => {
      if (event.detail && event.detail.title) {
        setAdditionalInfoTitle(event.detail.title);
      }
    };
    
    window.addEventListener('updateAdditionalInfoTitle', handleTitleUpdate);
    
    return () => {
      window.removeEventListener('updateAdditionalInfoTitle', handleTitleUpdate);
    };
  }, []);

  // Event Listener für Weitere-Berufserfahrungen-Titel-Änderungen vom Modal
  React.useEffect(() => {
    const handleTitleUpdate = (event: any) => {
      if (event.detail && event.detail.title) {
        setAdditionalWorkExperienceTitle(event.detail.title);
      }
    };
    
    window.addEventListener('updateAdditionalWorkExperienceTitle', handleTitleUpdate);
    
    return () => {
      window.removeEventListener('updateAdditionalWorkExperienceTitle', handleTitleUpdate);
    };
  }, []);

  // Einheitlicher Event-Listener für alle externen Reflow-Trigger.
  // Unterstützt den neuen gemeinsamen Prozess und bleibt zu Alt-Events kompatibel.
  React.useEffect(() => {
    const handleTemplateReflow = (event: Event) => {
      if (event.type === 'modalClosed') {
        lastModalCloseAtRef.current = Date.now();
        requestTemplateReflow('modal-close');
      } else if (event.type === 'refreshTemplate') {
        // Viele Modals senden kurz nach "modalClosed" noch ein "refreshTemplate".
        // Dies erzeugt einen zweiten, meist redundanten Reflow-Pass.
        if (Date.now() - lastModalCloseAtRef.current < 300) {
          return;
        }
        requestTemplateReflow('manual');
      } else {
        requestTemplateReflow('legacy');
      }
    };
    
    window.addEventListener('templateReflow', handleTemplateReflow);
    window.addEventListener('refreshTemplate', handleTemplateReflow);
    window.addEventListener('modalClosed', handleTemplateReflow);
    
    return () => {
      window.removeEventListener('templateReflow', handleTemplateReflow);
      window.removeEventListener('refreshTemplate', handleTemplateReflow);
      window.removeEventListener('modalClosed', handleTemplateReflow);
      if (reflowTimerRef.current) {
        clearTimeout(reflowTimerRef.current);
      }
    };
  }, [requestTemplateReflow]);

  // Zusätzlicher Stabilizer: wenn Webfonts final geladen sind, einmalig nachreflowen.
  // Das reduziert Überlagerungen an Seitenumbrüchen beim ersten Öffnen der Preview.
  React.useEffect(() => {
    let cancelled = false;
    const fontsApi = (document as any)?.fonts;
    if (!fontsApi || !fontsApi.ready || typeof fontsApi.ready.then !== 'function') {
      return;
    }

    Promise.resolve(fontsApi.ready)
      .then(() => {
        if (cancelled) return;
        requestTemplateReflow('legacy');
        window.setTimeout(() => {
          if (!cancelled) {
            requestTemplateReflow('legacy');
          }
        }, 120);
      })
      .catch(() => {
        // ignore: fonts API failures should not break template rendering
      });

    return () => {
      cancelled = true;
    };
  }, [requestTemplateReflow]);

  // Gemeinsamer useEffect für Refresh-Trigger, Template-Wechsel UND erstes Öffnen der Preview
  // Bei refreshKey > 0: Reset + Titel, danach Höhenmessung. Bei refreshKey === 0 (erstes Öffnen): nur Höhenmessung.
  // So ist die Platzierung (z. B. linke Spalte Professional) beim Öffnen und nach Refresh identisch.
  const visibilityHash = `${showCoreCompetenciesInCv}_${showCertificationsInCv}_${showItSkillsInCv}_${showIndustriesInCv}_${showLanguagesInCv}_${showHobbiesInCv}_${showEducationInCv}_${showWorkExperienceInCv}_${showAdditionalWorkExperienceInCv}_${showProfileInCv}_${showAdditionalInfoInCv}_${showProfileImageInCv}_${showContactInCv}_${showDateInCv}_${showCompanyNamesInCv}_${showIndustryInCv}_${showProjectSearchInCv}`;
  const serializeCollectionForLayout = React.useCallback((value: any): string => {
    if (!Array.isArray(value)) return '';
    return value.map((item: any) => {
      if (item === null || item === undefined) return '';
      if (typeof item === 'string') return item.trim();
      if (typeof item === 'number' || typeof item === 'boolean') return String(item);
      if (typeof item === 'object') {
        const keys = Object.keys(item).sort();
        return keys.map((key) => {
          const fieldValue = item[key];
          if (typeof fieldValue === 'string') return `${key}:${fieldValue.trim().length}`;
          if (typeof fieldValue === 'number' || typeof fieldValue === 'boolean') return `${key}:${String(fieldValue)}`;
          if (Array.isArray(fieldValue)) return `${key}[${fieldValue.length}]`;
          return `${key}:${fieldValue ? 1 : 0}`;
        }).join('|');
      }
      return '';
    }).join('||');
  }, []);
  const contentHash = React.useMemo(() => {
    const personalInfo = cvData?.personalInfo || {};
    const workCollection = cvData?.optimizedWorkExperiences || cvData?.workExperience || [];
    const educationCollection = cvData?.relevantEducations || cvData?.educations || cvData?.education || [];
    const itSkillsCollection = cvData?.relevantItSkills || cvData?.itSkills || [];
    const languagesCollection = cvData?.relevantLanguages || cvData?.languages || [];
    const competenciesCollection = cvData?.relevantCoreCompetencies || cvData?.coreCompetencies || [];
    const industriesCollection = cvData?.relevantIndustries || cvData?.industryExperiences || [];
    const hobbiesCollection = cvData?.relevantHobbies || cvData?.hobbies || [];
    const certsCollection = cvData?.relevantCertifications || cvData?.certifications || [];
    const additionalInfoCollection = cvData?.relevantAdditionalInfo || cvData?.additionalInfo || [];

    return JSON.stringify({
      fullNameLen: typeof personalInfo?.fullName === 'string' ? personalInfo.fullName.trim().length : 0,
      profileLen: typeof personalInfo?.shortProfile === 'string' ? personalInfo.shortProfile.trim().length : 0,
      locationLen: typeof personalInfo?.location === 'string' ? personalInfo.location.trim().length : 0,
      emailLen: typeof personalInfo?.email === 'string' ? personalInfo.email.trim().length : 0,
      phoneLen: typeof personalInfo?.phoneNumber === 'string' ? personalInfo.phoneNumber.trim().length : 0,
      profileImageLen: typeof profileImage === 'string' ? profileImage.length : 0,
      work: serializeCollectionForLayout(workCollection),
      education: serializeCollectionForLayout(educationCollection),
      itSkills: serializeCollectionForLayout(itSkillsCollection),
      languages: serializeCollectionForLayout(languagesCollection),
      competencies: serializeCollectionForLayout(competenciesCollection),
      industries: serializeCollectionForLayout(industriesCollection),
      hobbies: serializeCollectionForLayout(hobbiesCollection),
      certifications: serializeCollectionForLayout(certsCollection),
      additionalInfo: serializeCollectionForLayout(additionalInfoCollection)
    });
  }, [cvData, profileImage, serializeCollectionForLayout]);
  const prevVisibilityHashRef = React.useRef(visibilityHash);
  const prevContentHashRef = React.useRef(contentHash);
  React.useEffect(() => {
    setIsMeasurementStable(false);

    const visibilityChanged = prevVisibilityHashRef.current !== visibilityHash;
    prevVisibilityHashRef.current = visibilityHash;
    const contentChanged = prevContentHashRef.current !== contentHash;
    prevContentHashRef.current = contentHash;

    // Hard-Reset nur bei echten Layout-Ursachen:
    // - Sichtbarkeitsänderung (Checkboxen/Toggle)
    // - explizite Legacy-/Template-Change-Reflows
    // Modal-Close und manuelles "Template neu rendern" bleiben Soft-Reflows,
    // damit wiederholtes Öffnen/Schließen von Submodals keine Toggle-Sprünge erzeugt.
    const shouldHardReset =
      visibilityChanged ||
      contentChanged ||
      (refreshKey > 0 &&
        (lastReflowSourceRef.current === 'legacy' || lastReflowSourceRef.current === 'template-change'));
    if (shouldHardReset) {
      // 1. PageCount und gemessene Höhen zurücksetzen
      setPageCount(1);
      setMeasuredHeights({});
      setColumnContentHeightsPx({});
      
      if (refreshKey > 0 && lastReflowSourceRef.current !== 'manual') {
        // 2. Titel neu initialisieren
        setCoreCompetenciesTitle(getTranslatedTitle('KERNKOMPETENZEN'));
        setCertificationsTitle(getTranslatedTitle('ZERTIFIZIERUNG'));
        setItSkillsTitle(getTranslatedTitle('INFORMATIK'));
        setIndustriesTitle(getTranslatedTitle('BRANCHEN'));
        setLanguagesTitle(getTranslatedTitle('SPRACHEN'));
        setHobbiesTitle(getTranslatedTitle('HOBBYS'));
        setEducationTitle(getTranslatedTitle('AUSBILDUNG'));
        setWorkExperienceTitle(showProjectSearchInCv ? getTranslatedTitle('PROJEKTERFAHRUNG') : getTranslatedTitle('BERUFLICHER WERDEGANG'));
        setProfileTitle(getTranslatedTitle('PROFIL'));
        setAdditionalInfoTitle(getTranslatedTitle('WEITERE ANGABEN'));
        setAdditionalWorkExperienceTitle(getTranslatedTitle('WEITERE BERUFSERFAHRUNGEN'));
      }
    }

    // Immer: Höhen messen nach Verzögerung (beim ersten Öffnen der Preview UND bei Refresh)
    // Längere Verzögerung notwendig für Professional Template, wo Elemente umverteilt werden
    let attemptCount = 0;
    const maxAttempts = 10; // Maximal 10 Versuche (2 Sekunden)
    let stablePasses = 0;
    let lastHeightsSignature = '';
    let timeoutId: NodeJS.Timeout | null = null;
    let isCancelled = false;

    const measureHeights = () => {
      // Prüfe ob Component noch gemountet ist (templateRef existiert und ist im DOM)
      if (isCancelled || !templateRef.current || !templateRef.current.offsetParent) {
        return;
      }

      attemptCount++;
      const newHeights: {[key: string]: number} = {};
      const allRefKeys = Object.keys(elementRefs.current);

      allRefKeys.forEach(key => {
        const element = elementRefs.current[key];
        if (element) {
          // Prüfe ob Element sichtbar ist (im DOM und hat offsetParent)
          const measured = element.getBoundingClientRect().height;
          const isVisible = element.offsetParent !== null && measured > 0;
          if (isVisible) {
            // Enthält auch vertikale Margins, damit Seitenumbrüche
            // den tatsächlich gerenderten Platzbedarf berücksichtigen.
            const styles = window.getComputedStyle(element);
            const marginTop = parseFloat(styles.marginTop || '0') || 0;
            const marginBottom = parseFloat(styles.marginBottom || '0') || 0;
            const height = measured + marginTop + marginBottom;
            if (height > 0) {
              newHeights[key] = height;
            }
          }
        }
      });

      // Nur aktualisieren wenn wir tatsächlich Höhen gemessen haben
      if (Object.keys(newHeights).length > 0) {
        // Zusätzlich die realen Content-Höhen der Spaltencontainer erfassen,
        // damit die Seitenverteilung nicht auf statischen mm-Werten basiert.
        const getContentHeight = (el: HTMLDivElement | null): number | undefined => {
          if (!el || !el.offsetParent) return undefined;
          const rect = el.getBoundingClientRect();
          if (!rect || rect.height <= 0) return undefined;
          const styles = window.getComputedStyle(el);
          const paddingTop = parseFloat(styles.paddingTop || '0') || 0;
          const paddingBottom = parseFloat(styles.paddingBottom || '0') || 0;
          const borderTop = parseFloat(styles.borderTopWidth || '0') || 0;
          const borderBottom = parseFloat(styles.borderBottomWidth || '0') || 0;
          const contentHeight = rect.height - paddingTop - paddingBottom - borderTop - borderBottom;
          return contentHeight > 0 ? contentHeight : undefined;
        };
        const leftContentHeight = getContentHeight(leftColumnFrameRef.current);
        const rightContentHeight = getContentHeight(rightColumnFrameRef.current);
        if (leftContentHeight || rightContentHeight) {
          setColumnContentHeightsPx((prev) => ({
            left: leftContentHeight ?? prev.left,
            right: rightContentHeight ?? prev.right
          }));
        }

        const newSignature = JSON.stringify(newHeights);
        if (newSignature === lastHeightsSignature) {
          stablePasses += 1;
        } else {
          stablePasses = 0;
          lastHeightsSignature = newSignature;
        }

        setMeasuredHeights(newHeights);

        // Erst beenden, wenn mindestens zwei Messungen hintereinander stabil sind.
        // So vermeiden wir zu frühe Verteilung während Fonts/Transitionen noch nachziehen.
        if (stablePasses >= 1) {
          setIsMeasurementStable(true);
          return;
        }

        if (attemptCount < maxAttempts && !isCancelled) {
          if (templateRef.current && templateRef.current.offsetParent) {
            timeoutId = setTimeout(measureHeights, 180);
          }
        }
      } else if (attemptCount < maxAttempts && !isCancelled) {
        // Wenn keine Höhen gemessen wurden, versuche es erneut nach kurzer Verzögerung
        if (templateRef.current && templateRef.current.offsetParent) {
          timeoutId = setTimeout(measureHeights, 200);
        }
      } else {
        // Maximalversuche erreicht oder Component nicht mehr sichtbar:
        // Kein künstliches "stable", damit nicht mit ungültigen Messungen gerechnet wird.
        // Vorhandene Messwerte bleiben erhalten; falls keine Messung möglich ist,
        // arbeitet die Verteilung weiterhin mit Schätzwerten.
        setIsMeasurementStable(false);
      }
    };

    timeoutId = setTimeout(measureHeights, 500);

    return () => {
      isCancelled = true;
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [refreshKey, visibilityHash, contentHash]);

  // Initialisiere und aktualisiere den Titel basierend auf der Zielsprache
  React.useEffect(() => {
    setCoreCompetenciesTitle(getTranslatedTitle('KERNKOMPETENZEN'));
    setCertificationsTitle(getTranslatedTitle('ZERTIFIZIERUNG'));
    setItSkillsTitle(getTranslatedTitle('INFORMATIK'));
    setIndustriesTitle(getTranslatedTitle('BRANCHEN'));
    setLanguagesTitle(getTranslatedTitle('SPRACHEN'));
    setHobbiesTitle(getTranslatedTitle('HOBBYS'));
    setEducationTitle(getTranslatedTitle('AUSBILDUNG'));
    setWorkExperienceTitle(showProjectSearchInCv ? getTranslatedTitle('PROJEKTERFAHRUNG') : getTranslatedTitle('BERUFLICHER WERDEGANG'));
    setProfileTitle(getTranslatedTitle('PROFIL'));
    setAdditionalInfoTitle(getTranslatedTitle('WEITERE ANGABEN'));
    setAdditionalWorkExperienceTitle(getTranslatedTitle('WEITERE BERUFSERFAHRUNGEN'));
  }, [targetLanguage, showProjectSearchInCv]);
  
  // Globale Print-Styles für Header/Footer-Versteckung
  React.useEffect(() => {
    const style = document.createElement('style');
    style.textContent = `
      @media print {
        @page {
          margin: 25.4mm 25.4mm 25.4mm 25.4mm;
          size: A4;
        }
        body {
          margin: 0mm !important;
          padding: 0mm !important;
        }
        * {
          -webkit-print-color-adjust: exact !important;
          color-adjust: exact !important;
        }
        .cv-template * {
          margin: 0mm !important;
          padding: 0mm !important;
          box-shadow: none !important;
        }
        .cv-template {
          margin: 0mm !important;
          padding: 0mm !important;
          box-shadow: none !important;
        }
      }
    `;
    document.head.appendChild(style);
    return () => {
      if (document.head.contains(style)) {
        document.head.removeChild(style);
      }
    };
  }, []);

  const renderText = (value: string, variant: any = 'body1', sx: any = {}) => {
    if (!value || value.trim() === '') return null;
    return <Typography variant={variant} sx={sx}>{value}</Typography>;
  };

  // Funktion zum Übersetzen von Sektionstiteln basierend auf targetLanguage
  const getTranslatedTitle = (title: string) => {
    const translations: { [key: string]: { [key: string]: string } } = {
      'KONTAKT': {
        'deutsch': 'KONTAKT',
        'englisch': 'CONTACT',
        'französisch': 'CONTACT',
        'italienisch': 'CONTATTO'
      },
      'PROFIL': {
        'deutsch': 'PROFIL',
        'englisch': 'PROFILE',
        'französisch': 'PROFIL',
        'italienisch': 'PROFILO'
      },
      'AUSBILDUNG': {
        'deutsch': 'AUSBILDUNG',
        'englisch': 'EDUCATION',
        'französisch': 'FORMATION',
        'italienisch': 'ISTRUZIONE'
      },
      'ZERTIFIZIERUNG': {
        'deutsch': 'ZERTIFIZIERUNG',
        'englisch': 'CERTIFICATION',
        'französisch': 'CERTIFICATION',
        'italienisch': 'CERTIFICAZIONE'
      },
      'INFORMATIK': {
        'deutsch': 'INFORMATIK',
        'englisch': 'INFORMATION TECHNOLOGY',
        'französisch': 'INFORMATIQUE',
        'italienisch': 'INFORMATICA'
      },
      'BRANCHEN': {
        'deutsch': 'BRANCHEN',
        'englisch': 'INDUSTRIES',
        'französisch': 'SECTEURS',
        'italienisch': 'SETTORI'
      },
      'SPRACHEN': {
        'deutsch': 'SPRACHEN',
        'englisch': 'LANGUAGES',
        'französisch': 'LANGUES',
        'italienisch': 'LINGUE'
      },
      'HOBBYS': {
        'deutsch': 'HOBBYS',
        'englisch': 'HOBBIES',
        'französisch': 'LOISIRS',
        'italienisch': 'HOBBY'
      },
      'WEITERE ANGABEN': {
        'deutsch': 'WEITERE ANGABEN',
        'englisch': 'ADDITIONAL INFORMATION',
        'französisch': 'INFORMATIONS COMPLÉMENTAIRES',
        'italienisch': 'INFORMAZIONI AGGIUNTIVE'
      },
      'KERNKOMPETENZEN': {
        'deutsch': 'KERNKOMPETENZEN',
        'englisch': 'CORE COMPETENCIES',
        'französisch': 'COMPÉTENCES CLÉS',
        'italienisch': 'COMPETENZE CHIAVE'
      },
      'BERUFLICHER WERDEGANG': {
        'deutsch': 'BERUFLICHER WERDEGANG',
        'englisch': 'PROFESSIONAL EXPERIENCE',
        'französisch': 'PARCOURS PROFESSIONNEL',
        'italienisch': 'ESPERIENZA PROFESSIONALE'
      },
      'PROJEKTERFAHRUNG': {
        'deutsch': 'PROJEKTERFAHRUNG',
        'englisch': 'PROJECT EXPERIENCE',
        'französisch': 'EXPÉRIENCE DE PROJET',
        'italienisch': 'ESPERIENZA DI PROGETTO'
      },
      'WEITERE BERUFSERFAHRUNGEN': {
        'deutsch': 'WEITERE BERUFSERFAHRUNGEN',
        'englisch': 'ADDITIONAL WORK EXPERIENCE',
        'französisch': 'AUTRES EXPÉRIENCES PROFESSIONNELLES',
        'italienisch': 'ALTRE ESPERIENZE PROFESSIONALI'
      },
      'PERSÖNLICHE INFORMATIONEN': {
        'deutsch': 'PERSÖNLICHE INFORMATIONEN',
        'englisch': 'PERSONAL INFORMATION',
        'französisch': 'INFORMATIONS PERSONNELLES',
        'italienisch': 'INFORMAZIONI PERSONALI'
      },
      'FÄHIGKEITEN UND INTERESSEN': {
        'deutsch': 'FÄHIGKEITEN UND INTERESSEN',
        'englisch': 'SKILLS AND INTERESTS',
        'französisch': 'COMPÉTENCES ET INTÉRÊTS',
        'italienisch': 'COMPETENZE E INTERESSI'
      },
      'Namensgröße anpassen': {
        'deutsch': 'Namensgröße anpassen',
        'englisch': 'Adjust Name Size',
        'französisch': 'Ajuster la taille du nom',
        'italienisch': 'Regola dimensione nome'
      },
      'Schriftgröße:': {
        'deutsch': 'Schriftgröße:',
        'englisch': 'Font Size:',
        'französisch': 'Taille de police:',
        'italienisch': 'Dimensione carattere:'
      },
      'Schließen': {
        'deutsch': 'Schließen',
        'englisch': 'Close',
        'französisch': 'Fermer',
        'italienisch': 'Chiudi'
      },
      'Kontakt-Anzeige anpassen': {
        'deutsch': 'Kontakt-Anzeige anpassen',
        'englisch': 'Adjust Contact Display',
        'französisch': 'Ajuster l\'affichage des contacts',
        'italienisch': 'Regola visualizzazione contatti'
      },
      'Anzeigemodus wählen': {
        'deutsch': 'Anzeigemodus wählen',
        'englisch': 'Select Display Mode',
        'französisch': 'Sélectionner le mode d\'affichage',
        'italienisch': 'Seleziona modalità di visualizzazione'
      },
      'Text anzeigen': {
        'deutsch': 'Text anzeigen',
        'englisch': 'Show Text',
        'französisch': 'Afficher le texte',
        'italienisch': 'Mostra testo'
      },
      'Icon anzeigen': {
        'deutsch': 'Icon anzeigen',
        'englisch': 'Show Icon',
        'französisch': 'Afficher l\'icône',
        'italienisch': 'Mostra icona'
      }
    };

    return translations[title]?.[targetLanguage] || title;
  };

  // Funktion zum Übersetzen von Sprachlevel-Codes (CEFR) basierend auf targetLanguage
  const getTranslatedLanguageLevel = (level: string): string => {
    if (!level) return '';
    // Nur "native" wird übersetzt, CEFR-Kürzel (A1-C2) bleiben unverändert
    if (level === 'native') {
      const nativeTranslations: { [key: string]: string } = {
        'deutsch': 'Muttersprache',
        'englisch': 'Native speaker',
        'französisch': 'Langue maternelle',
        'italienisch': 'Madrelingua'
      };
      return nativeTranslations[targetLanguage] || 'Muttersprache';
    }
    return level;
  };

  // Funktion zum Übersetzen von IT-Skill-Level-Codes basierend auf targetLanguage
  const getTranslatedItSkillLevel = (level: string): string => {
    if (!level) return '';
    const levelTranslations: { [key: string]: { [key: string]: string } } = {
      'beginner': { 'deutsch': 'Anfänger', 'englisch': 'Beginner', 'französisch': 'Débutant', 'italienisch': 'Principiante' },
      'intermediate': { 'deutsch': 'Fortgeschritten', 'englisch': 'Intermediate', 'französisch': 'Avancé', 'italienisch': 'Avanzato' },
      'professional': { 'deutsch': 'Profi', 'englisch': 'Professional', 'französisch': 'Professionnel', 'italienisch': 'Professionista' },
      'expert': { 'deutsch': 'Experte', 'englisch': 'Expert', 'französisch': 'Expert', 'italienisch': 'Esperto' }
    };
    return levelTranslations[level]?.[targetLanguage] || level;
  };

  // Funktion zum Formatieren von Datum von yyyy-mm-dd zu dd.mm.yyyy
  const formatDate = (dateString: string | undefined) => {
    if (!dateString) return 'Sofort';
    
    // Prüfe ob es bereits im richtigen Format ist (enthält Punkte)
    if (dateString.includes('.')) return dateString;
    
    // Konvertiere von yyyy-mm-dd zu dd.mm.yyyy
    const parts = dateString.split('-');
    if (parts.length === 3) {
      return `${parts[2]}.${parts[1]}.${parts[0]}`;
    }
    
    return dateString;
  };

  // Funktion zum Übersetzen von Kontakt-Labels basierend auf targetLanguage
  const getTranslatedContactLabel = (label: string) => {
    const translations: { [key: string]: { [key: string]: string } } = {
      'Email:': {
        'deutsch': 'Email:',
        'englisch': 'Email:',
        'französisch': 'Email:',
        'italienisch': 'Email:'
      },
      'Mobile:': {
        'deutsch': 'Mobile:',
        'englisch': 'Phone:',
        'französisch': 'Téléphone:',
        'italienisch': 'Telefono:'
      },
      'Adresse:': {
        'deutsch': 'Adresse:',
        'englisch': 'Address:',
        'französisch': 'Adresse:',
        'italienisch': 'Indirizzo:'
      },
      'Name': {
        'deutsch': 'Name',
        'englisch': 'Name',
        'französisch': 'Nom',
        'italienisch': 'Nome'
      },
      'Jahrgang': {
        'deutsch': 'Jahrgang',
        'englisch': 'Year of Birth',
        'französisch': 'Année de naissance',
        'italienisch': 'Anno di nascita'
      },
      'Mobile': {
        'deutsch': 'Mobile',
        'englisch': 'Phone',
        'französisch': 'Téléphone',
        'italienisch': 'Telefono'
      },
      'Email': {
        'deutsch': 'Email',
        'englisch': 'Email',
        'französisch': 'Email',
        'italienisch': 'Email'
      },
      'Adresse': {
        'deutsch': 'Adresse',
        'englisch': 'Address',
        'französisch': 'Adresse',
        'italienisch': 'Indirizzo'
      },
      'Nationalität': {
        'deutsch': 'Nationalität',
        'englisch': 'Nationality',
        'französisch': 'Nationalité',
        'italienisch': 'Nazionalità'
      },
      'Zivilstand': {
        'deutsch': 'Zivilstand',
        'englisch': 'Marital Status',
        'französisch': 'État civil',
        'italienisch': 'Stato civile'
      },
      'Verfügbar ab': {
        'deutsch': 'Verfügbar ab',
        'englisch': 'Available from',
        'französisch': 'Disponible dès',
        'italienisch': 'Disponibile da'
      },
      'Aufenthaltsbewilligung': {
        'deutsch': 'Aufenthaltsbewilligung',
        'englisch': 'Residence Permit',
        'französisch': 'Permis de séjour',
        'italienisch': 'Permesso di soggiorno'
      }
    };

    return translations[label]?.[targetLanguage] || label;
  };

  const renderContactInfo = () => (
    <>
      {(() => {
        const hasPhone = !!cvData.personalInfo?.phoneNumber?.trim();
        const hasEmail = !!cvData.personalInfo?.email?.trim();
        const hasCompleteAddress =
          !!cvData.personalInfo?.address?.trim() &&
          !!cvData.personalInfo?.postalCode?.trim() &&
          !!cvData.personalInfo?.city?.trim();

        return (
          <>
          {/* Persönliche Informationen */}
          <SectionTitle textColor={rightColumnTitleColor}>
            {getTranslatedTitle('PERSÖNLICHE INFORMATIONEN')}
          </SectionTitle>
      
      {/* Abstand */}
      <Box sx={{ height: '15px' }} />
      
      {/* Name */}
      <Box sx={{ display: 'flex', gap: '6px', marginBottom: '0px' }}>
        <Box sx={{
          border: '1px solid transparent',
          borderRadius: '4px',
          padding: '3px',
          backgroundColor: 'transparent',
          width: '88px', // Sehr schmale Label-Spalte für maximale Textbreite rechts
          marginBottom: '0px',
          ...(showBorder && {
            border: '1px solid #e0e0e0',
            backgroundColor: '#f8f8f8'
          }),
          '&:hover': {
            backgroundColor: '#f0f0f0',
            border: '1px solid #d0d0d0'
          }
        }}>
              <ContactItem>
                <strong>{getTranslatedContactLabel('Name')}</strong>
              </ContactItem>
        </Box>
        <Box sx={{
          border: '1px solid transparent',
          borderRadius: '4px',
          padding: '3px',
          backgroundColor: 'transparent',
          flex: '1',
          marginBottom: '0px',
          ...(showBorder && {
            border: '1px solid #e0e0e0',
            backgroundColor: '#f8f8f8'
          }),
          '&:hover': {
            backgroundColor: '#f0f0f0',
            border: '1px solid #d0d0d0'
          }
        }}>
          <ContactItem>
            {cvData.personalInfo?.fullName || 'Max Mustermann'}
          </ContactItem>
        </Box>
      </Box>
      
      {/* Jahrgang - nur anzeigen wenn Daten vorhanden */}
      {cvData.personalInfo?.birthYear && (
        <Box sx={{ display: 'flex', gap: '6px', marginBottom: '0px' }}>
          <Box sx={{
            border: '1px solid transparent',
            borderRadius: '4px',
            padding: '3px',
            backgroundColor: 'transparent',
            width: '88px',
            marginBottom: '0px',
            ...(showBorder && {
              border: '1px solid #e0e0e0',
              backgroundColor: '#f8f8f8'
            }),
            '&:hover': {
              backgroundColor: '#f0f0f0',
              border: '1px solid #d0d0d0'
            }
          }}>
                <ContactItem>
                  <strong>{getTranslatedContactLabel('Jahrgang')}</strong>
                </ContactItem>
          </Box>
          <Box sx={{
            border: '1px solid transparent',
            borderRadius: '4px',
            padding: '3px',
            backgroundColor: 'transparent',
            flex: '1',
            marginBottom: '0px',
            ...(showBorder && {
              border: '1px solid #e0e0e0',
              backgroundColor: '#f8f8f8'
            }),
            '&:hover': {
              backgroundColor: '#f0f0f0',
              border: '1px solid #d0d0d0'
            }
          }}>
            <ContactItem>
              {cvData.personalInfo?.birthYear}
            </ContactItem>
          </Box>
        </Box>
      )}
      
      {/* Nationalität - nur anzeigen wenn Daten vorhanden */}
      {cvData.personalInfo?.nationality && (
        <Box sx={{ display: 'flex', gap: '6px', marginBottom: '0px' }}>
          <Box sx={{
            border: '1px solid transparent',
            borderRadius: '4px',
            padding: '3px',
            backgroundColor: 'transparent',
            width: '88px',
            marginBottom: '0px',
            ...(showBorder && {
              border: '1px solid #e0e0e0',
              backgroundColor: '#f8f8f8'
            }),
            '&:hover': {
              backgroundColor: '#f0f0f0',
              border: '1px solid #d0d0d0'
            }
          }}>
            <ContactItem>
              <strong>{getTranslatedContactLabel('Nationalität')}</strong>
            </ContactItem>
          </Box>
          <Box sx={{
            border: '1px solid transparent',
            borderRadius: '4px',
            padding: '3px',
            backgroundColor: 'transparent',
            flex: '1',
            marginBottom: '0px',
            ...(showBorder && {
              border: '1px solid #e0e0e0',
              backgroundColor: '#f8f8f8'
            }),
            '&:hover': {
              backgroundColor: '#f0f0f0',
              border: '1px solid #d0d0d0'
            }
          }}>
            <ContactItem>
              {cvData.personalInfo?.nationality}
            </ContactItem>
          </Box>
        </Box>
      )}
      
      {/* Aufenthaltsbewilligung - nur anzeigen wenn Daten vorhanden */}
      {cvData.personalInfo?.residencePermit && (
        <Box sx={{ display: 'flex', gap: '6px', marginBottom: '0px' }}>
          <Box sx={{
            border: '1px solid transparent',
            borderRadius: '4px',
            padding: '3px',
            backgroundColor: 'transparent',
            width: '88px',
            marginBottom: '0px',
            ...(showBorder && {
              border: '1px solid #e0e0e0',
              backgroundColor: '#f8f8f8'
            }),
            '&:hover': {
              backgroundColor: '#f0f0f0',
              border: '1px solid #d0d0d0'
            }
          }}>
            <ContactItem>
              <strong>{getTranslatedContactLabel('Aufenthaltsbewilligung')}</strong>
            </ContactItem>
          </Box>
          <Box sx={{
            border: '1px solid transparent',
            borderRadius: '4px',
            padding: '3px',
            backgroundColor: 'transparent',
            flex: '1',
            marginBottom: '0px',
            ...(showBorder && {
              border: '1px solid #e0e0e0',
              backgroundColor: '#f8f8f8'
            }),
            '&:hover': {
              backgroundColor: '#f0f0f0',
              border: '1px solid #d0d0d0'
            }
          }}>
            <ContactItem>
              {cvData.personalInfo?.residencePermit}
            </ContactItem>
          </Box>
        </Box>
      )}
      
      {/* Zivilstand - nur anzeigen wenn Daten vorhanden UND Kontakt sichtbar */}
      {showContactInCv && cvData.personalInfo?.maritalStatus && (
        <Box sx={{ display: 'flex', gap: '6px', marginBottom: '0px' }}>
          <Box sx={{
            border: '1px solid transparent',
            borderRadius: '4px',
            padding: '3px',
            backgroundColor: 'transparent',
            width: '88px',
            marginBottom: '0px',
            ...(showBorder && {
              border: '1px solid #e0e0e0',
              backgroundColor: '#f8f8f8'
            }),
            '&:hover': {
              backgroundColor: '#f0f0f0',
              border: '1px solid #d0d0d0'
            }
          }}>
            <ContactItem>
              <strong>{getTranslatedContactLabel('Zivilstand')}</strong>
            </ContactItem>
          </Box>
          <Box sx={{
            border: '1px solid transparent',
            borderRadius: '4px',
            padding: '3px',
            backgroundColor: 'transparent',
            flex: '1',
            marginBottom: '0px',
            ...(showBorder && {
              border: '1px solid #e0e0e0',
              backgroundColor: '#f8f8f8'
            }),
            '&:hover': {
              backgroundColor: '#f0f0f0',
              border: '1px solid #d0d0d0'
            }
          }}>
            <ContactItem>
              {cvData.personalInfo?.maritalStatus}
            </ContactItem>
          </Box>
        </Box>
      )}
      
      {/* Verfügbar ab - nur anzeigen wenn Daten vorhanden UND Kontakt sichtbar */}
      {showContactInCv && cvData.personalInfo?.availableFrom && (
        <Box sx={{ display: 'flex', gap: '6px', marginBottom: '0px' }}>
          <Box sx={{
            border: '1px solid transparent',
            borderRadius: '4px',
            padding: '3px',
            backgroundColor: 'transparent',
            width: '88px',
            marginBottom: '0px',
            ...(showBorder && {
              border: '1px solid #e0e0e0',
              backgroundColor: '#f8f8f8'
            }),
            '&:hover': {
              backgroundColor: '#f0f0f0',
              border: '1px solid #d0d0d0'
            }
          }}>
            <ContactItem>
              <strong>{getTranslatedContactLabel('Verfügbar ab')}</strong>
            </ContactItem>
          </Box>
          <Box sx={{
            border: '1px solid transparent',
            borderRadius: '4px',
            padding: '3px',
            backgroundColor: 'transparent',
            flex: '1',
            marginBottom: '0px',
            ...(showBorder && {
              border: '1px solid #e0e0e0',
              backgroundColor: '#f8f8f8'
            }),
            '&:hover': {
              backgroundColor: '#f0f0f0',
              border: '1px solid #d0d0d0'
            }
          }}>
            <ContactItem>
              {formatDate(cvData.personalInfo?.availableFrom)}
            </ContactItem>
          </Box>
        </Box>
      )}
      
      {/* Mobile - nur anzeigen wenn Kontakt sichtbar UND Nummer vorhanden */}
      {showContactInCv && hasPhone && (
        <Box sx={{ display: 'flex', gap: '6px', marginBottom: '0px' }}>
          <Box sx={{
            border: '1px solid transparent',
            borderRadius: '4px',
            padding: '3px',
            backgroundColor: 'transparent',
            width: '88px', // Sehr schmale Label-Spalte für maximale Textbreite rechts
            marginBottom: '0px',
            ...(showBorder && {
              border: '1px solid #e0e0e0',
              backgroundColor: '#f8f8f8'
            }),
            '&:hover': {
              backgroundColor: '#f0f0f0',
              border: '1px solid #d0d0d0'
            }
          }}>
                <ContactItem>
                  <strong>{getTranslatedContactLabel('Mobile')}</strong>
                </ContactItem>
          </Box>
          <Box sx={{
            border: '1px solid transparent',
            borderRadius: '4px',
            padding: '3px',
            backgroundColor: 'transparent',
            flex: '1',
            marginBottom: '0px',
            ...(showBorder && {
              border: '1px solid #e0e0e0',
              backgroundColor: '#f8f8f8'
            }),
            '&:hover': {
              backgroundColor: '#f0f0f0',
              border: '1px solid #d0d0d0'
            }
          }}>
            <ContactItem>
              {cvData.personalInfo?.phoneNumber || ''}
            </ContactItem>
          </Box>
        </Box>
      )}

      {/* Email - nur anzeigen wenn Kontakt sichtbar UND Email vorhanden */}
      {showContactInCv && hasEmail && (
        <Box sx={{ display: 'flex', gap: '6px', marginBottom: '0px' }}>
          <Box sx={{
            border: '1px solid transparent',
            borderRadius: '4px',
            padding: '3px',
            backgroundColor: 'transparent',
            width: '88px', // Sehr schmale Label-Spalte für maximale Textbreite rechts
            marginBottom: '0px',
            ...(showBorder && {
              border: '1px solid #e0e0e0',
              backgroundColor: '#f8f8f8'
            }),
            '&:hover': {
              backgroundColor: '#f0f0f0',
              border: '1px solid #d0d0d0'
            }
          }}>
                <ContactItem>
                  <strong>{getTranslatedContactLabel('Email')}</strong>
                </ContactItem>
          </Box>
          <Box sx={{
            border: '1px solid transparent',
            borderRadius: '4px',
            padding: '3px',
            backgroundColor: 'transparent',
            flex: '1',
            marginBottom: '0px',
            ...(showBorder && {
              border: '1px solid #e0e0e0',
              backgroundColor: '#f8f8f8'
            }),
            '&:hover': {
              backgroundColor: '#f0f0f0',
              border: '1px solid #d0d0d0'
            }
          }}>
            <ContactItem>
              {cvData.personalInfo?.email || ''}
            </ContactItem>
          </Box>
        </Box>
      )}

      {/* Adresse - nur anzeigen wenn Kontakt sichtbar UND vollständig vorhanden */}
      {showContactInCv && hasCompleteAddress && (
        <Box sx={{ 
          display: 'flex', 
          gap: '6px', 
          marginBottom: '0px',
        }}>
          <Box sx={{
            border: '1px solid transparent',
            borderRadius: '4px',
            padding: '3px',
            backgroundColor: 'transparent',
            width: '88px', // Sehr schmale Label-Spalte für maximale Textbreite rechts
            marginBottom: '0px',
            ...(showBorder && {
              border: '1px solid #e0e0e0',
              backgroundColor: '#f8f8f8'
            }),
            '&:hover': {
              backgroundColor: '#f0f0f0',
              border: '1px solid #d0d0d0'
            }
          }}>
            <ContactItem>
              <strong>{getTranslatedContactLabel('Adresse')}</strong>
            </ContactItem>
          </Box>
          <Box sx={{
            border: '1px solid transparent',
            borderRadius: '4px',
            padding: '3px',
            backgroundColor: 'transparent',
            flex: '1',
            marginBottom: '0px',
            ...(showBorder && {
              border: '1px solid #e0e0e0',
              backgroundColor: '#f8f8f8'
            }),
            '&:hover': {
              backgroundColor: '#f0f0f0',
              border: '1px solid #d0d0d0'
            }
          }}>
            <ContactItem>
              {cvData.personalInfo?.address || ''}
            </ContactItem>
            <ContactItem>
              {cvData.personalInfo?.postalCode || ''} {cvData.personalInfo?.city || ''}
            </ContactItem>
          </Box>
        </Box>
      )}
          </>
        );
      })()}
    </>
  );

  const renderProfile = () => (
    <Box
      onClick={(e) => {
        // Trigger the Profile modal with current title
        const event = new CustomEvent('openProfileModal', {
          detail: { title: profileTitle }
        });
        window.dispatchEvent(event);
      }}
      sx={{ cursor: 'pointer' }}
    >
      <SectionTitle 
      >
        {profileTitle}
      </SectionTitle>
      <Typography sx={{ fontSize: '10pt', lineHeight: 1.5, fontFamily: 'var(--cv-body-font, Arial, sans-serif)', paddingLeft: '15px', color: leftColumnTextColor, whiteSpace: 'pre-wrap' }}>
        {cvData.personalInfo?.shortProfile || 'Engagierte und erfahrene Fachfrau mit mehrjähriger Erfahrung in verschiedenen Bereichen.'}
      </Typography>
    </Box>
  );







  const renderEducation = () => {
    return (
      <CriticalSection>
        <SectionTitle textColor={leftColumnTitleColor}>BILDUNG</SectionTitle>
        {cvData.relevantEducations && cvData.relevantEducations.length > 0 ? (
          cvData.relevantEducations.map((edu: any, index: number) => (
            <WorkItem key={index} textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>
              <JobTitle textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>{edu.degree}</JobTitle>
              {edu.description && (
                <CompanyDate textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>{edu.description}</CompanyDate>
              )}
              <CompanyDate textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>{edu.institution}</CompanyDate>
              <CompanyDate textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>{edu.startDate} - {edu.endDate || 'Heute'}</CompanyDate>
            </WorkItem>
          ))
        ) : (
          <>
            <WorkItem textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>
              <JobTitle textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>Leadership</JobTitle>
              <CompanyDate textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>HSO Oerlikon</CompanyDate>
              <CompanyDate textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>2018-2019</CompanyDate>
            </WorkItem>
            <WorkItem textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>
              <JobTitle textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>Berufsbildner Kurs</JobTitle>
              <CompanyDate textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>Berufsbildner.ch</CompanyDate>
              <CompanyDate textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>2016</CompanyDate>
            </WorkItem>
            <WorkItem textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>
              <JobTitle textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>Fachfrau Betreuung Kind</JobTitle>
              <CompanyDate textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>Berufsfachschule Winterthur</CompanyDate>
              <CompanyDate textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>2013-2016</CompanyDate>
            </WorkItem>
          </>
        )}
      </CriticalSection>
    );
  };

  const renderCoreCompetencies = () => {
    return (
      <CriticalSection>
        <SectionTitle textColor={leftColumnTitleColor}>
          {getTranslatedTitle('KERNKOMPETENZEN')}
        </SectionTitle>
        {cvData.coreCompetencies && cvData.coreCompetencies.length > 0 ? (
          cvData.coreCompetencies.map((competency: any, index: number) => (
            <ListItem key={index} textColor={leftColumnTitleColor}>
              {competency.competency}
            </ListItem>
          ))
        ) : (
          <>
            <ListItem textColor={leftColumnTitleColor}>Teamarbeit</ListItem>
            <ListItem textColor={leftColumnTitleColor}>Kommunikation</ListItem>
            <ListItem textColor={leftColumnTitleColor}>Problemlösung</ListItem>
            <ListItem textColor={leftColumnTitleColor}>Flexibilität</ListItem>
          </>
        )}
      </CriticalSection>
    );
  };

  const renderCertifications = () => {
    return (
      <CriticalSection>
        <SectionTitle textColor={leftColumnTitleColor}>
          {getTranslatedTitle('ZERTIFIZIERUNG')}
        </SectionTitle>
        {cvData.certifications && cvData.certifications.length > 0 ? (
          cvData.certifications.map((cert: any, index: number) => (
            <ListItem key={index} textColor={leftColumnTitleColor}>
              {cert.name}
            </ListItem>
          ))
        ) : (
          <>
            <ListItem textColor={leftColumnTitleColor}>Berufsbildner Kurs</ListItem>
            <ListItem textColor={leftColumnTitleColor}>Leadership Zertifikat</ListItem>
          </>
        )}
      </CriticalSection>
    );
  };

  const renderSkills = () => {
    return (
      <Box sx={{
        pageBreakInside: 'avoid',
        breakInside: 'avoid'
      }}>
        <SectionTitle textColor={leftColumnTitleColor}>
          {getTranslatedTitle('INFORMATIK')}
        </SectionTitle>
        {cvData.relevantItSkills && cvData.relevantItSkills.length > 0 ? (
          cvData.relevantItSkills.map((skill: any, index: number) => (
            <ListItem key={index} textColor={leftColumnTitleColor}>
              {skill.skill}: {getTranslatedItSkillLevel(skill.level)}
            </ListItem>
          ))
        ) : (
          <>
            <ListItem textColor={leftColumnTitleColor}>MS PowerPoint: Gute Kenntnisse</ListItem>
            <ListItem textColor={leftColumnTitleColor}>MS Word: Sehr gute Kenntnisse</ListItem>
            <ListItem textColor={leftColumnTitleColor}>Outlook: Sehr gute Kenntnisse</ListItem>
            <ListItem textColor={leftColumnTitleColor}>Excel: Grundkenntnisse</ListItem>
          </>
        )}
      </Box>
    );
  };

  const renderIndustries = () => {
    return (
      <Box sx={{
        pageBreakInside: 'avoid',
        breakInside: 'avoid'
      }}>
        <SectionTitle textColor={leftColumnTitleColor}>
          {getTranslatedTitle('BRANCHEN')}
        </SectionTitle>
        {cvData.relevantIndustries && cvData.relevantIndustries.length > 0 ? (
          cvData.relevantIndustries.map((industry: any, index: number) => (
            <ListItem key={index} textColor={leftColumnTitleColor}>
              {industry.industry} ({industry.years})
            </ListItem>
          ))
        ) : (
          <>
            <ListItem textColor={leftColumnTitleColor}>Gesundheitswesen (5 Jahre)</ListItem>
            <ListItem textColor={leftColumnTitleColor}>Bildung (3 Jahre)</ListItem>
            <ListItem textColor={leftColumnTitleColor}>Soziale Dienste (2 Jahre)</ListItem>
            <ListItem textColor={leftColumnTitleColor}>Betreuung (4 Jahre)</ListItem>
          </>
        )}
      </Box>
    );
  };

  const renderLanguagesCv = () => {
    return (
      <Box sx={{
        pageBreakInside: 'avoid',
        breakInside: 'avoid'
      }}>
        <SectionTitle textColor={leftColumnTitleColor}>
          {getTranslatedTitle('SPRACHEN')}
        </SectionTitle>
        {cvData.relevantLanguages && cvData.relevantLanguages.length > 0 ? (
          cvData.relevantLanguages.map((language: any, index: number) => (
            <ListItem key={index} textColor={leftColumnTitleColor}>
              {language.language}: {getTranslatedLanguageLevel(language.level)}
            </ListItem>
          ))
        ) : (
          <>
            <ListItem textColor={leftColumnTitleColor}>Deutsch: Muttersprache</ListItem>
            <ListItem textColor={leftColumnTitleColor}>Englisch: Fließend</ListItem>
            <ListItem textColor={leftColumnTitleColor}>Französisch: Grundkenntnisse</ListItem>
          </>
        )}
      </Box>
    );
  };

  const renderHobbies = () => {
    return (
      <Box sx={{
        pageBreakInside: 'avoid',
        breakInside: 'avoid'
      }}>
        <SectionTitle textColor={leftColumnTitleColor}>
          {getTranslatedTitle('HOBBYS')}
        </SectionTitle>
        {cvData.relevantHobbies && cvData.relevantHobbies.length > 0 ? (
          cvData.relevantHobbies.map((hobby: any, index: number) => (
            <ListItem key={index} textColor={leftColumnTitleColor}>
              {hobby.hobby && hobby.description ? `${hobby.hobby}: ${hobby.description}` : hobby.hobby || hobby.description}
            </ListItem>
          ))
        ) : (
          <>
            <ListItem textColor={leftColumnTitleColor}>Fotografie</ListItem>
            <ListItem textColor={leftColumnTitleColor}>Reisen</ListItem>
            <ListItem textColor={leftColumnTitleColor}>Sport</ListItem>
            <ListItem textColor={leftColumnTitleColor}>Lesen</ListItem>
          </>
        )}
      </Box>
    );
  };

  const renderAdditionalInfo = () => {
    return (
      <Box sx={{
        pageBreakInside: 'avoid',
        breakInside: 'avoid'
      }}>
        <SectionTitle textColor={leftColumnTitleColor}>WEITERE INFORMATIONEN</SectionTitle>
        {cvData.relevantAdditionalInfo && cvData.relevantAdditionalInfo.length > 0 ? (
          cvData.relevantAdditionalInfo.map((info: any, index: number) => (
            <ListItem key={index} textColor={leftColumnTitleColor}>
              {info.info && info.details ? `${info.info}: ${info.details}` : info.description || info.info || info.details}
            </ListItem>
          ))
        ) : (
          <>
            <ListItem textColor={leftColumnTitleColor}>Führerschein Klasse B</ListItem>
            <ListItem textColor={leftColumnTitleColor}>Erste-Hilfe-Kurs</ListItem>
            <ListItem textColor={leftColumnTitleColor}>Flexible Arbeitszeiten</ListItem>
            <ListItem textColor={leftColumnTitleColor}>Teamorientiert</ListItem>
          </>
        )}
      </Box>
    );
  };

  // Berechne die Anzahl der benötigten Seiten
  const [pageCount, setPageCount] = React.useState(1);
  const templateRef = React.useRef<HTMLDivElement>(null);

  // Dynamische px/mm Umrechnung basierend auf der real gerenderten A4-Breite.
  // Verhindert, dass Seitenumbrüche an eine starre 96-DPI-Annahme gebunden sind.
  const getPixelsPerMillimeter = React.useCallback(() => {
    const fallback = 3.78;
    const root = templateRef.current?.closest('.cv-template') as HTMLElement | null;
    const widthPx = root?.getBoundingClientRect?.().width || 0;
    if (widthPx > 0) {
      return widthPx / 210; // A4-Breite in mm
    }
    return fallback;
  }, []);

  // Template-Wechsel: über refreshKey den bewährten Reset+Messung-Zyklus auslösen
  React.useEffect(() => {
    requestTemplateReflow('template-change');
  }, [templateStyle, requestTemplateReflow]);

  // Sammle alle Elemente der rechten Spalte
  const getRightColumnElements = () => {
    const elements = [];
    const estimateModernProfessionalWorkMetaHeight = (work: any) => {
      const hasCompanyLine = !!(showCompanyNamesInCv && work?.company);
      const hasIndustryLine = !!(showIndustryInCv && work?.industry);
      const hasDateLine = !!showDateInCv;
      // In diesem Layout steht links das Datum (optional) und rechts Position + optionale Meta-Zeilen.
      // Die Zeilenhöhe der flex-row richtet sich nach der höheren Spalte.
      const rightColumnLines = 1 + (hasCompanyLine ? 1 : 0) + (hasIndustryLine ? 1 : 0); // Position ist immer sichtbar
      const leftColumnLines = hasDateLine ? 1 : 0;
      const dominantLines = Math.max(rightColumnLines, leftColumnLines);
      const lineHeight = 20;
      const spacingAfterRows = hasIndustryLine ? 5 : hasCompanyLine ? 3 : 0;
      return (dominantLines * lineHeight) + spacingAfterRows;
    };
    
    // Name nur für Professional Template in der rechten Spalte
    if (templateStyle === 'professional' && cvData.personalInfo?.fullName) {
      elements.push({
        type: 'name',
        content: cvData.personalInfo.fullName,
        height: 40
      });
    }
    
    // Profilbild nur für Modern und Classic Templates (nicht für Professional)
    if (templateStyle !== 'professional' && profileImage && showProfileImageInCv) {
      elements.push({
        type: 'profileImage',
        content: profileImage,
        height: 60 // Reduziert von 80
      });
    } else if (templateStyle !== 'professional' && !profileImage && showProfileImageInCv) {
      // Platzhalter-DIV nur wenn Profilbild aktiviert ist aber kein Bild vorhanden
      elements.push({
        type: 'profileImagePlaceholder',
        content: null,
        height: 19 // Muss zu rendered 5mm (~19px) passen, sonst kippt Pagination
      });
    }
    
    // Profil für Professional Template in der rechten Spalte
    if (templateStyle === 'professional' && cvData.personalInfo?.shortProfile && showProfileInCv) {
      // Konservativere Höhenschätzung basierend auf Textlänge
      const profileText = cvData.personalInfo.shortProfile || '';
      const estimatedLines = Math.ceil(profileText.length / 80); // ~80 Zeichen pro Zeile
      const profileHeight = Math.max(60, 30 + (estimatedLines * 25)); // Mindestens 60px, sonst basierend auf Zeilen
      elements.push({
        type: 'profile',
        content: cvData.personalInfo.shortProfile,
        height: profileHeight
      });
    }
    
    // Wenn showProfileImageInCv false ist, wird gar nichts hinzugefügt
    

    
    // Kernkompetenzen entfernt - werden jetzt in der linken Spalte angezeigt
    
    // Relevante Arbeitserfahrungen (aus optimizedWorkExperiences mit relevant: true)
    const relevantWorkExperiences = cvData.optimizedWorkExperiences ? 
      cvData.optimizedWorkExperiences
        .map((work: any, idx: number) => ({ ...work, _originalIndex: idx }))
        .filter((work: any) => work.relevant === true || work.relevant === 'true') : 
      (cvData.workExperience || []);
    
    // Abstand oberhalb von BERUFLICHER WERDEGANG
    if (relevantWorkExperiences && relevantWorkExperiences.length > 0 && showWorkExperienceInCv) {
      elements.push({
        type: 'spacerBeforeWorkExperience',
        content: null,
        height: 2.5
      });
    }
    
    if (relevantWorkExperiences && relevantWorkExperiences.length > 0 && showWorkExperienceInCv) {
      relevantWorkExperiences.forEach((work: any, index: number) => {
        const baseHeight = Math.max(52, 24 + estimateModernProfessionalWorkMetaHeight(work) + 6);
        const descriptionHeight = work.description 
          ? (showBulletPointsInCv 
              ? work.description.split('\n').filter((line: string) => line.trim()).length * 22
              : Math.ceil(work.description.length / 90) * 18)
          : 0;
        const responsibilitiesHeight = work.responsibilities ? work.responsibilities.length * 16 : 0;
        const safetyMargin = showBulletPointsInCv ? 14 : 6;
        const workHeight = baseHeight + descriptionHeight + responsibilitiesHeight + safetyMargin;

        if (index === 0) {
          elements.push({
            type: 'workExperienceWithTitle',
            content: {
              title: workExperienceTitle,
              work: work
            },
            _sourceWork: work,
            _originalIndex: work._originalIndex,
            _workIndex: index,
            _totalCount: relevantWorkExperiences.length,
            _isNonRelevant: false,
            height: 28 + workHeight
          });
        } else {
          elements.push({
            type: 'workExperience',
            content: work,
            _sourceWork: work,
            _originalIndex: work._originalIndex,
            _workIndex: index,
            _totalCount: relevantWorkExperiences.length,
            _isNonRelevant: false,
            height: workHeight
          });
        }
      });
    }
    
    // Nicht-relevante Berufserfahrungen (aus optimizedWorkExperiences mit relevant: false)
    const nonRelevantWorkExperiences = cvData.optimizedWorkExperiences ? 
      cvData.optimizedWorkExperiences
        .map((work: any, idx: number) => ({ ...work, _originalIndex: idx }))
        .filter((work: any) => work.relevant === false || work.relevant === 'false') : [];
    
    if (nonRelevantWorkExperiences && nonRelevantWorkExperiences.length > 0 && showAdditionalWorkExperienceInCv) {
      // Abstand vor nicht-relevanten Berufserfahrungen
      elements.push({
        type: 'spacerBeforeAdditionalWorkExperience',
        content: null,
        height: 10 // 10mm Abstand
      });
      
      // Titel für nicht-relevante Berufserfahrungen
      elements.push({
        type: 'additionalWorkExperienceTitle',
        content: additionalWorkExperienceTitle,
        height: 20
      });
      
      // Nicht-relevante Berufserfahrungen
      nonRelevantWorkExperiences.forEach((work: any, index: number) => {
        // Konservativere Höhenschätzung inkl. möglicher Zeilenumbrüche
        // in Position/Firma (lange Strings).
        const positionLen = (work?.position || work?.jobTitle || '').toString().trim().length;
        const companyLen = (work?.company || '').toString().trim().length;
        const estimatedWrapLines = Math.max(
          1,
          Math.ceil(positionLen / 48) + Math.ceil(companyLen / 56) - 1
        );
        const dynamicWrapHeight = Math.max(0, estimatedWrapLines - 1) * 14;
        const baseHeight = Math.max(54, 24 + estimateModernProfessionalWorkMetaHeight(work) + dynamicWrapHeight);
        const safetyMargin = 16;
        const workHeight = baseHeight + safetyMargin;
        
        elements.push({
          type: 'additionalWorkExperience',
          content: work,
          _originalIndex: work._originalIndex,
          _workIndex: index,
          _totalCount: nonRelevantWorkExperiences.length,
          _isNonRelevant: true,
          height: workHeight
        });
      });
    }
    
    elements.forEach((el: any, idx: number) => { el._globalIndex = idx; });
    return elements;
  };

  // Sammle alle Elemente der linken Spalte
  const getLeftColumnElements = () => {
    const elements = [];
    
    // Name nur für Modern und Classic Templates (nicht für Professional)
    if (templateStyle !== 'professional' && cvData.personalInfo?.fullName) {
      elements.push({
        type: 'name',
        content: cvData.personalInfo.fullName,
        height: 30
      });
    }
    
    // Profilbild für Professional Template ganz oben in der linken Spalte
    if (templateStyle === 'professional' && profileImage && showProfileImageInCv) {
      elements.push({
        type: 'profileImage',
        content: profileImage,
        height: 60 // Gleiche Höhe wie Modern Template
      });
    } else if (templateStyle === 'professional' && !profileImage && showProfileImageInCv) {
      // Platzhalter-DIV nur wenn Profilbild aktiviert ist aber kein Bild vorhanden
      elements.push({
        type: 'profileImagePlaceholder',
        content: null,
        height: 19 // Muss zu rendered 5mm (~19px) passen, sonst kippt Pagination
      });
    }
    
    // Für Classic Template: Profilbild separat (nur wenn aktiviert und vorhanden)
    if (templateStyle === 'classic' && showProfileImageInCv && profileImage) {
      elements.push({
        type: 'profileImage',
        content: profileImage,
        height: 60 // Nur für Profilbild
      });
    }
    
    const hasPhone = !!cvData.personalInfo?.phoneNumber?.trim();
    const hasEmail = !!cvData.personalInfo?.email?.trim();
    const hasCompleteAddress =
      !!cvData.personalInfo?.address?.trim() &&
      !!cvData.personalInfo?.postalCode?.trim() &&
      !!cvData.personalInfo?.city?.trim();
    const hasMaritalStatus = !!cvData.personalInfo?.maritalStatus;
    const hasAvailableFrom = !!cvData.personalInfo?.availableFrom;
    const hasResidencePermitInContact = !!cvData.personalInfo?.residencePermit;

    // Muss die gleiche Sichtbarkeitslogik wie renderContactInfo abbilden,
    // damit linke Spalten-Paginierung und tatsächliches Rendering übereinstimmen.
    const hasVisibleContactSection = showContactInCv && (
      hasPhone ||
      hasEmail ||
      hasCompleteAddress ||
      hasMaritalStatus ||
      hasAvailableFrom ||
      hasResidencePermitInContact
    );

    // Kontaktdaten - für alle Templates (inkl. Classic)
    if (hasVisibleContactSection) {
      const optionalContactRows = [
        hasPhone,
        hasEmail,
        hasCompleteAddress,
        hasMaritalStatus,
        hasAvailableFrom,
        hasResidencePermitInContact
      ].filter(Boolean).length;
      // Grundgerüst in renderContactInfo: Titel + Spacer + Name + Geburtsjahr + Nationalität.
      const baseContactRows = 5;
      const professionalContactOffset = templateStyle === 'professional'
        ? 10 + (profileImageBorder ? profileImageBorderThickness : 0)
        : 0;
      const contactHeight = Math.max(
        170,
        24 + ((baseContactRows + optionalContactRows) * 26) + professionalContactOffset
      );
      elements.push({
        type: 'contact',
        content: {
          email: cvData.personalInfo?.email,
          phone: cvData.personalInfo?.phoneNumber,
          address: cvData.personalInfo?.location,
          residencePermit: cvData.personalInfo?.residencePermit
        },
        height: contactHeight
      });
    }
    
    // Abstand/Trenner oberhalb von Profil nur wenn Kontakt-Sektion sichtbar
    if (cvData.personalInfo?.shortProfile && hasVisibleContactSection) {
      elements.push({
        type: 'spacerBeforeProfile',
        content: null,
        height: 5 // 5mm Abstand
      });
    }

    // Zusätzliche persönliche Angaben als eigenes Element (nicht im Spacer rendern)
    const hasNationality = !!cvData.personalInfo?.nationality;
    const hasResidencePermit = !!cvData.personalInfo?.residencePermit;
    if (hasNationality || hasResidencePermit) {
      const lineCount = (hasNationality ? 1 : 0) + (hasResidencePermit ? 1 : 0);
      elements.push({
        type: 'profileMeta',
        content: {
          nationality: cvData.personalInfo?.nationality,
          residencePermit: cvData.personalInfo?.residencePermit
        },
        height: lineCount * 38
      });
    }
    
    // Profil nur für Modern und Minimal Templates (nicht für Professional)
    if (templateStyle !== 'professional' && cvData.personalInfo?.shortProfile && showProfileInCv) {
      // Konservativere Höhenschätzung basierend auf Textlänge
      const profileText = cvData.personalInfo.shortProfile || '';
      const estimatedLines = Math.ceil(profileText.length / 80); // ~80 Zeichen pro Zeile
      const profileHeight = Math.max(60, 30 + (estimatedLines * 25)); // Mindestens 60px, sonst basierend auf Zeilen
      elements.push({
        type: 'profile',
        content: cvData.personalInfo.shortProfile,
        height: profileHeight
      });
    }
    
    // Kernkompetenzen direkt unter dem Kurzprofil
    if (cvData.coreCompetencies && cvData.coreCompetencies.length > 0 && showCoreCompetenciesInCv) {
      elements.push({
        type: 'coreCompetencies',
        content: cvData.coreCompetencies,
        height: 50 + (cvData.coreCompetencies.length * 15) // Erhöht: Titel 50px (von 30), Einträge 15px (von 10)
      });
    }
    
    // Ausbildung
    if (cvData.relevantEducations && cvData.relevantEducations.length > 0 && showEducationInCv) {
      elements.push({
        type: 'education',
        content: cvData.relevantEducations,
        height: 50 + (cvData.relevantEducations.length * 50) // Erhöht: Titel 50px (von 30), Einträge 50px (von 40)
      });
    }
    
    // Zertifizierungen
    if (cvData.certifications && cvData.certifications.length > 0 && showCertificationsInCv) {
      elements.push({
        type: 'certifications',
        content: cvData.certifications,
        height: 50 + (cvData.certifications.length * 15) // Erhöht: Titel 50px (von 30), Einträge 15px (von 10)
      });
    }
    
    // IT-Skills
    if (cvData.relevantItSkills && cvData.relevantItSkills.length > 0 && showItSkillsInCv) {
      elements.push({
        type: 'itSkills',
        content: cvData.relevantItSkills,
        height: 50 + (cvData.relevantItSkills.length * 15) // Erhöht: Titel 50px (von 30), Einträge 15px (von 10)
      });
    }
    
    // Branchen
    if (cvData.relevantIndustries && cvData.relevantIndustries.length > 0 && showIndustriesInCv) {
      elements.push({
        type: 'industries',
        content: cvData.relevantIndustries,
        height: 50 + (cvData.relevantIndustries.length * 15) // Erhöht: Titel 50px (von 30), Einträge 15px (von 10)
      });
    }
    
    // Sprachen
    if (cvData.relevantLanguages && cvData.relevantLanguages.length > 0 && showLanguagesInCv) {
      elements.push({
        type: 'languages',
        content: cvData.relevantLanguages,
        height: 50 + (cvData.relevantLanguages.length * 15) // Erhöht: Titel 50px (von 30), Einträge 15px (von 10)
      });
    }
    
    // Hobbys
    if (cvData.relevantHobbies && cvData.relevantHobbies.length > 0 && showHobbiesInCv) {
      elements.push({
        type: 'hobbies',
        content: cvData.relevantHobbies,
        height: 50 + (cvData.relevantHobbies.length * 15) // Erhöht: Titel 50px (von 30), Einträge 15px (von 10)
      });
    }
    
    // Weitere Angaben (optimiert)
    if (cvData.relevantAdditionalInfo && cvData.relevantAdditionalInfo.length > 0 && showAdditionalInfoInCv) {
      elements.push({
        type: 'additionalInfoOptimized',
        content: cvData.relevantAdditionalInfo,
        height: 50 + (cvData.relevantAdditionalInfo.length * 15) // Erhöht: Titel 50px (von 30), Einträge 15px (von 10)
      });
    }
    
    // Weitere Informationen (nur wenn keine optimierten Weitere Angaben vorhanden sind)
    if (cvData.additionalInfo && cvData.additionalInfo.length > 0 && (!cvData.relevantAdditionalInfo || cvData.relevantAdditionalInfo.length === 0)) {
      elements.push({
        type: 'additionalInfoTitle',
        content: 'WEITERE INFORMATIONEN',
        height: 20
      });
      
      elements.push({
        type: 'additionalInfo',
        content: cvData.additionalInfo,
        height: 30 + (cvData.additionalInfo.length * 10)
      });
    }
    
    elements.forEach((el: any, idx: number) => { el._globalIndex = idx; });
    return elements;
  };

  // Verteile Elemente auf Seiten basierend auf verfügbarem Platz
  const distributeElementsToPages = (
    elements: any[],
    frameHeightMM: number = 262,
    prefix: string = '',
    isMinimal: boolean = false,
    measuredFrameHeightPx?: number
  ) => {
    // Korrekte Höhenberechnung mit einheitlichen Einheiten
    const mmToPx = getPixelsPerMillimeter();
    const frameHeightPX = measuredFrameHeightPx && measuredFrameHeightPx > 0
      ? measuredFrameHeightPx
      : frameHeightMM * mmToPx; // Fallback nur falls Containerhöhe noch nicht messbar
    
    // Spacing zwischen Elementen folgt den realen Container-Gaps.
    // Left-Column nutzt 6px, Right-Column 5px.
    const gapPerElement = isMinimal ? 5 : (prefix.includes('_left') ? 6 : 5);
    
    // Paging nur mit real gemessenen DOM-Höhen (ohne Schätz-Fallbacks).
    // Nicht am isMeasurementStable-Flag blockieren, da dieses in Randfällen
    // dauerhaft false bleiben kann und dann nur 1 Seite gerendert wird.
    const hasReliableMeasuredHeights = Object.keys(measuredHeights).length > 0;
    // Kein 1-Seiten-Fallback mehr bei fehlenden Messwerten:
    // sonst kann Professional dauerhaft auf Seite 1 "festhängen".
    // Stattdessen unten gemischt rechnen: gemessene Höhen, sonst Element-Höhenmodell.

    const allElementsMeasured = elements.every((element: any, index: number) => {
      const globalIdx = element._globalIndex !== undefined ? element._globalIndex : index;
      const elementKey = prefix ? `${prefix}_${element.type}_${globalIdx}` : `${element.type}_${globalIdx}`;
      const measured = measuredHeights[elementKey];
      return typeof measured === 'number' && measured > 0;
    });
    // Wichtig: Nicht auf 1-Seiten-Fallback blockieren, wenn einzelne Messwerte fehlen.
    // Sonst werden untere Elemente (z. B. "Weitere Berufserfahrungen") durch overflow:hidden abgeschnitten.
    // In diesem Fall mit gemischten Werten weiterrechnen: gemessen, sonst Element-Höhenmodell.
    
    let availableHeight: number;
    
    if (isMinimal) {
      // Für Classic Template: Weniger restriktive Berechnung
      // frameHeightMM = 246mm enthält bereits Word-Standard-Margins (297mm - 2x 25.4mm)!
      // Reduziere Sicherheitsmargen für Classic Template
      const safetyMargin = 4;
      const additionalMargin = 0;
      
      availableHeight = frameHeightPX - safetyMargin - additionalMargin;
    } else {
      // Für Modern/Professional: Neue korrekte Berechnung (keine doppelten Margins)
      // frameHeightMM = 246mm enthält bereits Word-Standard-Margins (297mm - 2x 25.4mm)!
      // WICHTIG: Wenn Höhen noch nicht gemessen wurden, verwende größeren Sicherheitsabstand
      const safetyMargin = 0;
      const additionalMargin = 0;
      
      availableHeight = frameHeightPX - safetyMargin - additionalMargin;
      // Beim Template-Wechsel auf Professional sind in den ersten Pässen oft noch
      // nicht alle aktuellen Elementhöhen gemessen. Ohne Reserve werden Work-Blöcke
      // dann zu optimistisch platziert und können über den Umbruch laufen.
      if (!allElementsMeasured && templateStyle === 'professional') {
        availableHeight -= 22;
      }
    }
    
    const pages: any[][] = [];
    let currentPage: any[] = [];
    let currentHeight = 0;
    let elementCount = 0;
    
    elements.forEach((element: any, index: number) => {
      // Vor stabiler Messung werden Schätzwerte verwendet.
      // Nach stabiler Messung werden primär echte DOM-Höhen verwendet.
      // _globalIndex ist stabil über Seitenumbrüche hinweg (im Gegensatz zum forEach-index nach Verteilung)
      const globalIdx = element._globalIndex !== undefined ? element._globalIndex : index;
      const elementKey = prefix ? `${prefix}_${element.type}_${globalIdx}` : `${element.type}_${globalIdx}`;
      const measuredElementHeight = measuredHeights[elementKey];
      const elementHeight = (typeof measuredElementHeight === 'number' && measuredElementHeight > 0)
        ? measuredElementHeight
        : (element.height || 0);
      if (elementHeight <= 0) {
        return;
      }
      
      // Berechne benötigte Höhe inklusive Gap.
      // Kleiner Sicherheits-Puffer für Work-Experience-Blöcke:
      // Diese enthalten verschachtelte Typografie/Container und können durch
      // Rundung/Border/Padding sonst 1-8px über den Umbruch laufen.
      const paginationSafetyBuffer =
        element.type === 'workExperience' ||
        element.type === 'workExperienceWithTitle' ||
        element.type === 'additionalWorkExperience'
          ? (allElementsMeasured ? 8 : 20)
          : 0;
      const isWorkExperienceBlock =
        element.type === 'workExperience' ||
        element.type === 'workExperienceWithTitle' ||
        element.type === 'additionalWorkExperience';

      // Kappe übergroße Einzel-Elemente auf die verfügbare Seitenhöhe, damit
      // sie die Seitenverteilung nicht sprengen und nicht über den Umbruch laufen.
      const effectiveElementHeight = Math.min(elementHeight, availableHeight);
      // Für Berufserfahrungen nie mit gekappter Höhe platzieren:
      // Wenn zu wenig Platz auf der aktuellen Seite ist, komplett auf nächste Seite verschieben.
      const placementHeight = isWorkExperienceBlock ? elementHeight : effectiveElementHeight;
      const requiredHeight = currentHeight + placementHeight + paginationSafetyBuffer + (elementCount > 0 ? gapPerElement : 0);

      // Spezielle Regel: Spacer vor "Weitere Berufserfahrungen" nicht am Ende einer Seite
      if (element.type === 'spacerBeforeAdditionalWorkExperience') {
        // Alle Element-Höhen werden hier in px geführt.
        // Daher keine zusätzliche mm->px Umrechnung für Titelhöhe.
        const minRequiredSpace = 20 + gapPerElement + 85;
        const remainingSpace = availableHeight - requiredHeight;
        
        if (remainingSpace < minRequiredSpace && currentPage.length > 0) {
          // Nicht genug Platz (< 3cm) - überspringe Spacer und starte neue Seite
          pages.push(currentPage);
          currentPage = [];
          currentHeight = 0;
          elementCount = 0;
        } else {
          // Genug Platz (>= 3cm) - füge Spacer hinzu
          currentPage.push(element);
          currentHeight = requiredHeight;
          elementCount++;
        }
      }
      // Spezielle Regel: "Weitere Berufserfahrungen" Titel nur platzieren wenn min. Platz für Titel + mindestens 1 Element darunter
      else if (element.type === 'additionalWorkExperienceTitle') {
        // Mindestplatz in px: Titel + Gap + mindestens ein Eintrag.
        const minRequiredSpace = 20 + gapPerElement + 85;
        const remainingSpace = availableHeight - requiredHeight;
        
        if (remainingSpace < minRequiredSpace && currentPage.length > 0) {
          // Nicht genug Platz - verschiebe Titel auf nächste Seite
          pages.push(currentPage);
          currentPage = [element];
          currentHeight = effectiveElementHeight;
          elementCount = 1;
        } else {
          // Genug Platz - platziere auf aktueller Seite
          currentPage.push(element);
          currentHeight = requiredHeight;
          elementCount++;
        }
      }
      // Spezielle Regel: "Weitere Berufserfahrungen" Elemente - prüfe ob nach Titel genug Platz ist
      else if (element.type === 'additionalWorkExperience') {
        // Prüfe ob das vorherige Element der Titel war
        const previousElement = currentPage.length > 0 ? currentPage[currentPage.length - 1] : null;
        const isAfterTitle = previousElement && previousElement.type === 'additionalWorkExperienceTitle';
        
        if (isAfterTitle) {
          // Wenn direkt nach Titel, prüfe ob genug Platz für dieses Element + Gap ist
          const requiredHeightAfterTitle = currentHeight + effectiveElementHeight + gapPerElement;
          if (requiredHeightAfterTitle > availableHeight) {
            // Nicht genug Platz nach Titel:
            // Titel + erstes "Weitere Berufserfahrungen"-Element zusammen auf neue Seite verschieben.
            const titleElement = currentPage.pop();
            const titleGlobalIdx = titleElement?._globalIndex !== undefined ? titleElement._globalIndex : 0;
            const titleKey = prefix ? `${prefix}_${titleElement?.type}_${titleGlobalIdx}` : `${titleElement?.type}_${titleGlobalIdx}`;
            const measuredTitleHeight = titleElement
              ? ((measuredHeights[titleKey] && measuredHeights[titleKey] > 0) ? measuredHeights[titleKey] : (titleElement.height || 0))
              : 0;
            const effectiveTitleHeight = Math.min(measuredTitleHeight, availableHeight);
            const combinedHeight = effectiveTitleHeight + gapPerElement + effectiveElementHeight;

            if (currentPage.length > 0) {
              pages.push(currentPage);
            }

            if (titleElement && combinedHeight <= availableHeight) {
              currentPage = [titleElement, element];
              currentHeight = combinedHeight;
              elementCount = 2;
            } else {
              currentPage = [element];
              currentHeight = effectiveElementHeight;
              elementCount = 1;
            }
          } else {
            // Genug Platz - füge hinzu
            currentPage.push(element);
            currentHeight = requiredHeight;
            elementCount++;
          }
        } else {
          // Normale Behandlung wenn nicht direkt nach Titel
          if (requiredHeight > availableHeight && currentPage.length > 0) {
            pages.push(currentPage);
            currentPage = [element];
            currentHeight = placementHeight;
            elementCount = 1;
          } else if (!isWorkExperienceBlock && effectiveElementHeight >= availableHeight) {
            if (currentPage.length > 0) {
              pages.push(currentPage);
            }
            currentPage = [element];
            currentHeight = effectiveElementHeight;
            elementCount = 1;
          } else {
            currentPage.push(element);
            currentHeight = requiredHeight;
            elementCount++;
          }
        }
      }
      // Spezielle Regel: "Fähigkeiten und Interessen" Titel nur platzieren wenn min. 6cm Platz darunter
      else if (element.type === 'skillsAndInterestsTitle' && element.content === 'Fähigkeiten und Interessen') {
        const minRequiredSpace = 40 * mmToPx;
        const remainingSpace = availableHeight - requiredHeight;
        
        if (remainingSpace < minRequiredSpace && currentPage.length > 0) {
          // Nicht genug Platz (< 6cm) - verschiebe Titel auf nächste Seite
          pages.push(currentPage);
          currentPage = [element];
          currentHeight = effectiveElementHeight;
          elementCount = 1;
        } else {
          // Genug Platz (>= 6cm) - platziere auf aktueller Seite
          currentPage.push(element);
          currentHeight = requiredHeight;
          elementCount++;
        }
      } else {
        // Normale Behandlung für alle anderen Elemente
        // WICHTIG: Wenn Element größer als verfügbarer Platz ist, muss es trotzdem auf eine Seite
        if (requiredHeight > availableHeight && currentPage.length > 0) {
          // Aktuelle Seite ist voll - starte neue Seite
          pages.push(currentPage);
          currentPage = [element];
          currentHeight = placementHeight;
          elementCount = 1;
        } else if (!isWorkExperienceBlock && effectiveElementHeight >= availableHeight) {
          // Element ist größer als eine Seite - platziere es trotzdem auf einer eigenen Seite
          // Dies verhindert dass Elemente außerhalb der Seiten platziert werden
          if (currentPage.length > 0) {
            pages.push(currentPage);
          }
          currentPage = [element];
          currentHeight = effectiveElementHeight;
          elementCount = 1;
        } else {
          // Element passt auf aktuelle Seite
          currentPage.push(element);
          currentHeight = requiredHeight;
          elementCount++;
        }
      }
    });
    
    // WICHTIG: Füge letzte Seite hinzu, auch wenn sie nicht voll ist
    if (currentPage.length > 0) {
      pages.push(currentPage);
    }
    
    // Sicherheitscheck: Stelle sicher dass mindestens eine Seite existiert
    if (pages.length === 0) {
      pages.push([]);
    }
    
    return pages;
  };

  // Verteile rechte Elemente auf Seiten
  const distributeRightElementsToPages = () => {
    const elements = getRightColumnElements();
    // Muss mit der real gerenderten rechten Frame-Höhe übereinstimmen,
    // sonst entstehen Verschiebungen am Seitenumbruch.
    return distributeElementsToPages(elements, 246, templateStyle, false, columnContentHeightsPx.right);
  };

  // Verteile linke Elemente auf Seiten
  const distributeLeftElementsToPages = () => {
    const elements = getLeftColumnElements();
    return distributeElementsToPages(elements, 246, `${templateStyle}_left`, false, columnContentHeightsPx.left);
  };

  // Spezielle Funktion für Minimal Template - sammelt alle Elemente in der richtigen Struktur
  const getMinimalElements = () => {
    const elements = [];
    const estimateClassicWorkMetaHeight = (work: any) => {
      const hasCompanyOrDateLine = !!((showCompanyNamesInCv && work?.company) || showDateInCv);
      const hasIndustryLine = !!(showIndustryInCv && work?.industry);
      // Classic rendert Position immer als erste Zeile, danach eine kombinierte Firma/Datum-Zeile
      // und optional eine Branchen-Zeile.
      const lineCount = 1 + (hasCompanyOrDateLine ? 1 : 0) + (hasIndustryLine ? 1 : 0);
      const lineHeight = 20;
      const spacingAfterRows = hasIndustryLine ? 4 : 2;
      return (lineCount * lineHeight) + spacingAfterRows;
    };
    
    // Name
    if (cvData.personalInfo?.fullName) {
      elements.push({
        type: 'name',
        content: cvData.personalInfo.fullName,
        height: 40 // Realistische Höhe für Name
      });
    }
    
    // Persönliche Informationen (Titel, Name, Jahrgang, Nationalität immer sichtbar)
    // Zusätzliche Kontaktdaten (Zivilstand, Verfügbar ab, Mobile, Email, Adresse) werden durch showContactInCv in renderContactInfo() gesteuert
    if (cvData.personalInfo) {
      // Höhe anpassen basierend auf showContactInCv
      const baseHeight = 80; // Für Titel, Name, Jahrgang, Nationalität
      const contactHeight = showContactInCv ? 100 : 0; // Zusätzliche Höhe für Kontaktfelder
      elements.push({
        type: 'contact',
        content: {
          email: cvData.personalInfo?.email,
          phone: cvData.personalInfo?.phoneNumber,
          address: cvData.personalInfo?.location,
          residencePermit: cvData.personalInfo?.residencePermit
        },
        height: baseHeight + contactHeight
      });
    }
    
    // Profilbild separat
    if (showProfileImageInCv && profileImage) {
      elements.push({
        type: 'profileImage',
        content: profileImage,
        height: 60 // Nur für Profilbild
      });
    }
    
    // Profil
    if (cvData.personalInfo?.shortProfile && showProfileInCv) {
      // Konservativere Höhenschätzung basierend auf Textlänge
      const profileText = cvData.personalInfo.shortProfile || '';
      const estimatedLines = Math.ceil(profileText.length / 80); // ~80 Zeichen pro Zeile
      const profileHeight = Math.max(80, 40 + (estimatedLines * 25)); // Mindestens 80px, sonst basierend auf Zeilen
      elements.push({
        type: 'profile',
        content: cvData.personalInfo.shortProfile,
        height: profileHeight
      });
    }
    
    // Berufserfahrung - als einzelne Elemente wie bei anderen Templates
    const relevantWorkExperiences = cvData.optimizedWorkExperiences ? 
      cvData.optimizedWorkExperiences
        .map((work: any, idx: number) => ({ ...work, _originalIndex: idx }))
        .filter((work: any) => work.relevant === true || work.relevant === 'true') : 
      (cvData.workExperience || []);
    
    if (relevantWorkExperiences && relevantWorkExperiences.length > 0 && showWorkExperienceInCv) {
      // Abstand oberhalb von BERUFLICHER WERDEGANG
      elements.push({
        type: 'spacerBeforeWorkExperience',
        content: null,
        height: 20 // Mehr Abstand zwischen Sektionen
      });
      
      // Titel für Berufserfahrung
      elements.push({
        type: 'workExperienceTitle',
        content: workExperienceTitle,
        height: 40 // Realistische Höhe für Titel
      });
      
      // Jede Berufserfahrung als einzelnes Element
      relevantWorkExperiences.forEach((work: any, index: number) => {
        // Konservativere Höhenberechnung basierend auf Inhalt
        const baseHeight = Math.max(100, 30 + estimateClassicWorkMetaHeight(work) + 18);
        const descriptionHeight = work.description 
          ? (showBulletPointsInCv 
              ? work.description.split('\n').filter((line: string) => line.trim()).length * 30
              : Math.ceil(work.description.length / 80) * 25)
          : 0;
        const responsibilitiesHeight = work.responsibilities && work.responsibilities.length > 0 
          ? work.responsibilities.length * 25 : 0;
        const safetyMargin = showBulletPointsInCv ? 30 : 20;
        const workHeight = baseHeight + descriptionHeight + responsibilitiesHeight + safetyMargin;
        
        elements.push({
          type: 'workExperience',
          content: work,
          _originalIndex: work._originalIndex,
          _workIndex: index,
          _totalCount: relevantWorkExperiences.length,
          _isNonRelevant: false,
          height: workHeight
        });
      });
    }
    
    // Bildung
    if (cvData.relevantEducations && cvData.relevantEducations.length > 0 && showEducationInCv) {
      elements.push({
        type: 'education',
        content: cvData.relevantEducations,
        height: 60 + (cvData.relevantEducations.length * 60) // Erhöht: Titel 60px (von 45), Einträge 60px (von 50)
      });
    }
    
    // Fähigkeiten und Interessen (Überschrift) - immer anzeigen wenn Kernkompetenzen vorhanden sind
    if (cvData.coreCompetencies && cvData.coreCompetencies.length > 0) {
      elements.push({
        type: 'skillsAndInterestsTitle',
        content: getTranslatedTitle('FÄHIGKEITEN UND INTERESSEN'),
        height: 30
      });
    }
    
    // Kernkompetenzen
    if (cvData.coreCompetencies && cvData.coreCompetencies.length > 0 && showCoreCompetenciesInCv) {
      elements.push({
        type: 'coreCompetencies',
        content: cvData.coreCompetencies,
        height: 50 + Math.ceil(cvData.coreCompetencies.length / 2) * 18 // Erhöht: Titel 50px (von 15), Einträge 18px (von 12)
      });
    }
    
    // Zertifizierungen
    if (cvData.certifications && cvData.certifications.length > 0 && showCertificationsInCv) {
      elements.push({
        type: 'certifications',
        content: cvData.certifications,
        height: 50 + (cvData.certifications.length * 18) // Erhöht: Titel 50px (von 15), Einträge 18px (von 12)
      });
    }
    
    // IT-Skills
    if (cvData.relevantItSkills && cvData.relevantItSkills.length > 0 && showItSkillsInCv) {
      elements.push({
        type: 'itSkills',
        content: cvData.relevantItSkills,
        height: 50 + (cvData.relevantItSkills.length * 18) // Erhöht: Titel 50px (von 15), Einträge 18px (von 12)
      });
    }
    
    // Branchen
    if (cvData.relevantIndustries && cvData.relevantIndustries.length > 0 && showIndustriesInCv) {
      elements.push({
        type: 'industries',
        content: cvData.relevantIndustries,
        height: 50 + (cvData.relevantIndustries.length * 18) // Erhöht: Titel 50px (von 15), Einträge 18px (von 12)
      });
    }
    
    // Sprachen
    if (cvData.languages && cvData.languages.length > 0 && showLanguagesInCv) {
      elements.push({
        type: 'languages',
        content: cvData.languages,
        height: 50 + (cvData.languages.length * 18) // Erhöht: Titel 50px (von 15), Einträge 18px (von 12)
      });
    }
    
    // Hobbies
    if (cvData.relevantHobbies && cvData.relevantHobbies.length > 0 && showHobbiesInCv) {
      elements.push({
        type: 'hobbies',
        content: cvData.relevantHobbies,
        height: 50 + (cvData.relevantHobbies.length * 18) // Erhöht: Titel 50px (von 15), Einträge 18px (von 12)
      });
    }
    
    // Zusätzliche Informationen / Weitere Angaben - direkt nach Hobbys
    if (cvData.additionalInfoOptimized && cvData.additionalInfoOptimized.length > 0 && showAdditionalInfoInCv) {
      elements.push({
        type: 'additionalInfoOptimized',
        content: cvData.additionalInfoOptimized,
        height: 50 + (cvData.additionalInfoOptimized.length * 18) // Erhöht: Titel 50px (von 15), Einträge 18px (von 12)
      });
    }
    
    // Weitere Berufserfahrungen (nicht-relevante aus optimizedWorkExperiences)
    const nonRelevantWorkExperiences = cvData.optimizedWorkExperiences ? 
      cvData.optimizedWorkExperiences
        .map((work: any, idx: number) => ({ ...work, _originalIndex: idx }))
        .filter((work: any) => work.relevant === false || work.relevant === 'false') : [];
    
    if (nonRelevantWorkExperiences && nonRelevantWorkExperiences.length > 0 && showAdditionalWorkExperienceInCv) {
      elements.push({
        type: 'spacerBeforeAdditionalWorkExperience',
        content: null,
        height: 20 // Abstand zwischen Sektionen
      });
      
      elements.push({
        type: 'additionalWorkExperienceTitle',
        content: additionalWorkExperienceTitle,
        height: 30
      });
      
      // Jede weitere Berufserfahrung als einzelnes Element (reduzierte Darstellung)
      nonRelevantWorkExperiences.forEach((work: any, index: number) => {
        // Konservativere Höhenschätzung für kompakte Darstellung
        // Basis-Höhe für Position, Firma, Datum (2 Zeilen)
        const baseHeight = Math.max(50, 24 + estimateClassicWorkMetaHeight(work) + 8); // Passt sich an Firma/Branche/Datum an
        const safetyMargin = 16; // Groesserer Puffer, damit nichts ueber den Seitenumbruch laeuft
        const workHeight = baseHeight + safetyMargin;
        
        elements.push({
          type: 'additionalWorkExperience',
          content: work,
          _originalIndex: work._originalIndex,
          _workIndex: index,
          _totalCount: nonRelevantWorkExperiences.length,
          _isNonRelevant: true,
          height: workHeight
        });
      });
    }
    
    elements.forEach((el: any, idx: number) => { el._globalIndex = idx; });
    return elements;
  };

  // Hash von measuredHeights um Änderungen zu erkennen (für useMemo Dependency)
  const measuredHeightsHash = React.useMemo(() => {
    return JSON.stringify(measuredHeights);
  }, [measuredHeights]);

  // ZENTRALE GECACHTE SEITENBERECHNUNG - wird nur bei Änderungen neu berechnet
  // Verhindert Race Conditions und inkonsistente Ergebnisse
  // WICHTIG: cvData ist jetzt in Dependencies - Neuberechnung bei Daten-Änderungen
  // WICHTIG: refreshKey wird durch Modal-Schließen UND Visibility-Änderungen getriggert
  // WICHTIG: measuredHeightsHash wird verwendet um Änderungen an gemessenen Höhen zu erkennen
  const cachedDistributedPages = React.useMemo(() => {
    if (templateStyle === 'classic') {
      const allElements = getMinimalElements();
      const distributedPages = distributeElementsToPages(allElements, 246, 'minimal', true); // isMinimal = true
      return {
        type: 'classic' as const,
        pages: distributedPages,
        pageCount: distributedPages.length
      };
    } else {
      // Für Modern/Professional Template
      const distributedRightPages = distributeRightElementsToPages();
      const distributedLeftPages = distributeLeftElementsToPages();
      const maxPages = Math.max(distributedRightPages.length, distributedLeftPages.length);
      return {
        type: 'twocolumn' as const,
        rightPages: distributedRightPages,
        leftPages: distributedLeftPages,
        pageCount: maxPages
      };
    }
  }, [
    templateStyle, 
    measuredHeightsHash,
    columnContentHeightsPx.left,
    columnContentHeightsPx.right,
    refreshKey, 
    cvData.coreCompetencies?.length,
    cvData.workExperience?.length,
    cvData.relevantEducations?.length,
    cvData.itSkills?.length,
    cvData.languages?.length,
    cvData.certifications?.length,
    cvData.hobbies?.length,
    cvData.industries?.length,
    cvData.additionalInfo?.length,
    JSON.stringify(cvData.optimizedWorkExperiences?.map((w: any) => `${w.position}|${w.company}|${w.relevant}`)),
    cvData.personalInfo?.fullName,
    cvData.personalInfo?.shortProfile,
    showBulletPointsInCv,
    showCompanyNamesInCv,
    showIndustryInCv,
    showDateInCv,
    showProjectSearchInCv
  ]);

  // Aktualisiere pageCount automatisch wenn gecachte Berechnung sich ändert
  React.useEffect(() => {
    setPageCount(Math.max(1, cachedDistributedPages.pageCount));
  }, [cachedDistributedPages]);

  React.useEffect(() => {
    if (typeof onPageCountChange === 'function') {
      onPageCountChange(Math.max(1, pageCount));
    }
  }, [pageCount, onPageCountChange]);

  // Fix für initialen Render: Warte bis DOM nach cvData-Änderungen vollständig gerendert ist
  React.useEffect(() => {
    // Triggere Neuberechnung mit Delay wenn cvData-Arrays sich ändern
    const hasData = 
      (cvData.coreCompetencies?.length || 0) > 0 ||
      (cvData.workExperience?.length || 0) > 0 ||
      (cvData.relevantEducations?.length || 0) > 0 ||
      (cvData.itSkills?.length || 0) > 0;
    
    if (!hasData) {
      return;
    }
    
    // Verwende requestAnimationFrame + setTimeout für optimales Timing
    let timeoutId: NodeJS.Timeout;
    const rafId = requestAnimationFrame(() => {
      timeoutId = setTimeout(() => {
        triggerTemplateRefresh('legacy');
      }, 350); // 350ms Delay für vollständiges Rendering inkl. Fonts
    });

    return () => {
      cancelAnimationFrame(rafId);
      if (timeoutId) clearTimeout(timeoutId);
    };
  }, [
    cvData.coreCompetencies?.length,
    cvData.workExperience?.length,
    cvData.relevantEducations?.length,
    cvData.itSkills?.length,
    cvData.languages?.length,
    cvData.certifications?.length,
    triggerTemplateRefresh
  ]); // Triggere nur wenn Array-Längen sich ändern

  // Funktion zum Rendern mehrerer CV-Seiten
  const renderCvPages = () => {
    // Verwende GECACHTE Seitenberechnung statt Neuberechnung
    // Für Classic Template: Alle Elemente in einer Spalte
    if (templateStyle === 'classic' && cachedDistributedPages.type === 'classic') {
      // Verwende gecachte distributedPages - keine Neuberechnung!
      const distributedPages = cachedDistributedPages.pages!; // ! weil Type Guard sicherstellt dass pages existiert
      const pages = [];
      
      for (let i = 0; i < distributedPages.length; i++) {
        pages.push(
          <A4Page key={i}>
            {/* Header */}
            <PageHeader showFrames={showFrames} textColor={leftColumnTitleColor}>
              {/* Header ohne Text - Abstände bleiben erhalten */}
            </PageHeader>

            {/* Hauptinhalt-Bereich - Einspaltig für Minimal */}
            <PageContent>
              <Box 
                ref={templateRef}
                sx={{
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  position: 'relative',
                  zIndex: 1
                }}
              >
                {/* Einspaltige Anordnung für Minimal Template */}
                {distributedPages[i] && (
                  <FrameBox showFrames={showFrames} sx={{
                    position: 'absolute',
                    left: '0',
                    top: '25.4mm', // Word-Standard-Abstand oben (2.5cm)
                    width: '100%',
                    height: 'calc(297mm - 25.4mm - 25.4mm)', // 297mm - oben - unten
                    zIndex: 20,
                    padding: '0 10px',
                    display: 'flex',
                    flexDirection: 'column',
                    gap: '5px',
                    backgroundColor: 'transparent',
                    border: '2px solid green',
                    borderColor: showFrames ? 'green' : 'transparent'
                  }}>
                    {/* Seitennummer */}
                    {showFrames && (
                      <Box sx={{
                        position: 'absolute',
                        top: '5px',
                        right: '5px',
                        backgroundColor: 'green',
                        color: 'white',
                        padding: '2px 6px',
                        borderRadius: '3px',
                        fontSize: '12px',
                        fontWeight: 'bold',
                        zIndex: 25
                      }}>
                        Minimal-Seite {i + 1}
                      </Box>
                    )}
                    
                    {distributedPages[i].map((element: any, elementIndex: number) => {
                      const globalIdx = element._globalIndex !== undefined ? element._globalIndex : elementIndex;
                      const elementKey = `minimal_${element.type}_${globalIdx}`;
                      return (
                        <FrameBox 
                          key={elementIndex} 
                          showFrames={showFrames}
                          ref={(el) => { elementRefs.current[elementKey] = el as HTMLDivElement | null; }}
                          sx={{
                            border: `1px solid ${leftColumnTextColor}`,
                            borderColor: showFrames ? leftColumnTextColor : 'transparent',
                            borderRadius: '4px',
                            padding: '5px',
                            backgroundColor: 'transparent',
                            marginBottom: '5px',
                            transition: 'all 0.2s ease',
                            '&:hover': {
                              backgroundColor: '#f0f0f0',
                              border: '1px solid #d0d0d0'
                            }
                          }}
                        >
                          {/* Hier werden alle Elemente gerendert - Profilbild und Kontakt zusammen */}
                          {element.type === 'name' && (() => {
                            // Immer automatische Berechnung - manuelle Einstellungen werden überschrieben
                            // Classic Template - linke Spalte (200px)
                            const fontSize = tempManualFontSize !== null ? tempManualFontSize : calculateOptimalFontSizeWithLineBreaks(element.content, 200, nameFontSize).fontSize;
                            const formattedText = tempManualFontSize !== null ? element.content : calculateOptimalFontSizeWithLineBreaks(element.content, 200, nameFontSize).formattedText;
                            return (
                              <NameTitle 
                                nameTextColor={nameTextColor}
                                nameFontSize={fontSize}
                                onClick={handleNameClick}
                                sx={{
                                  cursor: isEditMode ? 'pointer' : 'default',
                                  '&:hover': isEditMode ? {
                                    backgroundColor: 'rgba(0, 0, 0, 0.04)',
                                    borderRadius: '4px',
                                    padding: '2px 4px',
                                    margin: '-2px -4px'
                                  } : {},
                                  fontSize: `${fontSize}pt !important`
                                }}
                              >
                                Lebenslauf
                              </NameTitle>
                            );
                          })()}
                          
                          {/* Kontaktdaten für Classic Template */}
                          {element.type === 'contact' && (
                            <Box>
                              {renderContactInfo()}
                            </Box>
                          )}
                          
                          {/* Profilbild für Classic Template */}
                          {element.type === 'profileImage' && (
                            <Box sx={{ 
                              // Classic: feste Position auf Höhe der Überschrift
                              // "PERSÖNLICHE INFORMATIONEN" für konsistente Ausrichtung.
                              position: 'absolute',
                              right: '20px',
                              top: '120px',
                              cursor: 'pointer',
                            }}
                            onClick={() => {
                              const event = new CustomEvent('openProfileImageModal');
                              window.dispatchEvent(event);
                            }}
                            >
                              <Box
                                sx={{
                                  width: 150,
                                  height: 150,
                                  border: profileImageBorder ? `${profileImageBorderThickness}px solid ${profileImageBorderColor}` : 'none',
                                  borderRadius: `${profileImageBorderRadius}%`,
                                  margin: '0',
                                  overflow: 'hidden',
                                  display: 'flex',
                                  justifyContent: 'center',
                                  alignItems: 'center',
                                  backgroundColor: 'transparent'
                                }}
                              >
                                <img
                                  src={element.content}
                                  alt="Profilbild"
                                  style={{
                                    maxWidth: '100%',
                                    maxHeight: '100%',
                                    objectFit: 'contain',
                                    userSelect: 'none'
                                  }}
                                />
                              </Box>
                            </Box>
                          )}
                          
                          {/* Alle anderen Elemente normal rendern */}
                          {element.type === 'profile' && (
                            <Box
                              onClick={(e) => {
                                const event = new CustomEvent('openProfileModal');
                                window.dispatchEvent(event);
                              }}
                              sx={{ cursor: 'pointer' }}
                            >
                              <SectionTitle textColor={leftColumnTitleColor}>
                                {profileTitle}
                              </SectionTitle>
                              <Typography sx={{ fontSize: '10pt', lineHeight: 1.5, fontFamily: 'var(--cv-body-font, Arial, sans-serif)', paddingLeft: '0px', color: leftColumnTextColor, whiteSpace: 'pre-wrap' }}>
                                {cvData.personalInfo?.shortProfile || 'Engagierte und erfahrene Fachfrau mit mehrjähriger Erfahrung in verschiedenen Bereichen.'}
                              </Typography>
                            </Box>
                          )}
                          
                          {/* Weitere Elemente... */}
                          {element.type === 'spacerBeforeWorkExperience' && (
                            <Box sx={{ height: '1mm' }} />
                          )}
                          
                          {element.type === 'workExperienceTitle' && (
                            <SectionTitle textColor={rightColumnTitleColor}>
                              {element.content}
                            </SectionTitle>
                          )}
                          
                          {element.type === 'workExperience' && (
                            <Box
                              onClick={(e) => {
                                const event = new CustomEvent('openWorkExperienceModal', {
                                  detail: { 
                                    title: workExperienceTitle,
                                    workExperience: element.content,
                                    isNonRelevant: false // Relevante Berufserfahrung
                                  }
                                });
                                window.dispatchEvent(event);
                              }}
                              sx={{ 
                                cursor: 'pointer',
                                position: 'relative',
                                pageBreakInside: 'avoid',
                                breakInside: 'avoid',
                                '&:hover .reorder-buttons': { opacity: 1 }
                              }}
                            >
                              {/* Reorder Buttons */}
                              {isEditMode && onWorkExperienceReorder && element._totalCount > 1 && (
                                <Box className="reorder-buttons" sx={{
                                  position: 'absolute',
                                  top: 0,
                                  right: 0,
                                  display: 'flex',
                                  gap: '2px',
                                  opacity: 0,
                                  transition: 'opacity 0.2s ease',
                                  zIndex: 10,
                                  backgroundColor: 'rgba(255,255,255,0.9)',
                                  borderRadius: '4px',
                                  padding: '1px'
                                }}>
                                  {element._workIndex > 0 && (
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onWorkExperienceReorder(element._originalIndex, 'up', false); }}
                                      sx={{ padding: '4px', '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' } }}>
                                      <KeyboardArrowUpIcon sx={{ fontSize: '24px' }} />
                                    </IconButton>
                                  )}
                                  {element._workIndex < element._totalCount - 1 && (
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onWorkExperienceReorder(element._originalIndex, 'down', false); }}
                                      sx={{ padding: '4px', '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' } }}>
                                      <KeyboardArrowDownIcon sx={{ fontSize: '24px' }} />
                                    </IconButton>
                                  )}
                                </Box>
                              )}
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                                {showDateInCv && (
                                <Box sx={{ width: '140px', flexShrink: 0 }}>
                                  <Typography sx={{ 
                                    fontSize: '10pt',
                                    fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                    color: rightColumnTextColor,
                                    fontWeight: 'bold'
                                  }}>
                                    {element.content.startDate} - {element.content.endDate || 'Heute'}
                                  </Typography>
                                </Box>
                                )}
                                <Box sx={{ flex: 1 }}>
                                  <Typography sx={{ 
                                    fontSize: '12pt', 
                                    fontWeight: 'bold',
                                    fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                    color: rightColumnTextColor,
                                    marginBottom: '2px'
                                  }}>
                                    {element.content.position || element.content.jobTitle}
                                  </Typography>
                                  {showCompanyNamesInCv && element.content.company && (
                                    <Typography sx={{ 
                                      fontSize: '10pt',
                                      fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                      color: rightColumnTextColor,
                                      marginBottom: element.content.industry ? '2px' : '5px'
                                    }}>
                                      {element.content.company}
                                    </Typography>
                                  )}
                                  {showIndustryInCv && element.content.industry && (
                                    <Typography sx={{ 
                                      fontSize: '9pt',
                                      fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                      color: rightColumnTextColor,
                                      fontStyle: 'italic',
                                      marginBottom: '5px'
                                    }}>
                                      {element.content.industry}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                              {element.content.description && (
                                <Box sx={{ paddingLeft: '160px', marginTop: '5px' }}>
                                  {showBulletPointsInCv ? (
                                    element.content.description.split('\n').filter((line: string) => line.trim()).map((line: string, idx: number) => (
                                      <Typography key={idx} sx={{ 
                                        fontSize: '10pt',
                                        lineHeight: 1.4,
                                        fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                        color: rightColumnTextColor,
                                        marginBottom: '2px',
                                        paddingLeft: '10px',
                                        position: 'relative',
                                        '&:before': {
                                          content: '"•"',
                                          position: 'absolute',
                                          left: '0',
                                          color: rightColumnTextColor
                                        }
                                      }}>
                                        {line.trim()}
                                      </Typography>
                                    ))
                                  ) : (
                                    <Typography sx={{ 
                                      fontSize: '10pt',
                                      lineHeight: 1.4,
                                      fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                      color: rightColumnTextColor,
                                      marginBottom: '5px',
                                      whiteSpace: 'pre-wrap'
                                    }}>
                                      {element.content.description}
                                    </Typography>
                                  )}
                                </Box>
                              )}
                              {element.content.responsibilities && element.content.responsibilities.length > 0 && (
                                <Box sx={{ paddingLeft: '125px', marginTop: '5px' }}>
                                  {element.content.responsibilities.map((responsibility: string, respIndex: number) => (
                                    <Typography key={respIndex} sx={{ 
                                      fontSize: '9pt', 
                                      lineHeight: 1.4, 
                                      fontFamily: 'var(--cv-body-font, Arial, sans-serif)', 
                                      color: rightColumnTextColor,
                                      marginBottom: '2px',
                                      paddingLeft: '10px',
                                      position: 'relative',
                                      '&:before': {
                                        content: '"•"',
                                        position: 'absolute',
                                        left: '0',
                                        color: rightColumnTextColor
                                      }
                                    }}>
                                      {responsibility}
                                    </Typography>
                                  ))}
                                </Box>
                              )}
                            </Box>
                          )}
                          
                          {/* Weitere Elemente für Minimal Template */}
                          {element.type === 'education' && (
                            <Box
                              onClick={(e) => {
                                const event = new CustomEvent('openEducationModal', {
                                  detail: { title: educationTitle }
                                });
                                window.dispatchEvent(event);
                              }}
                              sx={{ cursor: 'pointer' }}
                            >
                              <SectionTitle textColor={rightColumnTitleColor}>
                                {educationTitle}
                              </SectionTitle>
                              
                              {/* Abstand zwischen Titel und Einträgen */}
                              <Box sx={{ height: '15px' }} />
                              
                              {element.content.map((edu: any, eduIndex: number) => (
                                <Box key={eduIndex} sx={{ display: 'flex', alignItems: 'flex-start', gap: '20px', marginBottom: '10px' }}>
                                  <Box sx={{ width: '140px', flexShrink: 0 }}>
                                    <Typography sx={{ 
                                      fontSize: '10pt',
                                      fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                      color: rightColumnTextColor,
                                      fontWeight: 'bold'
                                    }}>
                                      {edu.startDate} - {edu.endDate}
                                    </Typography>
                                  </Box>
                                  <Box sx={{ flex: 1 }}>
                                    <Typography sx={{ 
                                      fontSize: '12pt', 
                                      fontWeight: 'bold',
                                      fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                      color: rightColumnTextColor,
                                      marginBottom: '2px'
                                    }}>
                                      {edu.degree}
                                    </Typography>
                                    {edu.description && (
                                      <Typography sx={{ 
                                        fontSize: '10pt',
                                        fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                        color: rightColumnTextColor,
                                        fontStyle: 'italic',
                                        marginBottom: '2px'
                                      }}>
                                        {edu.description}
                                      </Typography>
                                    )}
                                    <Typography sx={{ 
                                      fontSize: '10pt',
                                      fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                      color: rightColumnTextColor
                                    }}>
                                      {edu.institution}
                                    </Typography>
                                  </Box>
                                </Box>
                              ))}
                            </Box>
                          )}
                          
                          {element.type === 'skillsAndInterestsTitle' && (
                            <SectionTitle textColor={rightColumnTitleColor}>
                              {element.content}
                            </SectionTitle>
                          )}
                          
                          {element.type === 'coreCompetencies' && (
                            <Box
                              onClick={(e) => {
                                const event = new CustomEvent('openCoreCompetenciesModal', {
                                  detail: { title: coreCompetenciesTitle }
                                });
                                window.dispatchEvent(event);
                              }}
                              sx={{ cursor: 'pointer' }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                                <Typography sx={{ 
                                  fontSize: '10pt',
                                  lineHeight: 1.4,
                                  fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                  color: rightColumnTextColor,
                                  fontWeight: 'bold',
                                  width: '140px',
                                  flexShrink: 0
                                }}>
                                  {coreCompetenciesTitle}
                                </Typography>
                                <Box sx={{ flex: 1 }}>
                                  {element.content.map((comp: any, compIndex: number) => (
                                    <Typography key={compIndex} sx={{ 
                                      fontSize: '10pt', 
                                      marginBottom: '3px',
                                      fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                      color: rightColumnTextColor
                                    }}>
                                      • {typeof comp === 'string' ? comp : (comp.competency || '')}
                                    </Typography>
                                  ))}
                                </Box>
                              </Box>
                            </Box>
                          )}
                          
                          {element.type === 'certifications' && (
                            <Box
                              onClick={(e) => {
                                const event = new CustomEvent('openCertificationsModal', {
                                  detail: { title: certificationsTitle }
                                });
                                window.dispatchEvent(event);
                              }}
                              sx={{ cursor: 'pointer' }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                                <Typography sx={{ 
                                  fontSize: '10pt',
                                  lineHeight: 1.4,
                                  fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                  color: rightColumnTextColor,
                                  fontWeight: 'bold',
                                  width: '140px',
                                  flexShrink: 0
                                }}>
                                  {certificationsTitle}
                                </Typography>
                                <Box sx={{ flex: 1 }}>
                                  {element.content.map((cert: any, certIndex: number) => (
                                    <Typography key={certIndex} sx={{ 
                                      fontSize: '10pt', 
                                      marginBottom: '3px',
                                      fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                      color: rightColumnTextColor
                                    }}>
                                      • {cert.name}
                                    </Typography>
                                  ))}
                                </Box>
                              </Box>
                            </Box>
                          )}
                          
                          {element.type === 'itSkills' && (
                            <Box
                              onClick={(e) => {
                                const event = new CustomEvent('openItSkillsModal', {
                                  detail: { title: itSkillsTitle }
                                });
                                window.dispatchEvent(event);
                              }}
                              sx={{ cursor: 'pointer' }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                                <Typography sx={{ 
                                  fontSize: '10pt',
                                  lineHeight: 1.4,
                                  fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                  color: rightColumnTextColor,
                                  fontWeight: 'bold',
                                  width: '140px',
                                  flexShrink: 0
                                }}>
                                  {itSkillsTitle}
                                </Typography>
                                <Box sx={{ flex: 1 }}>
                                  {element.content.map((skill: any, skillIndex: number) => (
                                    <Typography key={skillIndex} sx={{ 
                                      fontSize: '10pt', 
                                      marginBottom: '3px',
                                      fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                      color: rightColumnTextColor
                                    }}>
                                      • {skill.skill}
                                    </Typography>
                                  ))}
                                </Box>
                              </Box>
                            </Box>
                          )}
                          
                          {element.type === 'industries' && (
                            <Box
                              onClick={(e) => {
                                const event = new CustomEvent('openIndustriesModal', {
                                  detail: { title: industriesTitle }
                                });
                                window.dispatchEvent(event);
                              }}
                              sx={{ cursor: 'pointer' }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                                <Typography sx={{ 
                                  fontSize: '10pt',
                                  lineHeight: 1.4,
                                  fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                  color: rightColumnTextColor,
                                  fontWeight: 'bold',
                                  width: '140px',
                                  flexShrink: 0
                                }}>
                                  {industriesTitle}
                                </Typography>
                                <Box sx={{ flex: 1 }}>
                                  {element.content.map((industry: any, industryIndex: number) => (
                                    <Box key={industryIndex} sx={{ 
                                      display: 'flex', 
                                      marginBottom: '3px',
                                      alignItems: 'center'
                                    }}>
                                      <Typography sx={{ 
                                        fontSize: '10pt', 
                                        fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                        color: rightColumnTextColor,
                                        width: '160px',
                                        flexShrink: 0
                                      }}>
                                        • {industry.industry}
                                      </Typography>
                                      <Typography sx={{ 
                                        fontSize: '10pt', 
                                        fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                        color: rightColumnTextColor,
                                        minWidth: '120px',
                                        textAlign: 'right'
                                      }}>
                                        ({industry.years} {translatedYears})
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            </Box>
                          )}
                          
                          {element.type === 'languages' && (
                            <Box
                              onClick={(e) => {
                                const event = new CustomEvent('openLanguagesModal', {
                                  detail: { title: languagesTitle }
                                });
                                window.dispatchEvent(event);
                              }}
                              sx={{ cursor: 'pointer' }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                                <Typography sx={{ 
                                  fontSize: '10pt',
                                  lineHeight: 1.4,
                                  fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                  color: rightColumnTextColor,
                                  fontWeight: 'bold',
                                  width: '140px',
                                  flexShrink: 0
                                }}>
                                  {languagesTitle}
                                </Typography>
                                <Box sx={{ flex: 1 }}>
                                  {element.content.map((language: any, languageIndex: number) => (
                                    <Box key={languageIndex} sx={{ 
                                      display: 'flex', 
                                      marginBottom: '3px',
                                      alignItems: 'center'
                                    }}>
                                      <Typography sx={{ 
                                        fontSize: '10pt', 
                                        fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                        color: rightColumnTextColor,
                                        width: '160px',
                                        flexShrink: 0,
                                        marginRight: '20px'
                                      }}>
                                        • {language.language}
                                      </Typography>
                                      <Typography sx={{ 
                                        fontSize: '10pt', 
                                        fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                        color: rightColumnTextColor,
                                        minWidth: '120px'
                                      }}>
                                        {getTranslatedLanguageLevel(language.level)}
                                      </Typography>
                                    </Box>
                                  ))}
                                </Box>
                              </Box>
                            </Box>
                          )}
                          
                          {element.type === 'hobbies' && (
                            <Box
                              onClick={(e) => {
                                const event = new CustomEvent('openHobbiesModal', {
                                  detail: { title: hobbiesTitle }
                                });
                                window.dispatchEvent(event);
                              }}
                              sx={{ cursor: 'pointer' }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                                <Typography sx={{ 
                                  fontSize: '10pt',
                                  lineHeight: 1.4,
                                  fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                  color: rightColumnTextColor,
                                  fontWeight: 'bold',
                                  width: '140px',
                                  flexShrink: 0
                                }}>
                                  {hobbiesTitle}
                                </Typography>
                                <Box sx={{ flex: 1 }}>
                                  {element.content.map((hobby: any, hobbyIndex: number) => (
                                    <Typography key={hobbyIndex} sx={{ 
                                      fontSize: '10pt', 
                                      marginBottom: '3px',
                                      fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                      color: rightColumnTextColor
                                    }}>
                                      • {hobby.hobby}
                                    </Typography>
                                  ))}
                                </Box>
                              </Box>
                            </Box>
                          )}
                          
                          {element.type === 'spacerBeforeAdditionalWorkExperience' && (
                            <Box sx={{ height: '10px' }} />
                          )}
                          
                          {element.type === 'additionalWorkExperienceTitle' && (
                            <SectionTitle textColor={rightColumnTitleColor}>
                              {element.content}
                            </SectionTitle>
                          )}
                          
                          {element.type === 'additionalWorkExperience' && (
                            <Box
                              onClick={(e) => {
                                const event = new CustomEvent('openWorkExperienceModal', {
                                  detail: { 
                                    title: additionalWorkExperienceTitle,
                                    workExperience: element.content,
                                    isNonRelevant: true // Weitere Berufserfahrung
                                  }
                                });
                                window.dispatchEvent(event);
                              }}
                              sx={{ 
                                cursor: 'pointer',
                                position: 'relative',
                                '&:hover .reorder-buttons': { opacity: 1 }
                              }}
                            >
                              {/* Reorder Buttons */}
                              {isEditMode && onWorkExperienceReorder && element._totalCount > 1 && (
                                <Box className="reorder-buttons" sx={{
                                  position: 'absolute',
                                  top: 0,
                                  right: 0,
                                  display: 'flex',
                                  gap: '2px',
                                  opacity: 0,
                                  transition: 'opacity 0.2s ease',
                                  zIndex: 10,
                                  backgroundColor: 'rgba(255,255,255,0.9)',
                                  borderRadius: '4px',
                                  padding: '1px'
                                }}>
                                  {element._workIndex > 0 && (
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onWorkExperienceReorder(element._originalIndex, 'up', true); }}
                                      sx={{ padding: '4px', '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' } }}>
                                      <KeyboardArrowUpIcon sx={{ fontSize: '24px' }} />
                                    </IconButton>
                                  )}
                                  {element._workIndex < element._totalCount - 1 && (
                                    <IconButton size="small" onClick={(e) => { e.stopPropagation(); onWorkExperienceReorder(element._originalIndex, 'down', true); }}
                                      sx={{ padding: '4px', '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' } }}>
                                      <KeyboardArrowDownIcon sx={{ fontSize: '24px' }} />
                                    </IconButton>
                                  )}
                                </Box>
                              )}
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                                {showDateInCv && (
                                <Box sx={{ width: '140px', flexShrink: 0 }}>
                                  <Typography sx={{ 
                                    fontSize: '10pt', 
                                    fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                    color: rightColumnTextColor,
                                    fontWeight: 'bold'
                                  }}>
                                    {element.content.startDate} - {element.content.endDate || 'Heute'}
                                  </Typography>
                                </Box>
                                )}
                                <Box sx={{ flex: 1 }}>
                                  <Typography sx={{ 
                                    fontSize: '12pt', 
                                    fontWeight: 'bold',
                                    fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                    color: rightColumnTextColor,
                                    marginBottom: '2px'
                                  }}>
                                    {element.content.position || element.content.jobTitle}
                                  </Typography>
                                  {showCompanyNamesInCv && element.content.company && (
                                    <Typography sx={{ 
                                      fontSize: '10pt',
                                      fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                      color: rightColumnTextColor
                                    }}>
                                      {element.content.company}
                                    </Typography>
                                  )}
                                  {showIndustryInCv && element.content.industry && (
                                    <Typography sx={{ 
                                      fontSize: '9pt',
                                      fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                      color: rightColumnTextColor,
                                      fontStyle: 'italic'
                                    }}>
                                      {element.content.industry}
                                    </Typography>
                                  )}
                                </Box>
                              </Box>
                            </Box>
                          )}
                          
                          {element.type === 'additionalInfoOptimized' && (
                            <Box
                              onClick={(e) => {
                                const event = new CustomEvent('openAdditionalInfoModal', {
                                  detail: { title: additionalInfoTitle }
                                });
                                window.dispatchEvent(event);
                              }}
                              sx={{ cursor: 'pointer' }}
                            >
                              <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: '20px' }}>
                                <Typography sx={{ 
                                  fontSize: '10pt',
                                  lineHeight: 1.4,
                                  fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                  color: rightColumnTextColor,
                                  fontWeight: 'bold',
                                  width: '140px',
                                  flexShrink: 0
                                }}>
                                  {additionalInfoTitle}
                                </Typography>
                                <Box sx={{ flex: 1 }}>
                                  {element.content.map((info: any, infoIndex: number) => (
                                    <Typography key={infoIndex} sx={{ 
                                      fontSize: '10pt', 
                                      marginBottom: '3px',
                                      fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                      color: rightColumnTextColor
                                    }}>
                                      • {info.info && info.details ? `${info.info}: ${info.details}` : info.description || info.info || info.details}
                                    </Typography>
                                  ))}
                                </Box>
                              </Box>
                            </Box>
                          )}
                        </FrameBox>
                      );
                    })}
                  </FrameBox>
                )}
              </Box>
            </PageContent>
          </A4Page>
        );
      }
      return pages;
    }
    
    // Standard zweispaltiges Layout für Modern und Professional
    // Verwende GECACHTE Werte statt Neuberechnung!
    if (cachedDistributedPages.type !== 'twocolumn') {
      return [];
    }
    
    const distributedRightPages = cachedDistributedPages.rightPages!; // ! weil Type Guard sicherstellt dass rightPages existiert
    const distributedLeftPages = cachedDistributedPages.leftPages!; // ! weil Type Guard sicherstellt dass leftPages existiert
    const maxPages = Math.max(distributedRightPages.length, distributedLeftPages.length);
    const isInitialRender = (cachedDistributedPages as any).isInitialRender === true;
    const pages = [];
    for (let i = 0; i < maxPages; i++) {
      pages.push(
        <A4Page key={i} ref={i === 0 ? firstPageRef : undefined}>
          {/* Beige DIV auf der linken Seite - über Header und Footer (immer sichtbar, unabhängig von showFrames) */}
          <Box sx={{ position: 'relative', width: '100%', height: '100%' }}>
            <GreenLeftBar leftColumnColor={leftColumnColor} />
            <LeftColumnOverlay>
              {leftColumnImage && (
                <Box
                  component="img"
                  src={leftColumnImage}
                  alt=""
                  sx={{
                    position: 'absolute',
                    top: 0,
                    left: 0,
                    width: 'auto',
                    height: 'auto',
                    maxWidth: '318px',
                    maxHeight: '1123px'
                  }}
                />
              )}
            </LeftColumnOverlay>
            {i === 0 && (
              <TopFullWidthOverlay 
                topBoxColor={topBoxColor}
                topBoxImage={topBoxImage}
                topBoxHeightMode={templateStyle === 'classic' ? 'nameBottom' : topBoxHeightMode}
                measuredNameBottomPx={templateStyle === 'classic' ? null : measuredNameBottomPx}
                measuredProfileMiddlePx={templateStyle === 'classic' ? null : measuredProfileMiddlePx}
              />
            )}
          </Box>
          
          {/* Header für jede Seite */}
          <PageHeader showFrames={showFrames} textColor={leftColumnTitleColor}>
            {/* Header ohne Text - Abstände bleiben erhalten */}
          </PageHeader>

          {/* Hauptinhalt-Bereich */}
          <PageContent>
            {/* CV-Inhalt auf allen Seiten */}
            {true && (
              <Box 
                ref={templateRef}
                sx={{
                  display: 'flex',
                  width: '100%',
                  height: '100%',
                  position: 'relative',
                  zIndex: 1 // Im Vordergrund über dem grünen DIV
                }}
              >
                        {/* Dynamische Platzierung der linken Spalte Elemente im grünen DIV */}
            {distributedLeftPages[i] && (
              <FrameBox
                ref={(el) => { if (i === 0) leftColumnFrameRef.current = el as HTMLDivElement | null; }}
                showFrames={showFrames}
                sx={{
                position: 'absolute',
                left: '-17mm', // Weiter links
                top: '2mm', // Einheitlicher fixer Offset oben für beide Spalten
                width: '42%', // Breiter
                height: '246mm', // Explizite, stabile Content-Höhe
                zIndex: 20,
                padding: '0px',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                gap: '6px',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                border: '2px solid blue',
                borderColor: showFrames ? 'blue' : 'transparent'
              }}>
                {/* Seitennummer */}
                {showFrames && (
                  <Box sx={{
                    position: 'absolute',
                    top: '5px',
                    right: '5px',
                    backgroundColor: 'blue',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    zIndex: 25
                  }}>
                    L-Seite {i + 1}
                  </Box>
                )}
                
                {distributedLeftPages[i].map((element: any, elementIndex: number) => {
                  const globalIdx = element._globalIndex !== undefined ? element._globalIndex : elementIndex;
                  const elementKey = `${templateStyle}_left_${element.type}_${globalIdx}`;
                  return (
                    <FrameBox 
                      key={elementIndex} 
                      showFrames={showFrames}
                      ref={(el) => { elementRefs.current[elementKey] = el as HTMLDivElement | null; }}
                      sx={{
                        border: `1px solid ${leftColumnTextColor}`,
                        borderColor: showFrames ? leftColumnTextColor : 'transparent',
                        borderRadius: '4px',
                        width: 'calc(100% + 4px)',
                        marginLeft: '-2px',
                        boxSizing: 'border-box',
                        padding: '1px',
                        backgroundColor: 'transparent',
                        marginBottom: '0px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: '#f0f0f0',
                          border: '1px solid #d0d0d0'
                        }
                      }}
                    >
                    {element.type === 'name' && templateStyle !== 'professional' && (() => {
                      // Immer automatische Berechnung - manuelle Einstellungen werden überschrieben
                      // Modern: 150px, Classic: 200px
                      const maxWidth = templateStyle === 'classic' ? 200 : 150;
                      const fontSize = tempManualFontSize !== null ? tempManualFontSize : calculateOptimalFontSizeWithLineBreaks(element.content, maxWidth, nameFontSize).fontSize;
                      const formattedText = tempManualFontSize !== null ? element.content : calculateOptimalFontSizeWithLineBreaks(element.content, maxWidth, nameFontSize).formattedText;
                      return (
                        <NameTitle 
                          data-cv-name-title="true"
                          nameTextColor={nameTextColor}
                          nameFontSize={fontSize}
                          onClick={handleNameClick}
                          sx={{
                            cursor: isEditMode ? 'pointer' : 'default',
                            '&:hover': isEditMode ? {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                              borderRadius: '4px',
                              padding: '2px 4px',
                              margin: '-2px -4px'
                            } : {},
                            fontSize: `${fontSize}pt !important`
                          }}
                        >
                          {formattedText}
                        </NameTitle>
                      );
                    })()}
                    {element.type === 'profileImage' && (
                      <Box 
                        data-cv-profile-image="true"
                        sx={{ 
                          textAlign: profileImageAlignment === 'left' ? 'left' : 
                                   profileImageAlignment === 'right' ? 'right' : 'center',
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          // Trigger das Profilbild-Modal
                          const event = new CustomEvent('openProfileImageModal');
                          window.dispatchEvent(event);
                        }}
                      >
                        <Box
                          sx={{
                            width: getProfileImageSize(profileImageSize),
                            aspectRatio: '1 / 1', // Quadratischer Container
                            margin: profileImageAlignment === 'left' ? '0 0 5px 0' :
                                    profileImageAlignment === 'right' ? '0 0 5px auto' :
                                    '0 auto 5px auto',
                            overflow: 'hidden',
                            borderRadius: `${profileImageBorderRadius}%`,
                            display: 'block',
                            border: profileImageBorder ? `${profileImageBorderThickness}px solid ${profileImageBorderColor}` : 'none'
                          }}
                        >
                          <img
                            src={element.content}
                            alt="Profilbild"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              userSelect: 'none'
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                    {element.type === 'profileImagePlaceholder' && (
                      <Box data-cv-profile-image="true" sx={{ 
                        height: '19px', // Einheitlich zu Berechnung (19px)
                        backgroundColor: 'transparent',
                        border: '2px dashed #ccc',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        color: '#999',
                        fontSize: '12px'
                      }}>
                        Profilbild Platzhalter
                      </Box>
                    )}
                    {element.type === 'contact' && (
                      <Box sx={{
                        // Zusätzlicher Abstand zum Profilbild im Professional Template
                        // Die Rahmenbreite beeinflusst die vertikale Positionierung der Kontakte
                        ...(templateStyle === 'professional' && {
                          marginTop: `calc(10px + ${profileImageBorder ? profileImageBorderThickness : 0}px)`
                        })
                      }}>
                        <SectionTitle 
                          textColor={leftColumnTitleColor}
                          onClick={handleContactClick}
                          sx={{ cursor: isEditMode ? 'pointer' : 'default' }}
                        >
                          {getTranslatedTitle('KONTAKT')}
                        </SectionTitle>
                        {element.content.email && (
                          <ContactItem textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>
                            {renderContactLabel('email')}
                            <div
                              contentEditable
                              suppressContentEditableWarning
                              style={{
                                fontSize: '10pt',
                                fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                color: leftColumnTextColor,
                                outline: 'none',
                                border: '1px solid transparent',
                                padding: '2px',
                                cursor: 'text',
                                flex: 1
                              }}
                              onFocus={(e) => {
                                e.currentTarget.style.border = '1px solid #ccc';
                                e.currentTarget.style.backgroundColor = '#f9f9f9';
                              }}
                              onBlur={(e) => {
                                e.currentTarget.style.border = '1px solid transparent';
                                e.currentTarget.style.backgroundColor = 'transparent';
                                onContactChange?.('email', e.currentTarget.textContent || '');
                                // Re-Messung nach Inline-Bearbeitung, damit linke Spalte (z. B. Professional) nicht überlappt
                                setTimeout(() => triggerTemplateRefresh('legacy'), 400);
                              }}
                            >
                              {element.content.email}
                            </div>
                          </ContactItem>
                        )}
                        {element.content.phone && (
                          <ContactItem textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>
                            {renderContactLabel('phone')}
                            <div
                              contentEditable
                              suppressContentEditableWarning
                              style={{
                                fontSize: '10pt',
                                fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                color: leftColumnTextColor,
                                outline: 'none',
                                border: '1px solid transparent',
                                padding: '2px',
                                cursor: 'text',
                                flex: 1
                              }}
                              onFocus={(e) => {
                                e.currentTarget.style.border = '1px solid #ccc';
                                e.currentTarget.style.backgroundColor = '#f9f9f9';
                              }}
                              onBlur={(e) => {
                                e.currentTarget.style.border = '1px solid transparent';
                                e.currentTarget.style.backgroundColor = 'transparent';
                                onContactChange?.('phone', e.currentTarget.textContent || '');
                                setTimeout(() => triggerTemplateRefresh('legacy'), 400);
                              }}
                            >
                              {element.content.phone}
                            </div>
                          </ContactItem>
                        )}
                        {element.content.address && (
                          <ContactItem textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>
                            {renderContactLabel('address')}
                            <div
                              contentEditable
                              suppressContentEditableWarning
                              style={{
                                fontSize: '10pt',
                                fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                color: leftColumnTextColor,
                                outline: 'none',
                                border: '1px solid transparent',
                                padding: '2px',
                                cursor: 'text',
                                flex: 1
                              }}
                              onFocus={(e) => {
                                e.currentTarget.style.border = '1px solid #ccc';
                                e.currentTarget.style.backgroundColor = '#f9f9f9';
                              }}
                              onBlur={(e) => {
                                e.currentTarget.style.border = '1px solid transparent';
                                e.currentTarget.style.backgroundColor = 'transparent';
                                onContactChange?.('address', e.currentTarget.textContent || '');
                                setTimeout(() => triggerTemplateRefresh('legacy'), 400);
                              }}
                            >
                              {element.content.address}
                            </div>
                          </ContactItem>
                        )}
                      </Box>
                    )}
                    {element.type === 'spacerBeforeProfile' && (
                      <Box sx={{ 
                        height: '0mm',
                        backgroundColor: 'transparent',
                        borderTop: `1px solid ${leftColumnTextColor}` // Farbe aus Textfarben-Modal für linke Spalte
                      }} />
                    )}
                    {element.type === 'profileMeta' && element.content?.nationality && (
                      <Box sx={{ mt: 0.5 }}>
                        <ContactItem textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>
                          <div style={{ fontSize: '10pt', fontFamily: 'var(--cv-body-font, Arial, sans-serif)', color: leftColumnTextColor }}>
                            <strong>{getTranslatedContactLabel('Nationalität')}</strong>
                          </div>
                          <div style={{ fontSize: '10pt', fontFamily: 'var(--cv-body-font, Arial, sans-serif)', color: leftColumnTextColor }}>
                            {element.content.nationality}
                          </div>
                        </ContactItem>
                      </Box>
                    )}
                    {element.type === 'profileMeta' && element.content?.residencePermit && (
                      <Box sx={{ mt: 0.5 }}>
                        <ContactItem textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>
                          <div style={{ fontSize: '10pt', fontFamily: 'var(--cv-body-font, Arial, sans-serif)', color: leftColumnTextColor }}>
                            <strong>{getTranslatedContactLabel('Aufenthaltsbewilligung')}</strong>
                          </div>
                          <div style={{ fontSize: '10pt', fontFamily: 'var(--cv-body-font, Arial, sans-serif)', color: leftColumnTextColor }}>
                            {element.content.residencePermit}
                          </div>
                        </ContactItem>
                      </Box>
                    )}
                    {element.type === 'profileMeta' && element.content?.residencePermit && (
                      <Box sx={{ 
                        height: '2mm',
                        backgroundColor: 'transparent'
                      }} />
                    )}
                    {element.type === 'profile' && templateStyle !== 'professional' && (
                      <Box
                        onClick={(e) => {
                          // Trigger the Profile modal with current title
                          const event = new CustomEvent('openProfileModal', {
                            detail: { title: profileTitle }
                          });
                          window.dispatchEvent(event);
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <SectionTitle textColor={leftColumnTitleColor}>
        {profileTitle}
      </SectionTitle>
                        <div
                          style={{
                            fontSize: '10pt',
                            lineHeight: 1.5,
                            fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                            color: leftColumnTextColor,
                            minHeight: '3em',
                            padding: '2px',
                            whiteSpace: 'pre-wrap'
                          }}
                        >
                          {element.content}
                        </div>
                      </Box>
                    )}
                    {element.type === 'coreCompetencies' && (
                      <Box
                        onClick={(e) => {
                          // Trigger the core competencies modal with current title
                          const event = new CustomEvent('openCoreCompetenciesModal', {
                            detail: { title: coreCompetenciesTitle }
                          });
                          window.dispatchEvent(event);
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <SectionTitle textColor={leftColumnTitleColor}>
                          {coreCompetenciesTitle}
                        </SectionTitle>
                        {element.content.map((comp: any, compIndex: number) => (
                          <ListItem key={compIndex} textColor={leftColumnTextColor} sx={{ fontSize: '10pt', marginBottom: '3px' }}>
                            <span>
                              {typeof comp === 'string' ? comp : (comp.competency || '')}
                            </span>
                          </ListItem>
                        ))}
                      </Box>
                    )}
                    {element.type === 'education' && (
                      <Box
                        onClick={(e) => {
                          // Trigger the Education modal with current title
                          const event = new CustomEvent('openEducationModal', {
                            detail: { title: educationTitle }
                          });
                          window.dispatchEvent(event);
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <SectionTitle textColor={leftColumnTitleColor}>
                          {educationTitle}
                        </SectionTitle>
                        {element.content.map((edu: any, eduIndex: number) => (
                          <EducationItem key={eduIndex} textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>
                            <EducationDegree textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>{edu.degree}</EducationDegree>
                            {edu.description && (
                              <EducationInstitution textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important`, fontStyle: 'italic' }}>{edu.description}</EducationInstitution>
                            )}
                            <EducationInstitution textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>{edu.institution}</EducationInstitution>
                            <EducationPeriod textColor={leftColumnTextColor} sx={{ color: `${leftColumnTextColor} !important` }}>{edu.startDate} - {edu.endDate}</EducationPeriod>
                          </EducationItem>
                        ))}
                      </Box>
                    )}
                    {element.type === 'certifications' && (
                      <Box
                        onClick={(e) => {
                          // Trigger the certifications modal with current title
                          const event = new CustomEvent('openCertificationsModal', {
                            detail: { title: certificationsTitle }
                          });
                          window.dispatchEvent(event);
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <SectionTitle textColor={leftColumnTitleColor}>
                          {certificationsTitle}
                        </SectionTitle>
                        {element.content.map((cert: any, certIndex: number) => (
                          <ListItem key={certIndex} textColor={leftColumnTextColor} sx={{ fontSize: '10pt', marginBottom: '3px' }}>
                            {cert.name}
                          </ListItem>
                        ))}
                      </Box>
                                          )}
                    {element.type === 'itSkills' && (
                      <Box
                        onClick={(e) => {
                          // Trigger the IT-Skills modal with current title
                          const event = new CustomEvent('openItSkillsModal', {
                            detail: { title: itSkillsTitle }
                          });
                          window.dispatchEvent(event);
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <SectionTitle textColor={leftColumnTitleColor}>
                          {itSkillsTitle}
                        </SectionTitle>
                        {element.content.map((skill: any, skillIndex: number) => (
                          <ListItem key={skillIndex} textColor={leftColumnTextColor} sx={{ fontSize: '10pt', marginBottom: '3px' }}>
                            {skill.skill}
                          </ListItem>
                        ))}
                      </Box>
                                          )}
                    {element.type === 'industries' && (
                      <Box
                        onClick={(e) => {
                          // Trigger the Industries modal with current title
                          const event = new CustomEvent('openIndustriesModal', {
                            detail: { title: industriesTitle }
                          });
                          window.dispatchEvent(event);
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <SectionTitle textColor={leftColumnTitleColor}>
                          {industriesTitle}
                        </SectionTitle>
                        {element.content.map((industry: any, industryIndex: number) => (
                          <ListItem key={industryIndex} textColor={leftColumnTextColor} sx={{ 
                            fontSize: '10pt', 
                            marginBottom: '3px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            paddingRight: '8px'
                          }}>
                            <span>{industry.industry}</span>
                            <span>({industry.years} {translatedYears})</span>
                          </ListItem>
                        ))}
                      </Box>
                                          )}
                    {element.type === 'languages' && (
                      <Box
                        onClick={(e) => {
                          // Trigger the Languages modal with current title
                          const event = new CustomEvent('openLanguagesModal', {
                            detail: { title: languagesTitle }
                          });
                          window.dispatchEvent(event);
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <SectionTitle textColor={leftColumnTitleColor}>
                          {languagesTitle}
                        </SectionTitle>
                        {element.content.map((language: any, languageIndex: number) => (
                          <ListItem key={languageIndex} textColor={leftColumnTextColor} sx={{ 
                            fontSize: '10pt', 
                            marginBottom: '3px',
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            paddingRight: '8px'
                          }}>
                            <span>{language.language}</span>
                            <span>({getTranslatedLanguageLevel(language.level)})</span>
                          </ListItem>
                        ))}
                      </Box>
                                          )}
                    {element.type === 'hobbies' && (
                      <Box
                        onClick={(e) => {
                          // Trigger the Hobbies modal with current title
                          const event = new CustomEvent('openHobbiesModal', {
                            detail: { title: hobbiesTitle }
                          });
                          window.dispatchEvent(event);
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <SectionTitle textColor={leftColumnTitleColor}>
                          {hobbiesTitle}
                        </SectionTitle>
                        {element.content.map((hobby: any, hobbyIndex: number) => (
                          <ListItem key={hobbyIndex} textColor={leftColumnTextColor} sx={{ fontSize: '10pt', marginBottom: '3px' }}>
                            {hobby.hobby}
                          </ListItem>
                        ))}
                      </Box>
                                          )}
                    {element.type === 'additionalInfoOptimized' && (
                      <Box
                        onClick={(e) => {
                          // Trigger the Additional Info modal with current title
                          const event = new CustomEvent('openAdditionalInfoModal', {
                            detail: { title: additionalInfoTitle }
                          });
                          window.dispatchEvent(event);
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <SectionTitle textColor={leftColumnTitleColor}>{additionalInfoTitle}</SectionTitle>
                        {element.content.map((info: any, infoIndex: number) => (
                          <ListItem key={infoIndex} textColor={leftColumnTextColor} sx={{ fontSize: '10pt', marginBottom: '3px' }}>
                            {info.info && info.details ? `${info.info}: ${info.details}` : info.description || info.info || info.details}
                          </ListItem>
                        ))}
                      </Box>
                    )}
                    {element.type === 'additionalInfoTitle' && (
                      <SectionTitle textColor={leftColumnTitleColor}>{element.content}</SectionTitle>
                    )}
                    {element.type === 'additionalInfo' && (
                      <Box>
                        {element.content.map((info: any, infoIndex: number) => (
                          <AdditionalInfoItem key={infoIndex}>
                            <AdditionalInfoText>{info.info}</AdditionalInfoText>
                          </AdditionalInfoItem>
                        ))}
                      </Box>
                    )}
                  </FrameBox>
                );
                })}
              </FrameBox>
            )}


              </Box>
            )}

            {/* Dynamische Platzierung der rechten Spalte Elemente im blauen DIV */}
            {distributedRightPages[i] && (
              <FrameBox
                ref={(el) => { if (i === 0) rightColumnFrameRef.current = el as HTMLDivElement | null; }}
                showFrames={showFrames}
                sx={{
                position: 'absolute',
                left: 'calc(25.4mm + 30%)',
                top: '2mm', // Einheitlicher fixer Offset oben für beide Spalten
                height: '246mm', // Explizite, stabile Content-Höhe
                width: 'calc(45% + 15mm)', // 5% breiter + 5mm zusätzlich
                zIndex: 20,
                padding: '10px 10px 0px 10px',
                boxSizing: 'border-box',
                display: 'flex',
                flexDirection: 'column',
                gap: '5px',
                overflow: 'hidden',
                backgroundColor: 'transparent',
                border: '2px solid red',
                borderColor: showFrames ? 'red' : 'transparent'
              }}>
                {/* Seitennummer */}
                {showFrames && (
                  <Box sx={{
                    position: 'absolute',
                    top: '5px',
                    right: '5px',
                    backgroundColor: 'red',
                    color: 'white',
                    padding: '2px 6px',
                    borderRadius: '3px',
                    fontSize: '12px',
                    fontWeight: 'bold',
                    zIndex: 25
                  }}>
                    Seite {i + 1}
                  </Box>
                )}
                
                {distributedRightPages[i].map((element: any, elementIndex: number) => {
                  const globalIdx = element._globalIndex !== undefined ? element._globalIndex : elementIndex;
                  const elementKey = `${templateStyle}_${element.type}_${globalIdx}`;
                  return (
                    <FrameBox 
                      key={elementIndex} 
                      showFrames={showFrames}
                      ref={(el) => { elementRefs.current[elementKey] = el as HTMLDivElement | null; }}
                      sx={{
                        border: `1px solid ${leftColumnTextColor}`,
                        borderColor: showFrames ? leftColumnTextColor : 'transparent',
                        borderRadius: '4px',
                        padding: '4px',
                        backgroundColor: 'transparent',
                        marginBottom: '0px',
                        transition: 'all 0.2s ease',
                        '&:hover': {
                          backgroundColor: '#f0f0f0',
                          border: '1px solid #d0d0d0'
                        }
                      }}
                    >
                    {element.type === 'name' && templateStyle === 'professional' && (() => {
                      // Immer automatische Berechnung - manuelle Einstellungen werden überschrieben
                      // Professional Template - rechte Spalte (mehr nutzbare Breite)
                      const maxWidth = 400;
                      const baseForTemplate = 40; // Professional Template: 40pt als Basis
                      const calc = calculateOptimalFontSizeWithLineBreaks(element.content, maxWidth, baseForTemplate);
                      const fontSize = Math.min(tempManualFontSize !== null ? tempManualFontSize : calc.fontSize, 40);
                      const formattedText = tempManualFontSize !== null ? element.content : calc.formattedText;
                      return (
                        <NameTitle 
                          data-cv-name-title="true"
                          nameTextColor={nameTextColor}
                          nameFontSize={fontSize}
                          onClick={handleNameClick}
                          sx={{
                            cursor: isEditMode ? 'pointer' : 'default',
                            '&:hover': isEditMode ? {
                              backgroundColor: 'rgba(0, 0, 0, 0.04)',
                              borderRadius: '4px',
                              padding: '2px 4px',
                              margin: '-2px -4px'
                            } : {},
                            fontSize: `${fontSize}pt !important`
                          }}
                        >
                          {formattedText}
                        </NameTitle>
                      );
                    })()}
                    {element.type === 'profileImage' && (
                      <Box 
                        data-cv-profile-image="true"
                        sx={{ 
                          textAlign: profileImageAlignment === 'left' ? 'left' : 
                                   profileImageAlignment === 'right' ? 'right' : 'center',
                          cursor: 'pointer',
                        }}
                        onClick={() => {
                          // Trigger das Profilbild-Modal
                          const event = new CustomEvent('openProfileImageModal');
                          window.dispatchEvent(event);
                        }}
                      >
                        <Box
                          sx={{
                            width: getProfileImageSize(profileImageSize),
                            aspectRatio: '1 / 1', // Quadratischer Container
                            margin: profileImageAlignment === 'left' ? '0 0 5px 0' :
                                    profileImageAlignment === 'right' ? '0 0 5px auto' :
                                    '0 auto 5px auto',
                            // Für Professional Template: Einfache Ausrichtung ohne komische Effekte
                            ...(templateStyle === 'professional' && {
                              marginLeft: profileImageAlignment === 'left' ? '-30px' :
                                         profileImageAlignment === 'right' ? 'auto' :
                                         '0' // Einfache Zentrierung
                            }),
                            overflow: 'hidden',
                            borderRadius: `${profileImageBorderRadius}%`,
                            display: 'block',
                            // Für Professional Template: outline statt border verwenden, da outline die Elementgröße nicht beeinflusst
                            ...(templateStyle === 'professional' && profileImageBorder ? {
                              outline: `${profileImageBorderThickness}px solid ${profileImageBorderColor}`
                            } : {}),
                            // Für andere Templates: normales border verwenden
                            ...(templateStyle !== 'professional' && {
                              border: profileImageBorder ? `${profileImageBorderThickness}px solid ${profileImageBorderColor}` : 'none'
                            })
                          }}
                        >
                          <img
                            src={element.content}
                            alt="Profilbild"
                            style={{
                              width: '100%',
                              height: '100%',
                              objectFit: 'contain',
                              userSelect: 'none'
                            }}
                          />
                        </Box>
                      </Box>
                    )}
                    {element.type === 'profileImagePlaceholder' && (
                      <Box data-cv-profile-image="true" sx={{ 
                        height: '19px', // Einheitlich zu Berechnung (19px)
                        backgroundColor: 'transparent'
                      }} />
                    )}
                    {element.type === 'profile' && templateStyle === 'professional' && (
                      <Box
                        onClick={(e) => {
                          // Trigger the Profile modal with current title
                          const event = new CustomEvent('openProfileModal', {
                            detail: { title: profileTitle }
                          });
                          window.dispatchEvent(event);
                        }}
                        sx={{ cursor: 'pointer' }}
                      >
                        <SectionTitle textColor={rightColumnTitleColor}>
        {profileTitle}
      </SectionTitle>
                        <div
                          style={{
                            fontSize: '10pt',
                            lineHeight: 1.5,
                            fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                            color: rightColumnTextColor,
                            minHeight: '3em',
                            padding: '2px',
                            whiteSpace: 'pre-wrap'
                          }}
                        >
                          {element.content}
                        </div>
                      </Box>
                    )}
                      {/* Kernkompetenzen entfernt - werden jetzt in der linken Spalte angezeigt */}
                      {element.type === 'spacerBeforeWorkExperience' && (
                        <Box sx={{ 
                          height: '1mm',
                          backgroundColor: 'transparent'
                        }} />
                      )}
                      {element.type === 'workExperienceWithTitle' && (
                        <Box
                          onClick={(e) => {
                            // Trigger the Work Experience modal with current title and specific work experience
                            const event = new CustomEvent('openWorkExperienceModal', {
                              detail: { 
                                title: workExperienceTitle,
                                workExperience: element._sourceWork || element.content.work,
                                isNonRelevant: false // Relevante Berufserfahrung
                              }
                            });
                            window.dispatchEvent(event);
                          }}
                          sx={{ 
                            cursor: 'pointer', 
                            position: 'relative',
                            '&:hover .reorder-buttons': { opacity: 1 }
                          }}
                        >
                        {/* Reorder Buttons */}
                        {isEditMode && onWorkExperienceReorder && element._totalCount > 1 && (
                          <Box className="reorder-buttons" sx={{
                            position: 'absolute',
                            top: 0,
                            right: 0,
                            display: 'flex',
                            gap: '2px',
                            opacity: 0,
                            transition: 'opacity 0.2s ease',
                            zIndex: 10,
                            backgroundColor: 'rgba(255,255,255,0.9)',
                            borderRadius: '4px',
                            padding: '1px'
                          }}>
                            {element._workIndex > 0 && (
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onWorkExperienceReorder(element._originalIndex, 'up', false); }}
                                sx={{ padding: '4px', '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' } }}>
                                <KeyboardArrowUpIcon sx={{ fontSize: '24px' }} />
                              </IconButton>
                            )}
                            {element._workIndex < element._totalCount - 1 && (
                              <IconButton size="small" onClick={(e) => { e.stopPropagation(); onWorkExperienceReorder(element._originalIndex, 'down', false); }}
                                sx={{ padding: '4px', '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' } }}>
                                <KeyboardArrowDownIcon sx={{ fontSize: '24px' }} />
                              </IconButton>
                            )}
                          </Box>
                        )}
                        <SectionTitle textColor={rightColumnTitleColor}>
                            {workExperienceTitle}
                          </SectionTitle>
                          <WorkItem textColor={rightColumnTitleColor}>
                            <>
                              <div
                                style={{
                                  fontSize: '12pt',
                                  fontWeight: 'bold',
                                  padding: '2px',
                                  color: rightColumnTextColor
                                }}
                              >
                                {element.content.work.position}
                              </div>
                              {showCompanyNamesInCv && element.content.work.company && (
                                <div
                                  style={{
                                    fontSize: '10pt',
                                    padding: '2px',
                                    color: rightColumnTextColor
                                  }}
                                >
                                  {element.content.work.company}
                                </div>
                              )}
                              {showIndustryInCv && element.content.work.industry && (
                                <div
                                  style={{
                                    fontSize: '9pt',
                                    padding: '2px',
                                    fontStyle: 'italic',
                                    color: rightColumnTextColor
                                  }}
                                >
                                  {element.content.work.industry}
                                </div>
                              )}
                              {showDateInCv && (
                              <div
                                style={{
                                  fontSize: '10pt',
                                  padding: '2px',
                                  color: rightColumnTextColor
                                }}
                              >
                                {element.content.work.period || `${element.content.work.startDate} - ${element.content.work.endDate || 'Heute'}`}
                              </div>
                              )}
                              {element.content.work.description && (
                                <JobDescription textColor={rightColumnTitleColor}>
                                  {showBulletPointsInCv ? (
                                    <Box sx={{ marginTop: '8px', padding: '2px' }}>
                                      {element.content.work.description.split('\n').filter((line: string) => line.trim()).map((line: string, idx: number) => (
                                        <ListItem key={idx} textColor={rightColumnTextColor} sx={{ fontSize: '10pt', marginBottom: '3px' }}>
                                          {line.trim()}
                                        </ListItem>
                                      ))}
                                    </Box>
                                  ) : (
                                    <div
                                      style={{
                                        fontSize: '10pt',
                                        lineHeight: 1.5,
                                        marginTop: '8px',
                                        fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                        color: rightColumnTextColor,
                                        minHeight: '3em',
                                        padding: '2px'
                                      }}
                                    >
                                      {element.content.work.description}
                                    </div>
                                  )}
                                </JobDescription>
                              )}
                            </>
                          </WorkItem>
                        </Box>
                      )}
                      {element.type === 'workExperience' && (
                      <Box
                        onClick={(e) => {
                          // Trigger the Work Experience modal with current title and specific work experience
                          const event = new CustomEvent('openWorkExperienceModal', {
                            detail: { 
                              title: workExperienceTitle,
                              workExperience: element._sourceWork || element.content,
                              isNonRelevant: false // Relevante Berufserfahrung
                            }
                          });
                          window.dispatchEvent(event);
                        }}
                        sx={{ 
                          cursor: 'pointer',
                          position: 'relative',
                          '&:hover .reorder-buttons': { opacity: 1 }
                        }}
                      >
                      {/* Reorder Buttons */}
                      {isEditMode && onWorkExperienceReorder && element._totalCount > 1 && (
                        <Box className="reorder-buttons" sx={{
                          position: 'absolute',
                          top: 0,
                          right: 0,
                          display: 'flex',
                          gap: '2px',
                          opacity: 0,
                          transition: 'opacity 0.2s ease',
                          zIndex: 10,
                          backgroundColor: 'rgba(255,255,255,0.9)',
                          borderRadius: '4px',
                          padding: '1px'
                        }}>
                          {element._workIndex > 0 && (
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onWorkExperienceReorder(element._originalIndex, 'up', false); }}
                              sx={{ padding: '4px', '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' } }}>
                              <KeyboardArrowUpIcon sx={{ fontSize: '24px' }} />
                            </IconButton>
                          )}
                          {element._workIndex < element._totalCount - 1 && (
                            <IconButton size="small" onClick={(e) => { e.stopPropagation(); onWorkExperienceReorder(element._originalIndex, 'down', false); }}
                              sx={{ padding: '4px', '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' } }}>
                              <KeyboardArrowDownIcon sx={{ fontSize: '24px' }} />
                            </IconButton>
                          )}
                        </Box>
                      )}
                      <WorkItem textColor={rightColumnTitleColor}>
                        <>
                          {!element._isContinuation && (
                            <div
                              style={{
                                fontSize: '12pt',
                                fontWeight: 'bold',
                                padding: '2px',
                                color: rightColumnTextColor
                              }}
                            >
                              {element.content.position}
                            </div>
                          )}
                          {!element._isContinuation && showCompanyNamesInCv && element.content.company && (
                            <div
                              style={{
                                fontSize: '10pt',
                                padding: '2px',
                                color: rightColumnTextColor
                              }}
                            >
                              {element.content.company}
                            </div>
                          )}
                          {!element._isContinuation && showIndustryInCv && element.content.industry && (
                            <div
                              style={{
                                fontSize: '9pt',
                                padding: '2px',
                                fontStyle: 'italic',
                                color: rightColumnTextColor
                              }}
                            >
                              {element.content.industry}
                            </div>
                          )}
                          {!element._isContinuation && showDateInCv && (
                          <div
                            style={{
                              fontSize: '10pt',
                              padding: '2px',
                              color: rightColumnTextColor
                            }}
                          >
                            {element.content.period || `${element.content.startDate} - ${element.content.endDate || 'Heute'}`}
                          </div>
                          )}
                          {element.content.description && (
                            <JobDescription textColor={rightColumnTitleColor}>
                              {showBulletPointsInCv ? (
                                <Box sx={{ marginTop: '8px', padding: '2px' }}>
                                  {element.content.description.split('\n').filter((line: string) => line.trim()).map((line: string, idx: number) => (
                                    <ListItem key={idx} textColor={rightColumnTextColor} sx={{ fontSize: '10pt', marginBottom: '3px' }}>
                                      {line.trim()}
                                    </ListItem>
                                  ))}
                                </Box>
                              ) : (
                                <div
                                  style={{
                                    fontSize: '10pt',
                                    lineHeight: 1.5,
                                    marginTop: '8px',
                                    fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                                    color: rightColumnTextColor,
                                    minHeight: '3em',
                                    padding: '2px',
                                    whiteSpace: 'pre-wrap'
                                  }}
                                >
                                  {element.content.description}
                                </div>
                              )}
                              {element.content.responsibilities && element.content.responsibilities.length > 0 && (
                                <Box sx={{ marginTop: '8px' }}>
                                  {element.content.responsibilities.map((resp: string, respIndex: number) => (
                                    <ListItem key={respIndex} textColor={rightColumnTextColor} sx={{ fontSize: '10pt', marginBottom: '3px' }}>
                                      {resp}
                                    </ListItem>
                                  ))}
                                </Box>
                              )}
                            </JobDescription>
                          )}
                        </>
                      </WorkItem>
                      </Box>
                    )}
                      {element.type === 'spacerBeforeAdditionalWorkExperience' && (
                        <Box sx={{ 
                          height: '10px',
                          backgroundColor: 'transparent'
                        }} />
                      )}
                      {element.type === 'additionalWorkExperienceTitle' && (
                        <SectionTitle textColor={rightColumnTitleColor}>
                          {element.content}
                        </SectionTitle>
                      )}
                      {element.type === 'additionalWorkExperience' && (
                        <Box
                          onClick={(e) => {
                            // Trigger the Work Experience modal with current title and specific work experience
                            const event = new CustomEvent('openWorkExperienceModal', {
                              detail: { 
                                title: additionalWorkExperienceTitle,
                                workExperience: element.content,
                                isNonRelevant: true // Nicht-relevante Berufserfahrung
                              }
                            });
                            window.dispatchEvent(event);
                          }}
                          sx={{ 
                            cursor: 'pointer',
                            position: 'relative',
                            pageBreakInside: 'avoid',
                            breakInside: 'avoid',
                            '&:hover .reorder-buttons': { opacity: 1 }
                          }}
                        >
                          {/* Reorder Buttons */}
                          {isEditMode && onWorkExperienceReorder && element._totalCount > 1 && (
                            <Box className="reorder-buttons" sx={{
                              position: 'absolute',
                              top: 0,
                              right: 0,
                              display: 'flex',
                              gap: '2px',
                              opacity: 0,
                              transition: 'opacity 0.2s ease',
                              zIndex: 10,
                              backgroundColor: 'rgba(255,255,255,0.9)',
                              borderRadius: '4px',
                              padding: '1px'
                            }}>
                              {element._workIndex > 0 && (
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onWorkExperienceReorder(element._originalIndex, 'up', true); }}
                                  sx={{ padding: '4px', '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' } }}>
                                  <KeyboardArrowUpIcon sx={{ fontSize: '24px' }} />
                                </IconButton>
                              )}
                              {element._workIndex < element._totalCount - 1 && (
                                <IconButton size="small" onClick={(e) => { e.stopPropagation(); onWorkExperienceReorder(element._originalIndex, 'down', true); }}
                                  sx={{ padding: '4px', '&:hover': { backgroundColor: 'rgba(0,0,0,0.08)' } }}>
                                  <KeyboardArrowDownIcon sx={{ fontSize: '24px' }} />
                                </IconButton>
                              )}
                            </Box>
                          )}
                          <div
                            style={{
                              fontSize: '10pt',
                              lineHeight: 1.5,
                              fontFamily: 'var(--cv-body-font, Arial, sans-serif)',
                              padding: '0px',
                              marginBottom: '0px',
                              color: rightColumnTextColor
                            }}
                          >
                            <div>
                              <strong>{element.content.position}</strong>
                            </div>
                            <div>
                              {showCompanyNamesInCv && element.content.company && showDateInCv ? 
                                `${element.content.company} (${element.content.startDate} - ${element.content.endDate || 'Heute'})` :
                                showCompanyNamesInCv && element.content.company ? 
                                element.content.company :
                                showDateInCv ?
                                `${element.content.startDate} - ${element.content.endDate || 'Heute'}` :
                                ''
                              }
                            </div>
                            {showIndustryInCv && element.content.industry && (
                              <div style={{ fontStyle: 'italic', fontSize: '9pt' }}>
                                {element.content.industry}
                              </div>
                            )}
                          </div>
                        </Box>
                      )}
                  </FrameBox>
                );
                })}
              </FrameBox>
            )}
          </PageContent>

          {/* Footer für jede Seite */}
          <PageFooter showFrames={showFrames}>
            {/* Footer ohne Text - Abstände bleiben erhalten */}
          </PageFooter>
        </A4Page>
      );
    }
    return pages;
  };

  // Berechne die tatsächliche Anzahl der Seiten basierend auf der GECACHTEN Verteilung
  // Keine Neuberechnung mehr - verwendet gecachte Werte!
  const getActualPageCount = () => {
    return cachedDistributedPages.pageCount;
  };

  return (
    <>
      <A4MultipleWrapper 
        key={refreshKey} 
        pages={getActualPageCount()} 
        className="cv-template"
        style={{
          '--cv-title-font': titleFont,
          '--cv-body-font': bodyFont,
        } as React.CSSProperties}
      >
        {renderCvPages()}
      </A4MultipleWrapper>
      
      {/* Modal für Namensgröße-Einstellungen */}
      <NameSizeModal
        open={isNameSizeModalOpen}
        onClose={() => {
          setIsNameSizeModalOpen(false);
          requestTemplateReflow('modal-close');
          // Zweiter Reflow-Pass, falls sich nach dem Close noch Zeilenumbrüche/Höhen stabilisieren
          setTimeout(() => {
            requestTemplateReflow('manual');
          }, 180);
        }}
        currentFontSize={getRenderedNameFontSize(cvData.personalInfo?.fullName || '')}
        onFontSizeChange={handleNameFontSizeChange}
        targetLanguage={targetLanguage}
        getTranslatedTitle={getTranslatedTitle}
      />
      
      {/* Modal für Kontakt-Einstellungen */}
      <ContactModal
        open={isContactModalOpen}
        onClose={() => {
          setIsContactModalOpen(false);
        }}
        onExited={() => {
          requestTemplateReflow('modal-close');
          // Zweiter Reflow-Pass, falls sich nach dem Close noch Zeilenumbrüche/Höhen stabilisieren
          setTimeout(() => {
            requestTemplateReflow('manual');
          }, 180);
        }}
        currentDisplayMode={contactDisplayMode}
        onDisplayModeChange={handleContactDisplayModeChange}
        targetLanguage={targetLanguage}
        getTranslatedTitle={getTranslatedTitle}
      />
    </>
  );
};

export default CvTemplate;  